// build 2026-08-20a: cache-rotate - the CDN pinned stale bytes under the v39.55 URL
/**
 * Agilite Media Flow — inline endless feed, library-fed, optimized.
 *
 * THE DESIGN (Dorian's mobile-endless-feed prototype, minus its costs): the
 * feed is simply the next thing in the product document — no button, no layer.
 * IG-style post heads, feature cards (native mp4 + copy overlay), 9:16 shorts
 * with ONE sound authority, review cards with real ratings, image cards,
 * double-tap like. Every item comes from agilite-media-feed-v2.json, exported
 * from the Agilite Media library; nothing is hardcoded here.
 *
 * WHAT "OPTIMIZED" MEANS, CONCRETELY:
 *  - Videos attach their src ONLY when near the viewport, play only while
 *    visible, pause on exit, release the source when far away. The prototype
 *    autoplayed every mp4 forever.
 *  - The DOM is RECYCLED: far-offscreen cycles collapse to fixed-height
 *    spacers (videos released first) and rebuild deterministically on return.
 *  - Images are lazy; growth is capped (section setting).
 *  - Session save + resume bar: leave the feed, come back to the exact spot,
 *    survives reload (sessionStorage, 45 min TTL).
 *
 * Isolation: `agmf-` namespace, no writes to other systems, every entry point
 * guarded — if anything here throws, the PDP must not notice.
 */
(function () {
  'use strict';

  var SESSION_PREFIX = 'AGMF_Session_v2:';
  var SESSION_TTL_MS = 45 * 60 * 1000;
  var CARDS_PER_CYCLE = 6;

  /* ---- MOVING THE PAGE, WHEREVER THE PAGE ACTUALLY SCROLLS ----------------
     Everything here used to be `window.scrollTo`, and on the desktop
     three-column layout the window is no longer what scrolls: the feed and the
     buy column are each their own pane, and the document has only the footer
     left to give. So the rating badge applied a pane-relative offset of a few
     thousand pixels to a document with a few hundred and clamped to the
     footer; the resume bar overshot to the same place; back-to-top asked a
     window already at zero to go to zero, and nothing happened.

     These work in DELTAS - how far a thing is from where it should be, right
     now - because a delta is the same number in either layout. The pane is
     found from the ELEMENT being scrolled to, not from the feed: the reviews
     accordion lives in the buy column, the feed in the media column, and they
     are different scrollers. */
  function agmfScroller(el) {
    return (el && el.closest) ? el.closest('.agdesk-media, .agdesk-buy') : null;
  }
  function agmfScrollBy(anchor, delta, smooth) {
    var host = agmfScroller(anchor);
    if (host) { host.scrollTop += delta; return; }        // a pane: no smooth, no clamp games
    window.scrollTo({ top: Math.max(0, window.scrollY + delta),
                      behavior: smooth ? 'smooth' : 'auto' });
  }
  function agmfScrollToStart(anchor, smooth) {
    var host = agmfScroller(anchor);
    if (host) { host.scrollTop = 0; return; }
    window.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' });
  }

  // AUTOSWAP — when a video finishes, does the next one come up by itself.
  // Chosen per surface in Agilite Media > Settings > "When a video finishes"
  // and shipped inside the feed (feed.autoAdvance), like the language rule:
  // one asset serves every storefront that reads it. These fallbacks are the
  // behaviour that predates the setting, so a feed exported before it existed
  // keeps working exactly as it did.
  var AUTO = { productCard: true, feed: false };

  // A LOOP IS CONTENT MEANT TO REPEAT: a clip baked here, or a cut made here
  // with a start and an end. It is never advanced off, on either surface, and
  // that is not a switch - "finished" is not a thing that happens to a loop.
  //
  // Distinct from the `loop` attribute and the embed's loop=1, which every
  // card carries as a PLAYBACK DEFAULT so nothing dead-ends on a black frame
  // when nobody is asking it to move on. Auto-advance turns that default off,
  // and only on the videos it actually governs.
  function isLooping(item) {
    if (!item) return false;
    if (item.format === 'GIF') return true;          // a clip baked here
    var cut = item.video && item.video.loop;
    // A cut needs a real range to BE a cut. The same guard the range handler
    // uses to decide whether to seek back (`le > ls`), so the exemption and
    // the looping can never disagree: a loop object with no end is played
    // straight through by that handler, and is therefore not a loop here.
    return !!cut && (cut.endSeconds || 0) > (cut.startSeconds || 0);
  }

  function qs(root, sel) { return root.querySelector(sel); }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function ratingChip(item) {
    var tags = item.tags || [];
    for (var i = 0; i < tags.length; i++) {
      var m = /([1-5])\s*\/\s*5/.exec(String(tags[i]));
      if (m) return m[1] + '/5';
    }
    return '';
  }

  // sizes DISPLAY as S M L XL (Dorian 2026-08-12) - the underlying variant
  // values never change, only what the shopper reads
  var SIZE_ABBREV = { 'small': 'S', 'medium': 'M', 'large': 'L',
    'x-large': 'XL', 'xlarge': 'XL', 'extra large': 'XL', 'xx-large': 'XXL',
    'xxlarge': 'XXL', '2x-large': 'XXL', 'x-small': 'XS', 'xsmall': 'XS',
    'one size': 'OS' };
  function sizeAbbrev(v) {
    return SIZE_ABBREV[String(v).trim().toLowerCase()] || v;
  }

  function fmtCount(n) {
    n = Number(n) || 0;
    if (!n) return '';
    if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return String(n);
  }

  var AGMF_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function fmtAgo(iso) {
    if (!iso) return '';
    var then = new Date(iso).getTime();
    if (isNaN(then)) return '';
    var days = Math.floor((Date.now() - then) / 86400000);
    if (days < 0) return '';
    if (days === 0) return 'today';
    if (days === 1) return '1 day ago';
    if (days < 7) return days + ' days ago';
    if (days < 30) { var w = Math.floor(days / 7); return w + (w === 1 ? ' week ago' : ' weeks ago'); }
    if (days < 365) { var mo = Math.floor(days / 30); return mo + (mo === 1 ? ' month ago' : ' months ago'); }
    var y = Math.floor(days / 365);
    return y + (y === 1 ? ' year ago' : ' years ago');
  }

  function fmtDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return AGMF_MONTHS[d.getMonth()] + ' ' + d.getFullYear();
  }

  // stroke icons (currentColor) — OS emoji render differently everywhere
  var ICO = {
    verified: '<svg class="agmf-verified" viewBox="0 0 24 24" width="14" height="14" aria-label="Verified buyer" role="img">' +
      '<path fill="#1d9bf0" d="M12 1.6l2.4 2.1 3.2-.3.9 3.1 2.9 1.4-1.1 3 1.1 3-2.9 1.4-.9 3.1-3.2-.3L12 22.4l-2.4-2.1-3.2.3-.9-3.1L2.6 16l1.1-3-1.1-3 2.9-1.4.9-3.1 3.2.3z"/>' +
      '<path fill="#fff" d="M10.8 15.3l-3-3 1.3-1.3 1.7 1.7 4.1-4.1 1.3 1.3z"/></svg>',
    mute: '<svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">' +
      '<path d="M12 4.5v15a.75.75 0 0 1-1.24.57L6.4 16.2H3.75A1.75 1.75 0 0 1 2 14.45v-4.9A1.75 1.75 0 0 1 3.75 7.8H6.4l4.36-3.87A.75.75 0 0 1 12 4.5z"/>' +
      '<path d="M16.2 9.1a.9.9 0 0 1 1.28 0L19 10.62l1.52-1.52a.9.9 0 1 1 1.28 1.28L20.28 11.9l1.52 1.52a.9.9 0 1 1-1.28 1.28L19 13.18l-1.52 1.52a.9.9 0 0 1-1.28-1.28l1.52-1.52-1.52-1.52a.9.9 0 0 1 0-1.28z"/></svg>',
    unmute: '<svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">' +
      '<path d="M12 4.5v15a.75.75 0 0 1-1.24.57L6.4 16.2H3.75A1.75 1.75 0 0 1 2 14.45v-4.9A1.75 1.75 0 0 1 3.75 7.8H6.4l4.36-3.87A.75.75 0 0 1 12 4.5z"/>' +
      '<path d="M15.6 8.4a.85.85 0 0 1 1.2 0 5.1 5.1 0 0 1 0 7.2.85.85 0 1 1-1.2-1.2 3.4 3.4 0 0 0 0-4.8.85.85 0 0 1 0-1.2z"/>' +
      '<path d="M18.2 5.7a.85.85 0 0 1 1.2 0 8.9 8.9 0 0 1 0 12.6.85.85 0 1 1-1.2-1.2 7.2 7.2 0 0 0 0-10.2.85.85 0 0 1 0-1.2z"/></svg>',
    pause: '<svg class="agmf-ico-pause" viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true">' +
      '<rect x="6" y="5" width="4.2" height="14" rx="1.2"/><rect x="13.8" y="5" width="4.2" height="14" rx="1.2"/></svg>',
    play: '<svg class="agmf-ico-play" viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true">' +
      '<path d="M8 5.2v13.6a.8.8 0 0 0 1.22.68l10.5-6.8a.8.8 0 0 0 0-1.36L9.22 4.52A.8.8 0 0 0 8 5.2z"/></svg>',
    yt: '<svg class="agmf-ico-yt" viewBox="0 0 28 20" width="26" height="19" aria-hidden="true"><rect width="28" height="20" rx="4.5" fill="#FF0000"/><path d="M11.4 5.7v8.6l7.6-4.3z" fill="#fff"/></svg>',
    // THE BAR'S OWN GLYPHS (v40.1). Drawn on YouTube's 36-unit grid with its
    // geometry - a bare triangle, two square bars - so the button reads as the
    // player's own. They keep the agmf-ico-play/pause class names, which is
    // what the paused-state swap in the CSS keys on.
    ytPlay: '<svg class="agmf-ico-play" viewBox="0 0 36 36" fill="currentColor" aria-hidden="true">' +
      '<path d="M12 10v16l14-8z"/></svg>',
    // four corners pointing outward - the shape YouTube's own fullscreen
    // button uses, so it needs no learning
    ytFull: '<svg viewBox="0 0 36 36" fill="currentColor" aria-hidden="true">' +
      '<path d="M10 16V10h6v2h-4v4h-2zm10-6h6v6h-2v-4h-4v-2zM10 20h2v4h4v2h-6v-6zm14 0h2v6h-6v-2h4v-4z"/></svg>',
    ytPause: '<svg class="agmf-ico-pause" viewBox="0 0 36 36" fill="currentColor" aria-hidden="true">' +
      '<path d="M11 10h4.6v16H11zM20.4 10H25v16h-4.6z"/></svg>',
    news: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 4h13v16H6a2 2 0 0 1-2-2V4zm13 4h3v10a2 2 0 0 1-2 2M7 8h7M7 12h7M7 16h4"/></svg>',
    thumb: '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 10v11H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h3zm0 0 4.2-7.4a1.8 1.8 0 0 1 3.3 1.2L13.8 8H19a2 2 0 0 1 2 2.4l-1.5 8A2 2 0 0 1 17.5 20H7"/></svg>',
    bubble: '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5H4.5L6 17.4A8.5 8.5 0 1 1 21 11.5z"/></svg>',
    heart: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20.6C7.2 17.2 3.4 13.9 3.4 10a4.7 4.7 0 0 1 8.6-2.6A4.7 4.7 0 0 1 20.6 10c0 3.9-3.8 7.2-8.6 10.6z"/></svg>',
    send: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>',
  };

  // Instagram's under-media chrome. Only REAL fields render; the whole block
  // collapses to nothing for non-IG items.
  function igContainer(item) {
    if (item.platform !== 'Instagram') return '';
    var m = item.metrics || {};
    var cap = item.text || '';
    if (!cap && item.title && !/^Instagram (video|post)/i.test(item.title)) cap = item.title;
    var date = fmtDate(item.publishedAt);
    var html = '<div class="agmf-ig-under">' +
      '<div class="agmf-ig-actions">' + ICO.heart + '<span class="agmf-ig-ico">' + ICO.bubble + '</span>' + ICO.send + '</div>';
    if (m.likes) html += '<strong class="agmf-ig-likes">' + fmtCount(m.likes) + ' likes</strong>';
    if (cap) {
      var poster = whoPosted(item, '');
      var who = poster.ours ? 'agilitetactical' : poster.name;
      html += '<p class="agmf-ig-cap"><strong>' + esc(who) + '</strong> ' + esc(cap) + '</p>';
    }
    // the count stays, the link does not (same rule as the picture above)
    if (m.comments) {
      html += '<span class="agmf-ig-comments">' + fmtCount(m.comments) + ' comments</span>';
    }
    if (date) html += '<small class="agmf-ig-date">' + date + '</small>';
    return html + '</div>';
  }

  // BELT AND BRACES: whatever markup a future card carries, a click inside one
  // never hands the shopper to another site. Internal links (a product, a
  // page) are untouched - only a foreign host is refused.
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a || !a.closest('.agmf-card, .agmf-hero-slide')) return;
    var host = '';
    try { host = new URL(a.getAttribute('href'), location.href).hostname; } catch (err) { host = ''; }
    if (host && host !== location.hostname) e.preventDefault();
  }, true);

  // set by boot from the section's data-scorpion attr; module scope because
  // postHead lives here too - the closure-scoped var crashed the whole boot
  // (ReferenceError on first card) and the failsafe blanked the feed
  var scorpionUrl = '';
  var scorpionLightUrl = '';
  // ── THE DESCRIPTION'S OWN EMBEDS LOAD LAST (Dorian 2026-09-06: "avoid
  //    users waiting for a video to load, youtube looks slow / black") ─────
  // Measured on the belt page: the two slowest requests of the whole page
  // were YouTube players NOBODY had scrolled to - `<iframe src=...>` written
  // into the product description in the admin, which the browser fetches at
  // page load because an iframe is not lazy by default. ~1MB of player each,
  // 2s on a fast desk connection and 10s on the first cold load, competing
  // with the pictures the shopper IS looking at, and drawing black until
  // they finish. The markup is his copy and is not touched: the src is
  // parked on the element and put back when the embed comes near, which is
  // exactly what the shopper's own scroll already pays for.
  function lazifyForeignEmbeds() {
    var frames = document.querySelectorAll('iframe[src*="youtube.com/embed"]:not(.agmf-ytframe):not([data-agmf-lazy])');
    if (!frames.length) return;
    var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (es) {
      es.forEach(function (en) {
        if (!en.isIntersecting) return;
        var f = en.target;
        io.unobserve(f);
        if (f.dataset.agmfLazy && !f.getAttribute('src')) f.setAttribute('src', f.dataset.agmfLazy);
      });
    }, { rootMargin: '600px 0px' }) : null;
    frames.forEach(function (f) {
      var src = f.getAttribute('src');
      if (!src) return;
      f.dataset.agmfLazy = src;
      f.setAttribute('loading', 'lazy');
      f.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      if (!f.getAttribute('allow')) f.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
      var r = f.getBoundingClientRect();
      var near = r.top < (window.innerHeight || 0) * 1.5 && r.bottom > -(window.innerHeight || 0);
      if (near || !io) return;          // already in reach: leave it loading
      f.removeAttribute('src');
      io.observe(f);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', lazifyForeignEmbeds);
  } else lazifyForeignEmbeds();
  // the accordions and the spec panel write their tabs later
  setTimeout(lazifyForeignEmbeds, 1200);
  setTimeout(lazifyForeignEmbeds, 4000);
  // ── DEAD MEDIA LEAVES THE PAGE (Dorian 2026-09-06: "avoid putting content
  //    that is 404 or any other that would produce an error") ─────────────
  // A YouTube player that answers with an error (deleted, private, embedding
  // off, region-blocked, referrer refused), a clip whose file is gone, a
  // picture that does not load: the piece is removed on the spot, remembered
  // for the session so the recycler and the gallery never rebuild it, and
  // announced as `agmf:media_error` - agmf-track.js puts it on the wire with
  // the record id, which is how Agilite Media learns the item is dead.
  var deadKeys = (function () {
    try { return JSON.parse(sessionStorage.getItem('agmf-dead') || '{}') || {}; } catch (e) { return {}; }
  })();
  function mediaDead(el, provider, code) {
    if (!el || !el.closest) return;
    var host = el.closest('.agmf-hero-slide') || el.closest('.agmf-card');
    if (!host || host.dataset.agmfDead) return;
    host.dataset.agmfDead = String(code == null ? '' : code);
    var item = null;
    try { item = window.AGMF && window.AGMF.itemFor ? window.AGMF.itemFor(host) : null; } catch (e) {}
    var key = (item && item.ck) || host.dataset.agmfCk || host.dataset.agmfTrackCk || '';
    if (key) {
      deadKeys[key] = 1;
      try { sessionStorage.setItem('agmf-dead', JSON.stringify(deadKeys)); } catch (e) {}
    }
    try {
      host.dispatchEvent(new CustomEvent('agmf:media_error', {
        bubbles: true,
        detail: {
          ck: key || null,
          record_id: item && item.id != null ? String(item.id) : (host.dataset.agmfId || null),
          content_type: (item && item.contentType) || host.dataset.agmfType || host.dataset.agmfTrackType || null,
          platform: (item && item.platform) || null,
          provider: provider || null,
          code: code == null ? null : String(code)
        }
      }));
    } catch (e) {}
    // a slide inside a rail goes alone; anything else takes its card with it
    var gone = el.closest('.agmf-slide') || host;
    // what the removal leaves one short, asked BEFORE it is gone
    var hostIs = function (cls) { return !!(host.classList && host.classList.contains(cls)); };
    var shortCycle = gone === host && hostIs('agmf-card') ? host.closest('.agmf-cycle') : null;
    var shortTrack = gone === host && hostIs('agmf-hero-slide') ? host.parentNode : null;
    try { gone.remove(); } catch (e) {}
    // A DEAD PIECE LEAVES NO HOLE (Dorian 2026-10-07: "on lots of page i see
    // more than one content that is alone, I dont see how that can be
    // possible since it should be only once, at the end of the product cart
    // feed"). It was possible because every block is planned to end on a
    // whole row and THEN loses a card whenever one dies here - any card
    // whose picture fails to load (an expired Instagram thumbnail, a T&E
    // image that 403s) - and each such block centred its now-lone last card.
    // A feed block is refilled; the product card re-runs its own parity.
    if (shortCycle) { try { onFeedCardDead(shortCycle); } catch (e) {} }
    if (shortTrack && shortTrack.__agmfBalance) { try { shortTrack.__agmfBalance(); } catch (e) {} }
  }
  // pictures: a failed card picture kills the card; a failed YouTube poster
  // only falls back to the jpg YouTube always has (the webp is not guaranteed)
  document.addEventListener('error', function (e) {
    var t = e.target;
    if (!t || t.tagName !== 'IMG' || !t.closest) return;
    if (t.classList.contains('agmf-ytposter')) {
      var sh = t.closest('[data-agmf-yt]');
      var id = sh && sh.getAttribute('data-agmf-yt');
      if (id && (t.getAttribute('src') || '').indexOf('/hqdefault.jpg') === -1) {
        t.src = 'https://i.ytimg.com/vi/' + encodeURIComponent(id) + '/hqdefault.jpg';
      }
      return;
    }
    if (t.closest('.agmf-head, .agmf-avatar, .agmf-yt-under, .agmf-yt-chan, .agmf-ig-cap, .agmf-caption, .agmf-heart')) return;
    if (!t.closest('.agmf-card, .agmf-hero-slide')) return;
    mediaDead(t, 'image', 0);
  }, true);

  // ── ONE CARD PER GESTURE ────────────────────────────────────────────────
  // Every horizontal rail in the feed - the same-kind carousels AND the
  // product gallery - is DRIVEN here rather than left to native scrolling.
  // History, so this does not get "simplified" back: CSS scroll-snap plus
  // momentum let one flick fly across five cards and land, and a settle-time
  // clamp could only yank it back afterwards, which reads as a jump. Owning
  // the gesture means the rail can never travel more than one card in the
  // first place.
  //
  // Two input paths, because a phone and a laptop swipe differently:
  //   * pointer drag - follows the finger, hard-capped at +/-1 card, releases
  //     onto exactly one (quarter-card of travel, or a real flick: 24px AND
  //     0.35px/ms, so a near-tap holds).
  //   * wheel/trackpad - a two-finger swipe is a STREAM of deltas, not one
  //     event; it is claimed with preventDefault and locked until 260ms of
  //     silence, so one physical swipe = one card no matter how many events
  //     it fires. Vertical intent is never claimed: the page keeps scrolling.
  // Where slide i actually STARTS, measured from the layout rather than
  // computed as i x clientWidth. On a phone whose viewport is a fractional
  // number of CSS pixels those two disagree by a pixel or two per slide, and
  // the error accumulates: the card lands slightly off and the neighbouring
  // video shows as a strip down the side (Dorian 2026-08-16).
  // The rail code below is module scope; the video manager and the motion
  // preference live inside the section closure. Calling them directly threw
  // "manageVideos is not defined" on EVERY swipe release - the handler died
  // before it moved anything, which is why swiping felt dead and a rail was
  // left mid-slide (Dorian 2026-08-24). The closure fills these in on boot.
  var onRailSettled = function () {};
  // ...and what to do when a feed card dies inside a block that is already
  // built (mediaDead below); the section closure fills it in on boot
  var onFeedCardDead = function () {};
  var railReducedMotion = function () { return false; };

  // THE SLIDES THAT ACTUALLY EXIST ON SCREEN.
  //
  // A rail can hold children that are not laid out - the product gallery ends
  // with three `display:none` theme images (measured 2026-08-24). A hidden
  // element reports an all-zero rect, so "which slide is nearest the left
  // edge?" scored those hidden ones as distance |track.left| - usually the
  // SMALLEST distance of all - and the rail was told it was sitting on slide
  // 17 of 20 when it was on slide 1. The next swipe then flew to the end:
  // Dorian's "swipe fast and it jumps to the last video". Anything without
  // width is not a position, and is skipped here rather than papered over.
  function railSlides(track) {
    var out = [];
    var kids = track.children;
    for (var i = 0; i < kids.length; i++) {
      var r = kids[i].getBoundingClientRect();
      if (r.width > 1) out.push({ el: kids[i], left: r.left });
    }
    return out;
  }

  // Positions are measured with fractional rects: `offsetLeft` rounds, and on a
  // phone whose viewport is a fractional number of CSS px that third of a pixel
  // per slide is what showed the neighbouring video down the side of the card.
  function railOffset(track, i) {
    var slides = railSlides(track);
    if (!slides.length) return 0;
    var n = Math.max(0, Math.min(slides.length - 1, i));
    return track.scrollLeft + (slides[n].left - track.getBoundingClientRect().left);
  }

  function railIndex(track) {
    var slides = railSlides(track);
    var left = track.getBoundingClientRect().left;
    var best = 0, bestDist = Infinity;
    for (var i = 0; i < slides.length; i++) {
      var d = Math.abs(slides[i].left - left);
      if (d < bestDist) { bestDist = d; best = i; }
    }
    return best;
  }

  function railCount(track) { return railSlides(track).length; }

  // Our own scroll animation. The browser's `behavior:'smooth'` is ~400ms of
  // slow easing and, worse, it can be silently ignored - when that happens the
  // rail stays put while the code believes it moved. That desync IS the "fast
  // swiping jumps to the last video" bug (Dorian 2026-08-24): each quick swipe
  // advanced a REMEMBERED index while the pixels never followed, and the next
  // release snapped to wherever that runaway index pointed. 190ms ease-out,
  // driven here, cancellable, and the index is re-MEASURED when it lands.
  function railAnimate(track, target, onDone) {
    if (track.__agmfAnim) { cancelAnimationFrame(track.__agmfAnim); track.__agmfAnim = 0; }
    var from = track.scrollLeft, delta = target - from;
    function finish() {
      track.scrollLeft = target;
      track.__agmfAnim = 0;
      track.__agmfFrom = railIndex(track);      // measured, never assumed
      if (onDone) onDone();
    }
    if (Math.abs(delta) < 0.5 || railReducedMotion()) { finish(); return; }
    var t0 = 0, DUR = 190, alive = false, done = false;
    function end() { if (!done) { done = true; finish(); } }
    function step(ts) {
      if (done) return;
      alive = true;
      if (!t0) t0 = ts;
      var p = Math.min(1, (ts - t0) / DUR);
      var e = 1 - Math.pow(1 - p, 3);           // ease-out cubic
      track.scrollLeft = from + delta * e;
      if (p < 1) track.__agmfAnim = requestAnimationFrame(step);
      else end();
    }
    track.__agmfAnim = requestAnimationFrame(step);
    // A FRAME IS NOT GUARANTEED. rAF is frozen in a background tab and
    // throttled under battery saver, and a rail left half-way between two
    // slides is exactly the "stuck showing two videos" state. If no frame
    // has arrived shortly, or the animation overruns, land it outright.
    setTimeout(function () { if (!alive) end(); }, 90);
    setTimeout(end, DUR + 260);
  }

  function ownHorizontalGesture(track) {
    if (!track || track.__agmfOwned) return;
    track.__agmfOwned = 1;
    var startX = 0, startY = 0, startLeft = 0, from = 0, w = 1;
    var dragging = false, decided = false, horizontal = false, t0 = 0;

    function width() { return Math.max(1, track.clientWidth); }
    function last() { return Math.max(0, railCount(track) - 1); }
    // Feed rails carry edge clones and wrap through their own settle handler.
    // The product gallery has no clones, so IT wraps here: past the end is the
    // first slide, before the start is the last one (Dorian 2026-08-24).
    function hasClones() { return !!track.querySelector('.agmf-slide--clone'); }

    function goTo(idx) {
      var wrapped = idx, instant = false;
      if (!hasClones()) {
        if (idx > last()) { wrapped = 0; instant = true; }
        else if (idx < 0) { wrapped = last(); instant = true; }
      } else {
        wrapped = Math.max(0, Math.min(last(), idx));
      }
      var target = railOffset(track, wrapped);
      // a wrap is a JUMP - animating the whole rail back would read as a rewind
      if (instant) {
        if (track.__agmfAnim) { cancelAnimationFrame(track.__agmfAnim); track.__agmfAnim = 0; }
        track.scrollLeft = target;
        track.__agmfFrom = railIndex(track);
        onRailSettled();
        return;
      }
      railAnimate(track, target, onRailSettled);
    }

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      // a new gesture OWNS the rail: kill any animation still running and read
      // the position off the pixels, not off what we last intended
      if (track.__agmfAnim) { cancelAnimationFrame(track.__agmfAnim); track.__agmfAnim = 0; }
      w = width();
      startX = e.clientX; startY = e.clientY;
      startLeft = track.scrollLeft;
      from = railIndex(track);
      track.__agmfFrom = from;
      dragging = true; decided = false; horizontal = false; t0 = Date.now();
    }, { passive: true });

    track.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX, dy = e.clientY - startY;
      if (!decided) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;   // still a tap
        decided = true;
        horizontal = Math.abs(dx) > Math.abs(dy);
        if (horizontal && track.setPointerCapture) {
          try { track.setPointerCapture(e.pointerId); } catch (err) {}
        }
      }
      if (!horizontal) return;
      var want = startLeft - dx;
      // one card of travel either way; at the ends the finger can pull a
      // quarter card into empty space so the wrap still feels like a swipe
      var min = from <= 0 && !hasClones() ? -w * 0.25 : railOffset(track, from - 1);
      var max = from >= last() && !hasClones() ? railOffset(track, last()) + w * 0.25
                                              : railOffset(track, from + 1);
      track.scrollLeft = Math.max(min, Math.min(max, want));
    }, { passive: true });

    function release(e) {
      if (!dragging) return;
      dragging = false;
      if (!horizontal) return;
      var dx = (e && e.clientX != null ? e.clientX : startX) - startX;
      var dt = Math.max(1, Date.now() - t0);
      var flick = Math.abs(dx) > 24 && Math.abs(dx) / dt > 0.35;   // px per ms
      var step = 0;
      if (dx <= -w * 0.25 || (flick && dx < 0)) step = 1;
      else if (dx >= w * 0.25 || (flick && dx > 0)) step = -1;
      goTo(from + step);
    }
    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (evt) {
      track.addEventListener(evt, release, { passive: true });
    });

    var wheelLock = false, wheelTimer = 0;
    track.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;   // the page's gesture
      e.preventDefault();
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(function () { wheelLock = false; }, 260);
      if (wheelLock || Math.abs(e.deltaX) < 8) return;
      wheelLock = true;
      goTo(railIndex(track) + (e.deltaX > 0 ? 1 : -1));
    }, { passive: false });

    // A MOUSE DRAG IS NOT A FILE DRAG. Images and links are natively
    // draggable, so pressing one and moving starts the browser's own
    // drag-and-drop: a ghost image follows the cursor and the pointer stream
    // is CANCELLED mid-gesture, leaving the rail parked between two slides.
    // Measured 2026-08-25: every gallery image reported draggable=true,
    // user-drag:auto. Synthetic events never reproduce it (untrusted events
    // do not start a native drag), which is why this only ever showed up on
    // a real mouse (Dorian).
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });

    track.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      e.preventDefault();
      goTo(railIndex(track) + (e.key === 'ArrowRight' ? 1 : -1));
    });
  }

  // v54: a created loop's caption arrives RESOLVED from Agilite Media -
  // concrete sizes (percent of frame height), colours, weights, font stacks.
  // The feature look is the PDP prototype card: gradient scrim, glass pill
  // kicker, tight headline, quiet body. It used to be burned into the mp4 by
  // ffmpeg, which cannot draw any of that - the words now paint HERE, so an
  // edit in Agilite Media reaches the page on the next feed publish with no
  // re-render and no theme push. Sizes use cqh against the media box, so the
  // card reads identically at any width this layout gives it.
  function capPartStyle(p) {
    return 'font-size:calc(' + (Number(p.sizePct) || 2) + ' * 1cqh);' +
      'font-weight:' + (Number(p.weight) || 400) + ';' +
      'letter-spacing:' + (Number(p.letterSpacing) || 0) + 'em;' +
      'color:' + esc(p.color || '#fff') + ';' +
      'font-family:' + esc(p.fontStack || 'Arial, Helvetica, sans-serif') + ';' +
      (p.uppercase ? 'text-transform:uppercase;' : '');
  }

  function capOverlayHtml(item) {
    var cap = item && item.caption;
    if (!cap) return '';
    if (cap.look === 'feature' && cap.parts) {
      var rows = '';
      [['kicker', 'agmf-cap-kicker'], ['title', 'agmf-cap-title'], ['body', 'agmf-cap-body']]
        .forEach(function (pair) {
          var p = cap.parts[pair[0]];
          if (!p) return;
          // p.html was sanitised server-side (caption_resolve.py) before it
          // ever reached this feed; plain text is escaped here.
          var words = p.html || (p.text ? esc(p.text) : '');
          if (!words) return;
          rows += '<span class="' + pair[1] +
            (pair[0] === 'kicker' && p.pill ? ' is-pill' : '') +
            '" style="' + capPartStyle(p) + '">' + words + '</span>';
        });
      if (!rows) return '';
      // A card dragged in the Loop Lab carries its point (fractions of the
      // frame, measured to the card's centre). The layout still supplies the
      // scrim, the column and the label treatment; only the place moves.
      var placed = cap.place && typeof cap.place.x === 'number' && typeof cap.place.y === 'number';
      var placeVars = placed
        ? ' style="--cap-x:' + (Math.min(1, Math.max(0, cap.place.x)) * 100).toFixed(2) +
          '%;--cap-y:' + (Math.min(1, Math.max(0, cap.place.y)) * 100).toFixed(2) + '%"'
        : '';
      return '<span class="agmf-cap agmf-cap--' + esc(cap.featureVariant || 'bottom') +
        (placed ? ' agmf-cap--placed' : '') +
        '" aria-hidden="true"' + placeVars + '><span class="agmf-cap-shade"></span>' +
        '<span class="agmf-cap-content">' + rows + '</span></span>';
    }
    if (!cap.text) return '';
    var off = cap.offsetY != null ? cap.offsetY : 0.92;
    return '<span class="agmf-cap agmf-cap--line agmf-cap-look--' + esc(cap.look || 'outline') +
      '" aria-hidden="true"><span class="agmf-cap-linebox" style="top:' +
      (off * 100).toFixed(1) + '%;transform:translateY(-' + (off * 100).toFixed(1) + '%);' +
      'text-align:' + esc(cap.align || 'center') + ';' + capPartStyle(cap) + '">' +
      esc(cap.text) + '</span></span>';
  }

  // v56 WHOSE POST IS THIS (Dorian 2026-09-06: "not all written agilite but
  // the name of the person that published it"). The feed now stamps every
  // card with source.kind - ours / creator / community / buyer / press - and
  // these two helpers are the only place the answer is decided.
  //
  // A creator's post keeps THEIR name, their picture and a quiet 'reposted'
  // line. Only our own content wears the Agilite mark.
  function whoPosted(item, fallbackSub) {
    var src = (item && item.source) || {};
    var kind = src.kind || (src.name ? 'creator' : 'ours');
    var ours = kind === 'ours';
    var name = ours ? 'Agilite' : (src.name || '').trim();
    if (!name) { name = 'Agilite'; kind = 'ours'; }
    var sub = fallbackSub || '';
    if (kind === 'creator') sub = sub ? sub + ' · reposted' : 'reposted';
    else if (kind === 'community') sub = 'T&E Community';
    else if (kind === 'press') sub = 'Press';
    return { name: name, sub: sub, avatar: src.avatar || '', kind: kind, ours: ours };
  }

  function sourceHead(item, fallbackSub, icon) {
    var who = whoPosted(item, fallbackSub);
    return postHead(who.name, who.sub, who.avatar, icon, who.ours);
  }

  function postHead(title, sub, avatar, icon, ours) {
    var av;
    // an Agilite post wears the REAL MARK, never an (A) monogram
    // (Dorian 2026-08-12) - and then the right side stays quiet. Whose post
    // it is now comes from the feed, not from the name happening to read
    // 'Agilite': a creator called Agilite Tutorials is not us.
    var isBrand = ours === undefined ? title === 'Agilite' : !!ours;
    if (isBrand && scorpionLightUrl) {
      av = '<span class="agmf-avatar agmf-avatar--mark" aria-hidden="true">' +
        '<img src="' + esc(scorpionLightUrl) + '" alt="" loading="lazy"></span>';
    } else if (icon) av = '<span class="agmf-avatar agmf-avatar--brand" aria-hidden="true">' + icon + '</span>';
    else if (avatar) av = '<span class="agmf-avatar agmf-avatar--img" aria-hidden="true"><img src="' + esc(avatar) + '" alt="" loading="lazy"></span>';
    else av = '<span class="agmf-avatar" aria-hidden="true">' +
      esc((String(title || 'A').trim()[0] || 'A').toUpperCase()) + '</span>';
    // NO THREE DOTS. Every head used to end with a "..." in the corner
    // because that is what a social post looks like - but it was never a
    // menu, nothing opened, and a control that does nothing is worse than no
    // control (Dorian 2026-09-06: "we said we should drop the 3 dots on top
    // right of each content but we didnt do it!"). Removed from the markup
    // rather than hidden in CSS, so there is nothing left to click or read.
    return '<div class="agmf-head">' + av +
      '<span class="agmf-head-copy"><strong>' + esc(title) + '</strong>' +
      (sub ? '<small>' + esc(sub) + '</small>' : '') + '</span></div>';
  }

  // ── card renderers (pure: item -> element; media attaches lazily) ─────────

  function videoShell(item, extraClass, extraInner) {
    // No src yet — manageVideos() attaches it near the viewport.
    // A BAKED clip's poster must NOT be the source video's 16:9 thumbnail:
    // cover-cropped into the 9:16 shell it showed a wildly zoomed frame with
    // the baked caption sliced in half (Dorian 2026-08-12, measured: file is
    // 360x640 while the ytimg poster is 1280x720). Such clips preload their
    // own first frame instead of borrowing a foreign one.
    var baked = item.format === 'GIF';
    return '<div class="agmf-media ' + (extraClass || '') + '">' +
      '<video muted loop playsinline disablepictureinpicture ' +
      'preload="' + (baked ? 'metadata' : 'none') + '" ' +
      'data-agmf-src="' + esc(item.video.src) + '" ' +
      (item.thumb && !baked ? 'poster="' + esc(item.thumb) + '" ' : '') + '></video>' +
      // A BAKED CLIP HAS NO SOUND, so it gets no speaker button - offering a
      // control that cannot do anything is worse than no control (Dorian
      // 2026-08-16). The baker strips the audio track outright (`-an`), and
      // the element stays `muted` whatever a future file carries.
      (baked ? '' :
        '<button class="agmf-sound" type="button" aria-label="Turn sound on" aria-pressed="false">' +
        '<span class="agmf-sound-muted">' + ICO.mute + '</span>' +
        '<span class="agmf-sound-on" hidden>' + ICO.unmute + '</span></button>') +
      (extraInner || '') + '</div>';
  }

  function ytShellHtml(id, thumb, extraClass, extraInner) {
    return '<div class="agmf-media agmf-media--yt ' + (extraClass || '') + '" data-agmf-yt="' + esc(id) + '">' +
      '<span class="agmf-ytpaused" aria-hidden="true"></span>' +
      (thumb ? '<img class="agmf-ytposter" loading="lazy" alt="" src="' + esc(thumb) + '">' : '') +
      // THE SHIELD NO LONGER TAKES THE PRESS. It still exists - the lightbox
      // and the paused overlay style against it - but the middle passes
      // through, so YouTube's own press-to-play-or-pause works again. That
      // gesture was never broken by `controls=0`; it was broken by this span
      // covering the whole player at z-index 2, which is why every aimed press
      // became "expand the clip" instead.
      '<span class="agmf-ytshield agmf-ytshield--through" aria-hidden="true"></span>' +
      // WHAT THE SHOPPER SEES WHILE IT IS PAUSED (Dorian 2026-09-06: "there is
      // sometimes a pause button on the screen (youtube native) but when
      // clicking it its making full screen instead"). `controls=0` removes
      // YouTube's control BAR but not the big centre buttons it paints on the
      // paused state - and that art lives inside the frame, under our shield,
      // so it looked pressable and every tap went to the shield's expand rule
      // instead. We cover it with our own: the veil only dims (it never takes
      // a tap), the button in the middle is the real control. Everything else
      // on the picture still opens the clip big, exactly as before.
      '<span class="agmf-yt-veil" aria-hidden="true"></span>' +
      '<button class="agmf-yt-resume" type="button" data-agmf-ytresume aria-label="Play">' +
      ICO.play + '</button>' +
      // THE BADGE THAT FLASHES ON A TOGGLE - the other half of what YouTube
      // draws in the middle. aria-hidden, not a status region: in a feed of
      // many cards, announcing every press would be noise, and the row
      // button's own label already carries the state.
      '<span class="agmf-yt-bezel" aria-hidden="true">' + ICO.ytPause + ICO.ytPlay + '</span>' +
      // THE SPEAKER STANDS ON ITS OWN (Dorian 2026-09-09: "restablish the sound
      // button, and dont keep youtube controls on all the time !, only on
      // hover"). It rode in the row for one round, which was fine while the row
      // was always up; the moment the row hides until hover, the one control a
      // muted autoplaying card exists to offer would have hidden with it. So it
      // returns to the place it has held since v39.60 - 56px up the right edge,
      // clear of the row when the row appears - and the row keeps the rest.
      '<button class="agmf-sound" type="button" data-agmf-ytsound aria-label="Turn sound on" aria-pressed="false">' +
      '<span class="agmf-sound-muted">' + ICO.mute + '</span>' +
      '<span class="agmf-sound-on" hidden>' + ICO.unmute + '</span></button>' +
      // -- THE BAR, BUILT TO YOUTUBE'S (Dorian 2026-09-09: "lets replace all
      //    controls to look exactly like native, including the link to the
      //    video page") ------------------------------------------------------
      // TRIMMED TO TWO (Dorian 2026-09-09: "Maybe having only the progress bar
      // and play pause button is enough"). It was built as a full replica -
      // clock, "YouTube" wordmark linking to the watch page, fullscreen glyph -
      // and the replica is what got cut, not the mechanism: same red line on
      // the same bottom-up scrim, same play/pause glyph, same seek.
      //
      // What went with the wordmark: the card no longer leads anywhere on
      // youtube.com. The player is chrome-less, so YouTube draws no way out
      // either - which means there is nothing left on a card for the exit
      // tracking to observe. The inference block further down stays for the
      // paused-overlay case, and reports itself as an inference.
      // Fullscreen went too; a press on the picture still opens the piece
      // large, which is the gesture that always did it.
      '<div class="agmf-ytc" data-agmf-ytc>' +
      '<span class="agmf-ytc-scrim" aria-hidden="true"></span>' +
      '<span class="agmf-yt-bar" data-agmf-ytbar>' +
      '<span class="agmf-yt-bar-rail"></span>' +
      '<span class="agmf-yt-bar-load"></span>' +
      '<span class="agmf-yt-bar-fill"><span class="agmf-yt-bar-knob"></span></span></span>' +
      '<div class="agmf-ytc-row">' +
      '<button class="agmf-yt-play" type="button" data-agmf-ytplay aria-label="Pause">' +
      ICO.ytPause + ICO.ytPlay + '</button>' +
      // FULL SCREEN IS OURS NOW. The replica never had one, which is why the
      // only route to a large clip was a press on the picture - and that press
      // fired instead of whatever control was being aimed at, reported three
      // times. A labelled button cannot be missed and cannot be mistaken.
      '<button class="agmf-yt-full" type="button" data-agmf-ytfs aria-label="Full screen">' +
      ICO.ytFull + '</button>' +
      '</div></div>' +
      '<button class="agmf-yt-close" type="button" data-agmf-ytclose aria-label="Close">&#10005;</button>' +
      (extraInner || '') + '</div>';
  }

  // blocks were sanitized at IMPORT time (style/script/on* stripped before
  // the library stored them); this trusts our own library, not Shopify
  function panelTabHtml(t) {
    // 'readmore' splits the tab: everything before it shows at once, the
    // button reveals everything after. Emitted by Agilite Media when the
    // product carries a part-2 field (custom.details_more) - a product
    // without one has no marker and renders exactly as before.
    var html = '';
    var folded = false;
    (t.blocks || []).forEach(function (b) {
      if (b.type === 'readmore') {
        if (folded) return;                    // one fold per tab
        folded = true;
        html += '<button type="button" class="agmf-readmore">' +
          esc(b.label || 'Read more') + '</button>' +
          '<div class="agmf-readmore-rest" hidden>';
        return;
      }
      if (b.type === 'html' && b.html) {
        html += '<div class="agmf-spec-html">' + b.html + '</div>';
      } else if (b.type === 'video' && b.provider === 'youtube' && b.id) {
        // BORN WAITING. This video does not mount until somebody presses play,
        // and it has to LOOK that way from the first paint - not from whenever
        // the visibility pass first reaches it. Stamping it only in that pass
        // left the shell showing a still picture under a control bar drawing
        // its playing face, with the centre disc hidden by the no-player rule:
        // a video that looks started and cannot be started. attachYt clears
        // this the moment a player is attached.
        html += ytShellHtml(b.id, b.thumb, 'agmf-spec-video')
          .replace('<div class="agmf-media', '<div data-agmf-await-tap="1" class="agmf-media');
      }
    });
    if (folded) html += '</div>';
    return html;
  }

  // Read more opens the rest as a READING DIALOG (Dorian 2026-08-20) — the
  // same sheet the press articles use, so the two long reads on the page feel
  // like one thing. Reuses .agmf-art-dialog/.agmf-artd-* wholesale: the scrim,
  // the close button and the scroll lock are already wired to
  // [data-agmf-artopen-close], so this needs no new close path.
  function openReadMoreDialog(btn) {
    var rest = btn.nextElementSibling;
    if (!rest || !rest.classList.contains('agmf-readmore-rest')) return;
    var open = document.querySelector('.agmf-art-dialog');
    if (open) { open.remove(); document.documentElement.classList.remove('agmf-noscroll'); }
    // the title is whatever names this section to the shopper: the accordion's
    // own summary, else the pinned spec card's active tab
    var acc = btn.closest && btn.closest('details.agmf-acc');
    var titleEl = acc && acc.querySelector('.agmf-acc-title');
    var title = (titleEl && titleEl.textContent.trim()) || 'Details';

    var dlg = document.createElement('div');
    dlg.className = 'agmf-art-dialog agmf-art-open agmf-rmd';
    dlg.innerHTML =
      '<div class="agmf-artd-scrim" data-agmf-artopen-close aria-hidden="true"></div>' +
      '<div class="agmf-artd-sheet" role="dialog" aria-modal="true" aria-label="' + esc(title) + '">' +
      '<span class="agmf-artd-progress" aria-hidden="true"><i></i></span>' +
      '<span class="agmf-artd-grab" aria-hidden="true"></span>' +
      '<button class="agmf-artd-close" type="button" data-agmf-artopen-close aria-label="Close">&#10005;</button>' +
      '<div class="agmf-artd-scroll">' +
      '<div class="agmf-rmd-head"><h3 dir="auto">' + esc(title) + '</h3></div>' +
      '<div class="agmf-artd-body agmf-rmd-body"></div>' +
      '</div></div>';
    // MOVE the already-sanitized nodes in rather than re-serializing them:
    // the library sanitized these blocks at import, and cloning keeps any
    // yt shell's dataset intact for manageVideos()
    var body = dlg.querySelector('.agmf-rmd-body');
    var copy = rest.cloneNode(true);
    // a cloned shell that ALREADY holds an iframe would give a second player
    // talking over the first - drop them; manageVideos re-attaches on sight
    copy.querySelectorAll('iframe').forEach(function (f) { f.remove(); });
    // SNAPSHOT first: childNodes is LIVE, and appendChild moves the node
    // out of it - iterating the live list would skip every other block
    Array.prototype.slice.call(copy.childNodes).forEach(function (n) { body.appendChild(n); });
    var scroller = dlg.querySelector('.agmf-artd-scroll');
    var fill = dlg.querySelector('.agmf-artd-progress > i');
    scroller.addEventListener('scroll', function () {
      var max = scroller.scrollHeight - scroller.clientHeight;
      fill.style.width = (max > 0 ? Math.min(100, (scroller.scrollTop / max) * 100) : 0) + '%';
    }, { passive: true });
    document.body.appendChild(dlg);
    document.documentElement.classList.add('agmf-noscroll');
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('.agmf-readmore');
    if (!btn) return;
    openReadMoreDialog(btn);      // the button stays: the read can be reopened
  });

  function featureCard(item) {
    var el = document.createElement('article');
    el.className = 'agmf-card agmf-card--feature';
    el.innerHTML = sourceHead(item, 'Product feature') +
      videoShell(item, 'agmf-media--43') +
      '<div class="agmf-overlay">' +
      '<h3 class="agmf-overlay-title">' + esc(item.title) + '</h3>' +
      (item.text ? '<p class="agmf-overlay-text">' + esc(item.text) + '</p>' : '') +
      '</div>';
    return el;
  }

  function shortCard(item) {
    var el = document.createElement('article');
    el.className = 'agmf-card agmf-card--short';
    var src = item.source || {};
    // a CREATED loop is our own cut, not somebody's Short: it says so, and
    // its shell takes the BAKED file's real shape (the 9:16 guess
    // cover-cropped a 16:9 bake into nonsense) - Dorian 2026-08-12
    var isLoop = item.format === 'GIF';
    if (isLoop) el.classList.add('agmf-card--clip');
    el.innerHTML = (isLoop
      ? postHead('Agilite', 'Product clip', '', null, true)
      : item.platform === 'T&E'
      ? monoHead(src.name || 'T&E member', 'T&E Community')
      : sourceHead(item, item.platform === 'Instagram' ? 'Instagram Reel' : 'Short video')) +
      // every card caps at 3:4 (his standing rule) - a created clip is no
      // exception: the 9:16 bake cover-crops into the 3:4 shell
      videoShell(item, isLoop ? 'agmf-media--43' : 'agmf-media--916',
        isLoop ? capOverlayHtml(item) : '') + igContainer(item);
    return el;
  }

  function ytCard(item) {
    // Muted autoplay, full-bleed — no tap-to-play. The iframe is attached by
    // manageVideos() near the viewport and released when far, exactly like
    // native <video> srcs (an embed can't be paused without the YT API, so
    // attach/detach IS its play/release lifecycle). Poster shows until then.
    var el = document.createElement('article');
    el.className = 'agmf-card agmf-card--yt';
    // geometry follows the REAL frame: a landscape video labeled Short
    // (sizing tutorial, 1280x720) letterboxed to death inside the vertical
    // shell. The exporter ships video.vertical from the watch page's embed
    // dims; the format label stays source-true either way. (2026-08-12)
    var isShort = item.format === 'Short';
    var vert = item.video && item.video.vertical != null ? !!item.video.vertical : isShort;
    var loop = item.video && item.video.loop;
    if (loop) {
      // a CREATED loop: our own cut of the source video - brand head, no
      // YouTube chrome, no timebar, range-looped by the jsapi handler.
      // v39.4: the editor's CROP is APPLIED - the library zooms the player
      // to the chosen window (e.g. a 9:16 slice of a 16:9 source); shipping
      // the crop without applying it showed the whole frame tiny (Dorian).
      var crop = loop.crop || null;
      var cropAspect = crop ? ({ '9:16': 9 / 16, '1:1': 1, '3:4': 3 / 4 })[crop.aspect] : null;
      var hasCrop = !!(cropAspect && crop.sourceAspect && cropAspect < crop.sourceAspect);
      // the caption paints INSIDE the media box (it sizes itself against
      // that box with cqh); the old flat agmf-loop-cap read the raw stored
      // caption, which the feed no longer ships - resolved styles ride on
      // item.caption for baked and embedded loops alike.
      // NEVER THE YOUTUBE THUMBNAIL (Dorian 2026-10-05: "PUT THE ACTUAL LOOP
      // AND NEVER USE THE THUMBNAIL EVEN FOR PREVIEWING"). A loop's `thumb` is
      // the source video's YouTube thumbnail - a different moment of a
      // different framing - so it is not painted, and the frame itself stays
      // invisible until the loop is actually playing (agilite-media-flow.css,
      // .agmf-yt-loop): the shopper sees the loop or nothing.
      el.innerHTML = postHead('Agilite', 'Product clip', '', null, true) +
        ytShellHtml(item.video.id, '',
          (hasCrop ? 'agmf-yt-crop ' : (vert ? 'agmf-media--yt-short ' : '')) + 'agmf-yt-loop',
          capOverlayHtml(item));
      var loopShell = el.querySelector('[data-agmf-yt]');
      if (loopShell) {
        loopShell.dataset.agmfLoopStart = String(loop.startSeconds || 0);
        loopShell.dataset.agmfLoopEnd = String(loop.endSeconds || 0);
        if (hasCrop) {
          // shell is 3:4. Scale the video so the crop WINDOW cover-fills the
          // shell, then center the window on the editor's focus point.
          var SHELL = 3 / 4;
          var winFrac = cropAspect / crop.sourceAspect;      // window width / video width
          var wPct = 100 / winFrac;                           // iframe width, % of shell width
          var hPct = (wPct / crop.sourceAspect) / (1 / SHELL); // iframe height, % of shell height
          var fx = typeof crop.focusX === 'number' ? crop.focusX : 0.5;
          var fy = typeof crop.focusY === 'number' ? crop.focusY : 0.5;
          var lPct = Math.min(0, Math.max(100 - wPct, 50 - fx * wPct));
          var tPct = Math.min(0, Math.max(100 - hPct, 50 - fy * hPct));
          loopShell.style.setProperty('--agmf-crop-w', wPct.toFixed(2) + '%');
          loopShell.style.setProperty('--agmf-crop-h', hPct.toFixed(2) + '%');
          loopShell.style.setProperty('--agmf-crop-l', lPct.toFixed(2) + '%');
          loopShell.style.setProperty('--agmf-crop-t', tPct.toFixed(2) + '%');
        }
        // the caption belongs INSIDE the media box so it rides the crop
        var capEl = el.querySelector('.agmf-loop-cap');
        if (capEl) loopShell.appendChild(capEl);
      }
      return el;
    }
    el.innerHTML = postHead(isShort ? 'YouTube Short' : 'YouTube Video', '', null, ICO.yt) +
      ytShellHtml(item.video.id, item.thumb, vert ? 'agmf-media--yt-short' : '') +
      ytUnder(item);
    return el;
  }

  function ytUnder(item) {
    var m = item.metrics || {};
    var src = item.source || {};
    // YouTube's own under-video order: title, then channel byline with
    // avatar &#183; views &#183; relative age
    var by = [];
    if (src.name) by.push(esc(src.name));
    if (m.views) by.push(fmtCount(m.views) + ' views');
    var ago = fmtAgo(item.publishedAt);
    if (ago) by.push(ago);
    var html = '<div class="agmf-caption agmf-yt-under"><h3>' + esc(item.title || 'Agilite') + '</h3>';
    if (by.length) {
      html += '<div class="agmf-yt-byline">' +
        (src.avatar ? '<img class="agmf-yt-chan" src="' + esc(src.avatar) + '" alt="" loading="lazy">' : '') +
        '<span>' + by.join(' &#183; ') + '</span></div>';
    }
    // like/comment pills removed (Dorian 2026-08-12): an actual YouTube
    // video shows title + channel byline only
    return html + '</div>';
  }

  // The product card and feed use the same player. In particular, a saved
  // YouTube clip must retain the editor's range/crop and explicit geometry.
  function heroClipShell(item) {
    if (item.video.provider !== 'youtube') return videoShell(item, '', capOverlayHtml(item));
    var shell = ytCard(item).querySelector('.agmf-media');
    return shell ? shell.outerHTML : '';
  }

  function reviewStars(item) {
    // real rating from the library tags ("N/5") - no rating, no stars
    var chip = ratingChip(item);
    if (!chip) return '';
    var n = parseInt(chip, 10) || 0;
    if (n < 1 || n > 5) return '';
    var stars = '';
    for (var i = 0; i < 5; i++) stars += i < n ? '★' : '☆';
    return '<div class="agmf-review-stars" aria-label="Rated ' + n + ' out of 5">' + stars + '</div>';
  }

  function reviewCard(item) {
    // v37 (Dorian): every review carries a real header - the reviewer's name
    // with a letter-monogram avatar, "Verified buyer", and the stars. The
    // photo keeps title + quote only (the header owns stars + identity).
    // dir="auto" keeps Hebrew reviews readable.
    var el = document.createElement('article');
    el.className = 'agmf-card agmf-card--review';
    el.__agmfItem = item;   // tapping opens the FULL review
    var revDate = fmtDate(item.publishedAt);
    var revName = ((item.source || {}).name || '').trim() || 'Verified buyer';
    var initial = revName.charAt(0).toUpperCase();
    // text posts from the community are NOT reviews - the sub line says
    // where they come from, never "Verified buyer" (Dorian 2026-08-12)
    var revSub = item.platform === 'Reviews' ? 'Verified buyer'
      : item.platform === 'T&E' ? 'T&E Community' : 'Community';
    var head =
      '<div class="agmf-head agmf-head--review">' +
      '<span class="agmf-avatar agmf-avatar--mono" aria-hidden="true">' + esc(initial) + '</span>' +
      '<span class="agmf-head-copy"><strong dir="auto">' + esc(revName) +
      (item.platform === 'Reviews' ? ICO.verified : '') + '</strong>' +
      '<small>' + revSub + (revDate ? ' · ' + revDate : '') + '</small></span></div>';
    if (item.thumb) {
      el.innerHTML = head +
        '<div class="agmf-media agmf-media--43 agmf-review-hero">' +
        '<img class="agmf-review-img" loading="lazy" alt="" src="' + esc(item.thumb) + '">' +
        '<div class="agmf-review-shade" aria-hidden="true"></div>' +
        '<div class="agmf-review-over">' + reviewStars(item) +
        (item.title ? '<h3 dir="auto">' + esc(item.title) + '</h3>' : '') +
        (item.text ? '<p dir="auto">' + esc(item.text) + '</p>' : '') +
        '</div></div>';
    } else {
      el.innerHTML = head +
        '<div class="agmf-review-copy">' + reviewStars(item) +
        '<h3 dir="auto">' + esc(item.title || '') + '</h3>' +
        (item.text ? '<p dir="auto">' + esc(item.text) + '</p>' : '') +
        '</div>';
    }
    return el;
  }

  function monoHead(name, sub) {
    // community/reviewer identity: letter-monogram avatar + name (no photo
    // avatars for people - only their initial)
    var initial = (String(name).trim().charAt(0) || 'A').toUpperCase();
    return '<div class="agmf-head agmf-head--person">' +
      '<span class="agmf-avatar agmf-avatar--mono" aria-hidden="true">' + esc(initial) + '</span>' +
      '<span class="agmf-head-copy"><strong dir="auto">' + esc(name) + '</strong>' +
      (sub ? '<small>' + esc(sub) + '</small>' : '') + '</span></div>';
  }

  function imageCard(item) {
    var el = document.createElement('article');
    el.className = 'agmf-card agmf-card--image';
    var img = '<img class="agmf-image" loading="lazy" alt="" src="' + esc(item.thumb) + '">';
    var src = item.source || {};
    var head;
    if (item.platform === 'T&E') head = monoHead(src.name || 'T&E member', 'T&E Community');
    else if (item.platform === 'Assets') {
      head = postHead('Agilite', src.label || 'Agilite Action', '', null, true);
      // product renders CONTAIN on the light ground - cover-zooming a
      // transparent belt render crops it into abstraction (Dorian 2026-08-12);
      // action PHOTOS keep the full-bleed cover
      if ((src.label || '') === 'Product View') el.className += ' agmf-card--contain';
    }
    else head = sourceHead(item, item.platform === 'Instagram' ? 'Instagram' : 'Product');
    // NOTHING IN A CARD LEAVES THE SITE. Instagram stills used to link to the
    // post, source-true, while every other still navigated nowhere (Dorian
    // 2026-08-12: "a feed card that yanks the shopper to another page
    // mid-scroll is a bug"). On 2026-09-03 he closed the exception too -
    // "clicking an Instagram content should NOT open the instagram page" and
    // then "clicking any content card should not open any external link" - so
    // the picture is just a picture, and on desktop it opens in the dialog
    // like every other piece.
    el.innerHTML = head + img + igContainer(item);
    return el;
  }

  function articleCard(item) {
    var el = document.createElement('article');
    el.className = 'agmf-card agmf-card--article' + (item.thumb ? '' : ' agmf-article--noimg');
    el.__agmfItem = item;   // the in-card reader needs the full record
    var src = item.source || {};
    var ago = fmtAgo(item.publishedAt);
    // v39.28: the card is a magazine plate - full-bleed art, a glass
    // publisher chip, the headline over a deep gradient, and a quiet READ
    // affordance instead of a link that looks like a link.
    // the publisher reads like every other source: icon + name in the HEAD
    // at the top of the card (Dorian 2026-08-13)
    el.innerHTML =
      postHead(src.name || 'Press', 'Article', src.avatar || null, src.avatar ? null : ICO.news) +
      '<div class="agmf-article-body">' +
      (item.thumb ? '<img class="agmf-article-img" loading="lazy" alt="" src="' + esc(item.thumb) + '">' : '') +
      '<span class="agmf-article-shade" aria-hidden="true"></span>' +
      '<div class="agmf-article-copy">' +
      '<h3 dir="auto">' + esc(item.title || '') + '</h3>' +
      (item.text ? '<p dir="auto">' + esc(item.text) + '</p>' : '') +
      '<span class="agmf-article-link">READ THE ARTICLE &#8594;</span>' +
      '</div></div>';
    return el;
  }

  function renderItem(item) {
    var el = buildCard(item);
    // ONE place stamps it, so every surface asks the same question of the DOM
    // instead of re-deriving "is this a loop" from fields it happens to hold.
    if (el && isLooping(item)) el.dataset.agmfLoop = '1';
    // EVERY card knows its record, not only reviews and articles. A rail
    // renders its slides through here without an index or a lap, so anything
    // asking the DOM "what is this?" (the desktop lightbox) got nothing back
    // for a slide and fell back to the product - a T&E photo by Archer opened
    // as "Agilite / Product" (measured 2026-09-02). The record on the element
    // is the answer that survives rails, clones and re-deals alike.
    if (el && !el.__agmfItem) el.__agmfItem = item;
    // AND IT SAYS SO IN THE MARKUP. A rail's slides were invisible to
    // anything that reads attributes: the phone review tool looks for
    // `[data-agmf-id]`, found only the OUTER card, and so recorded a verdict
    // against the rail's first item and dimmed the whole card - "if i swipe
    // right/left i dont want to reject the other content on the same card"
    // (Dorian 2026-09-03). With an id per slide the tool's own rule (the
    // innermost thing nearest the middle) lands on what is actually on
    // screen.
    // `data-agmf-ck` is deliberately NOT stamped here: that key belongs to
    // the seen ledger, and what counts as "shown" on a swipeable card is its
    // own decision (feed.seenPolicy), not a side effect of this line.
    if (el && item) {
      if (item.id != null && !el.dataset.agmfId) el.dataset.agmfId = String(item.id);
      if (item.contentType && !el.dataset.agmfType) el.dataset.agmfType = String(item.contentType);
    }
    return el;
  }

  function buildCard(item) {
    if (item.shape === 'article') return articleCard(item);
    var v = item.video || null;
    if (v && v.provider === 'file') {
      return item.platform === 'Shopify' ? featureCard(item) : shortCard(item);
    }
    if (v && v.provider === 'youtube') return ytCard(item);
    if (item.platform === 'Reviews') return reviewCard(item);
    if (item.thumb) return imageCard(item);
    return reviewCard(item);   // text-only non-review still reads as a quote card
  }

  // ── engine ────────────────────────────────────────────────────────────────

  function boot(section) {
    var feedEl = qs(section, '[data-agmf-feed]');
    var sentinel = qs(section, '[data-agmf-sentinel]');
    var returnBtn = qs(section, '.agmf-return');
    var topBtn = qs(section, '[data-agmf-top]');
    var resumeBar = document.querySelector('[data-agmf-resume]');
    if (!feedEl || !sentinel) return;

    section.style.setProperty('--agmf-accent', section.dataset.accent || '#92AE74');

    var handle = section.dataset.productHandle || '';
    scorpionUrl = section.dataset.scorpion || '';
    scorpionLightUrl = section.dataset.scorpionLight || '';
    var maxCycles = Math.max(2, parseInt(section.dataset.maxCycles, 10) || 24);
    var sessionKey = SESSION_PREFIX + handle;
    var order = [];            // interleaved items; cycle content is a pure fn of index

    // ONE SEED FOR THE SESSION. The order below draws on it, so a reload
    // inside the session (scroll-resume) rebuilds the same order and lands
    // where it was; the next visit draws a new one and the feed reads
    // differently. mulberry32: tiny, deterministic, good enough for dealing
    // cards - this is not cryptography.
    var orderSeed = (function () {
      var key = 'AGMF_Seed:' + handle;
      try {
        var kept = parseInt(sessionStorage.getItem(key), 10);
        if (kept > 0) return kept;
        var fresh = (Date.now() ^ (Math.random() * 0x7fffffff)) >>> 0 || 1;
        sessionStorage.setItem(key, String(fresh));
        return fresh;
      } catch (err) {
        return (Date.now() >>> 0) || 1;
      }
    })();
    function seededRandom(seed) {
      var s = seed >>> 0;
      return function () {
        s = (s + 0x6D2B79F5) >>> 0;
        var r = Math.imul(s ^ (s >>> 15), 1 | s);
        r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
        return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
      };
    }
    function shuffledBy(list, rnd) {
      var a = list.slice();
      for (var i = a.length - 1; i > 0; i--) {
        var j = Math.floor(rnd() * (i + 1));
        var tmp = a[i]; a[i] = a[j]; a[j] = tmp;
      }
      return a;
    }
    var cycles = [];           // {el, index, materialized, height}
    var soundOn = false;
    var raf = 0;

    // ---- interleave motion / image / review / image, prototype-style ----
    // NEVER TWO MOVING THINGS SIDE BY SIDE (Dorian 2026-08-31).
    //
    // On desktop the feed lays this order out two per row, so neighbours in
    // the order ARE a row. Two videos in one row is two things competing for
    // the same pair of eyes - one of the pair must be still. The rotation
    // above interleaves KINDS, which usually separates them, but nothing
    // guaranteed it: a lane of shorts followed by a lane of loops put motion
    // next to motion.
    //
    // The rule is applied to the ORDER, not to the layout, so it holds for
    // whatever column count the page is using - and costs nothing on a phone,
    // where one column means the same order simply alternates as it scrolls.
    // Repairing pairs after the fact only worked when a still existed AHEAD
    // of the clash. The product gallery builds every photograph first and
    // every video last, so there was never one ahead and the videos stayed
    // shoulder to shoulder (Dorian 2026-08-31, second report). Dealing the
    // two piles alternately is the rule that holds wherever it is applied.
    // DEAL THE VIDEOS EVENLY, FROM THE FIRST ROW. The rule above used to be
    // "deal a video only while videos still outnumber stills" - so on the
    // belt (56 videos, 72 stills) the first sixteen cards were all stills and
    // the first video sat in row nine (measured on the shipped feed, Dorian
    // 2026-09-02: "why we wait so long to add it"). Now motion opens, the
    // stills are spread between the videos in proportion, and no two videos
    // are ever order-neighbours while a still remains to separate them.
    function spreadMotion(list) {
      var moves = function (it) { return !!(it && (it.video || it.__motion)); };
      var moving = [], still = [];
      list.forEach(function (it) { (moves(it) ? moving : still).push(it); });
      if (moving.length < 2 || !still.length) return list;
      var out = [], mi = 0, si = 0;
      var stillsPerVideo = still.length / moving.length;
      var carry = 0;
      while (mi < moving.length) {
        out.push(moving[mi++]);
        // How many stills follow THIS video: at least one while stills last,
        // and the fraction carried forward so the spacing stays even across
        // the whole feed rather than bunching at either end.
        carry += stillsPerVideo;
        var n = Math.max(si < still.length ? 1 : 0, Math.floor(carry));
        carry -= n;
        while (n-- > 0 && si < still.length) out.push(still[si++]);
      }
      while (si < still.length) out.push(still[si++]);
      return out;
    }
    var spreadMotionEven = spreadMotion;   // the name the harness measured under

    function separateMotion(list) {
      var spread = spreadMotion(list);
      if (spread !== list) {
        list.length = 0;
        spread.forEach(function (it) { list.push(it); });
      }
      return list;
    }

    function buildOrder(items) {
      // STRICT ROTATION over content KINDS (Dorian 2026-08-12): a kind
      // repeats only once every kind that still has items has shown - the
      // scroll cycles the content types consistently instead of clumping.
      // Kinds = the same laneOf() families the carousels group by, so the
      // order and the carousels agree on what "same kind" means. ONE index
      // per kind (the two-index duplicate bug stays dead). The tail is the
      // deepest kind's leftovers - by then the whole rotation has shown.
      // ── SEEN CONTENT COMES LAST. ALL OF IT. (Dorian 2026-09-08: "if a
      //    content has been shown it will not be shown again except at the
      //    very end ... I keep seeing same content at similar placement") ──
      // The policy (agmf-policy.js) hands over one list, unseen first and the
      // pushed-back items behind them - and then this builder split that
      // list into KINDS and dealt the kinds round-robin. "Behind" survived
      // only inside each kind: the moment a kind ran out of unseen pieces,
      // its seen ones were dealt in the very next round, into the same slots
      // as before. Measured on the preview 2026-09-08 with 54 of 112 items
      // pushed back: the first card of the feed was one seen fifteen times,
      // on two loads in a row.
      //
      // So the deal is run twice: once over everything they have not been
      // shown, once over what they have, and the second deck follows the
      // first. Every kind still rotates, the videos still lead each round,
      // the preference blend still works inside each kind - the only change
      // is that the seen deck cannot start before the fresh one is finished.
      // What is "seen" is what the policy just said: its explain() names the
      // pieces it pushed behind and the ones the floor brought back.
      var tailCks = {};
      try {
        var verdict = window.AGMF_Policy && window.AGMF_Policy.explain ? window.AGMF_Policy.explain() : null;
        if (verdict) {
          (verdict.pushed || []).concat(verdict.restored || []).forEach(function (ck) { tailCks[ck] = 1; });
        }
      } catch (e) { /* no verdict - one deck, as before */ }
      var fresh = [], tail = [], cardLoops = [];
      items.forEach(function (it) {
        if (!it) return;
        // card-only: the exporter marked this as ALREADY SHOWN on the
        // product card (deck still or gallery clip). It stays in feed.items
        // so the gallery can ride it, but it never becomes a feed card -
        // the surface-dedupe setting in Agilite Media (Dorian 2026-08-26).
        if (it.ck && deadKeys[it.ck]) return;
        if (it.cardOnly) {
          // OUR LOOPS ARE NOT CARD-ONLY UNTIL THE CARD HAS SHOWN THEM (Dorian
          // 2026-10-07: "about the GIF/Loops made by us, if someone didnt see
          // it in the product cart, then it should be on the feed, so we make
          // sure there see it"). Measured on the belt: both loops sit 4th and
          // 5th in the pinned card, so on a phone they are three swipes away
          // and the card-only rule kept them out of the feed for everybody
          // who never swiped. A loop the card has shown this visit is still
          // card-only; one this browser saw before goes with the seen deck;
          // the rest LEAD the feed. When the card shows one later,
          // watchCardLoops() reports it and heroShowedItem() re-deals the
          // cycles below the fold without it, as it does for the tail.
          if (isLooping(it) && !(it.ck && heroShown[it.ck])) {
            (it.ck && tailCks[it.ck] ? tail : cardLoops).push(it);
          }
          return;
        }
        (it.ck && tailCks[it.ck] ? tail : fresh).push(it);
      });
      var rnd = seededRandom(orderSeed);
      // A MIXED BUNDLE FEED WITH SHARES (Dorian 2026-10-08) deals each product
      // by the rules below and mixes them lap by lap at the chosen rates - see
      // makeSharePlan. Everything above still applies first: dead pieces,
      // card-only pieces and the card's loops are sorted exactly as here.
      // typeof: the scripts/test-agmf-*.cjs harnesses run this function cut out
      // of the engine, in sandboxes that do not declare the plan.
      var plan = typeof sharePlan === 'undefined' ? null : sharePlan;
      // it either deals lap 0 of the mix into `order` (spread part by part),
      // or declines and the feed is dealt as it always was
      if (plan && !plan.start(fresh, tail, cardLoops, tailCks)) sharePlan = plan = null;
      if (!plan) {
        dealDeck(fresh, rnd);
        // unseen card loops first: the per-deck spread then makes them the
        // opening motion of the feed rather than wherever their kind came round
        Array.prototype.unshift.apply(order, cardLoops);
        var cut = order.length;
        dealDeck(tail, rnd);
        // THE VIDEOS ARE SPREAD PER DECK, NOT OVER BOTH (2026-09-23). The rule
        // above - seen content only at the very end (Dorian 2026-09-08) - was
        // still broken one step later: separateMotion spread the videos of BOTH
        // decks over the whole order, so when the seen deck was mostly video
        // and the fresh deck mostly stills, seen videos were dealt in between
        // the last unseen stills. Measured on a 36/68 fresh + 20/2 seen split
        // (videos/stills): 19 seen cards ahead of the last unseen one. Each
        // deck is now spread on its own and the seen deck still follows the
        // fresh one. With no seen deck - every first visit - this is the same
        // single call as before. The one cost: where the two decks meet, deep
        // in the scroll, two videos can end up side by side.
        if (cut > 0 && cut < order.length) {
          var freshPart = order.slice(0, cut), tailPart = order.slice(cut);
          separateMotion(freshPart);
          separateMotion(tailPart);
          order.length = 0;
          freshPart.concat(tailPart).forEach(function (it) { order.push(it); });
        } else {
          separateMotion(order);
        }
      }
      // With shares a lap holds only part of the library - the rest arrives in
      // later laps - so what the card's showings are counted against is every
      // piece of every product, not lap 0; and so is the stream's floor of
      // unshown pieces (poolLen, itemAtFlat): a lap can be far shorter than
      // the library - a 5-piece product at 50% makes it 10 cards - and
      // measured against the lap the floor switched every skip off.
      var pool = plan ? plan.pieces() : order;
      orderCks = {};
      pool.forEach(function (it) { if (it && it.ck) orderCks[it.ck] = 1; });
      poolLen = pool.length;
      countShownInOrder();
      // ON THE DESKTOP GRID THEY OPEN THE SECOND BLOCK (2026-10-08). The card
      // and the feed share one pane there, so the card's last row stands right
      // above block 0, and a loop at its head was the same clip one row under
      // its own slide whenever the pane jumped past the report (K-Zero SF's
      // loop is its last slide). Every desktop shopper scrolls through the
      // card to reach the feed, so a loop still unseen by block 1 is one the
      // card really did not show. Moved AFTER the spread, so the phone's order
      // is untouched and the loops are not re-spread past block 1; and after
      // the floor above, which the block planner's dry run reads.
      if (cardLoops.length && columns() >= 2) seatLoopsInBlockOne(cardLoops);
    }

    // ── WHERE BLOCK 1 REALLY STARTS (2026-10-08) ──────────────────────────
    // The loops used to be moved to index 6, which is block 1's first card
    // only while block 0 is six cards. spanFor() grows a block to seven-ten
    // cards to close its last row, decided by the very cards it holds, so a
    // loop at 6 rendered INSIDE block 0 whenever it grew - K-Zero SF on half
    // the seeds, every Magnetix bundle on every seed (measured 2026-10-08) -
    // one row under its own slide, and a block the shopper has reached is
    // never re-dealt. Where block 1 starts depends on where the loops sit, so
    // each start from 6 up is TRIED: block 0 is planned by spanFor itself with
    // the loops there (the real planner, not a second model of it - see
    // layBlock), and the first start where block 0 ends right before them,
    // full, with block 1 free of rows of two moving cards, wins (the search
    // below); else the first where block 0 ends right before them; else the
    // first where block 0 does not hold them; else 6, as before. It runs only
    // while no block is planned - at boot, and when heroShowedItem throws the
    // whole feed back (a piece the card showed ahead of the loops moves them a
    // card earlier) - and leaves the stream exactly as it found it.
    function seatLoopsInBlockOne(loops) {
      seatedLoops = loops;
      if (!loops.length || columns() < 2 || streamItem.length || cycleSpan[0]) return;
      var reach = 2 * (CARDS_PER_CYCLE + 2 * maxColumns()), moved = [], base = [];
      order.forEach(function (it, i) {
        (i < reach && loops.indexOf(it) !== -1 ? moved : base).push(it);
      });
      if (!moved.length) return;
      var cursor = streamCursor, kept = {}, key, rest = false;
      for (key in lapOrders) kept[key] = 1;
      // the first loop before base[at], loop m before base[at + m * gap]; a
      // gap of 0 seats them side by side, as they were seated before (with
      // one loop the gap means nothing)
      function seat(at, gap) {
        order.length = 0;
        for (var i = 0; i <= base.length; i++) {
          for (var m = 0; m < moved.length; m++) if (i === Math.min(base.length, at + m * gap)) order.push(moved[m]);
          if (i < base.length) order.push(base[i]);
        }
      }
      function flatsOf(sp) { var a = [], f; for (f = sp.start; f < sp.start + sp.len; f++) a.push(f); return a; }
      // 3: block 0 ends right before a loop and block 1 has no row holding two
      // moving cards; 2: block 0 ends right before a loop; 1: it holds none;
      // 0: it holds one, or it cannot close its last row (blockIsFull - the
      // planner's hard constraint: a seat that leaves block 0 a hole beside a
      // lone card never wins, review 2026-10-08)
      function verdict() {
        try {
          var sp = spanFor(0), f, held = false, cols = maxColumns();
          if (!blockIsFull(flatsOf(sp), cols)) return 0;
          for (f = sp.start; f < sp.start + sp.len; f++) if (moved.indexOf(itemAtFlat(f)) !== -1) held = true;
          if (held) return 0;
          if (moved.indexOf(itemAtFlat(sp.start + sp.len)) === -1) return 1;
          var rows = layBlock(spaceOutMotion(flatsOf(spanFor(1)), cols), cols), r, x, n;
          for (r = 0; r < rows.length; r++) {
            if (rows[r][0] !== null && spansRow(itemAtFlat(rows[r][0]))) continue;
            for (n = 0, x = 0; x < cols; x++) if (rows[r][x] !== null && isMotion(itemAtFlat(rows[r][x]))) n++;
            if (n >= 2) return 2;
          }
          return 3;
        } catch (e) {
          return 0;
        } finally {
          // did the dry run reach a loop after the first? If not, a wider gap
          // leaves every card it read where it was: the same verdict
          rest = false;
          for (var m = 1; m < moved.length; m++) if (streamItem.indexOf(moved[m]) !== -1) rest = true;
          streamItem.length = 0;
          streamBase.length = 0;
          streamCursor = cursor;
          cycleSpan.length = 0;
          for (key in lapOrders) if (!kept[key]) delete lapOrders[key];
        }
      }
      // never before where the stream resumes (a re-deal behind shown pieces)
      var from = Math.min(base.length, Math.max(CARDS_PER_CYCLE, cursor));
      var best = -1, bestGap = 0, opens = -1, fair = -1, at, gap, v;
      // TWO LOOPS ARE NOT SEATED SIDE BY SIDE (review 2026-10-08). Seated
      // together at block 1's head they share its first row - the Magnetix
      // Battle Belt page's two loops, on 66 of 100 seeds of its bundled feed -
      // and where the cards after them move too, block 1 has more moving
      // cards than its rows can part (`LL M. M.`). Only the first loop opens
      // block 1; each other one is `gap` base cards after the one before it.
      // The first start and the smallest gap where block 0 ends right before
      // the first loop AND block 1 lays out with no row of two moving cards
      // wins (verdict 3). Where none does, the old choice: the loops side by
      // side at the first start where block 0 ends right before them, else the
      // first where it holds none, else the first start.
      for (at = from; at <= base.length && at < from + reach && best < 0; at++) {
        seat(at, 0);                    // the old seat, kept as the fallback
        v = verdict();
        if (v >= 2 && opens < 0) opens = at;
        if (v === 1 && fair < 0) fair = at;
        if (moved.length === 1) { if (v === 3) best = at; continue; }
        for (gap = 1; gap <= reach && best < 0; gap++) {
          seat(at, gap);
          if (verdict() === 3) { best = at; bestGap = gap; }
          else if (!rest) break;        // past what blocks 0 and 1 read
        }
      }
      if (best >= 0) seat(best, bestGap);
      else seat(opens >= 0 ? opens : (fair >= 0 ? fair : from), 0);
      delete lapLanes[0];               // the rails' memo of lap 0's order
    }

    // one deck, dealt onto `order`: kinds in strict rotation, videos leading
    // each round, the order within a round drawn fresh from `rnd`. `into`, when
    // given, receives the deal instead of `order` - the share plan deals each
    // product of a mixed feed into its own pass by exactly these rules.
    function dealDeck(items, rnd, into) {
      var byKind = {};
      var kinds = [];
      items.forEach(function (it) {
        var k = laneOf(it);
        if (!byKind[k]) { byKind[k] = []; kinds.push(k); }
        byKind[k].push(it);
      });
      if (!kinds.length) return;
      // ── WHOSE FEED IS THIS (Dorian 2026-09-03) ────────────────────────
      // "Start wiring the Country to give priority to the content tagging
      // its languages ... harmonize it dont just put all straight but it
      // should along with the international content", and the same for a
      // known favourite colour.
      //
      // The preference is applied INSIDE each kind, before the rotation
      // draws from it: agmf-pref.js deals the pieces that speak to this
      // visitor alternately with the international ones (English, and
      // anything the library could not name), and sinks a language that is
      // neither theirs nor international to the back of its kind.
      //
      // This is the one place it can go without disturbing anything: the
      // kind ring, the seeded round order, separateMotion, the block sizer
      // and the per-platform caps all work on the SHAPE of the sequence,
      // and this only changes which piece of a given kind is next. A visitor
      // with no country and no colour changes nothing at all - blend()
      // returns the list it was given.
      if (window.AGMFPref && window.AGMFPref.blend) {
        try {
          kinds.forEach(function (k) { byKind[k] = window.AGMFPref.blend(byKind[k]); });
        } catch (e) { /* a preference is a nicety; the feed is not */ }
      }
      // the feed still OPENS on motion - video kinds lead each rotation
      kinds.sort(function (a, b) {
        var av = byKind[a][0].video ? 0 : 1;
        var bv = byKind[b][0].video ? 0 : 1;
        return av - bv;
      });
      var idx = {};
      var left = 0;
      kinds.forEach(function (k) { idx[k] = 0; left += byKind[k].length; });
      // A FIXED RING HAS A FIXED PERIOD. With two still kinds left it is two,
      // and on a two-column desktop a period of two pins each kind to one
      // side for the rest of the scroll - review always right, T&E always
      // left (Dorian 2026-09-02: "too much pattern"). Every round still
      // shows each kind once, and the videos still lead it; only the order
      // WITHIN the round is drawn fresh each time, from the session's seed.
      var motionKinds = kinds.filter(function (k) { return !!byKind[k][0].video; });
      var stillKinds = kinds.filter(function (k) { return !byKind[k][0].video; });
      while (left > 0) {
        var round = shuffledBy(motionKinds, rnd).concat(shuffledBy(stillKinds, rnd));
        for (var i = 0; i < round.length; i++) {
          var k = round[i];
          if (idx[k] < byKind[k].length) {
            (into || order).push(byKind[k][idx[k]++]);
            left--;
          }
        }
      }
    }

    function laneOf(item) {
      // Carousels never mix media kinds (Dorian 2026-08-11): sliding a
      // YouTube Short gives another YouTube Short, a Reel another Reel, an
      // article another article. A card with no same-kind siblings simply
      // never becomes a carousel.
      if (item.shape === 'article') return 'article';
      if (item.video && item.video.provider === 'youtube') {
        // created loops are their OWN kind: in the shorts lane they
        // carouselized with taller cards and stretched into dead space
        // below the clip (Dorian 2026-08-12)
        if (item.video.loop) return 'yt-loop';
        // KIND first (Dorian 2026-08-12): a Short is never the same card
        // kind as a long-form video, whatever its frame shape - the old
        // geometry-only split slid a landscape Short in with the videos.
        // Geometry is a SUFFIX so a carousel's slides still share a shell.
        var v = item.video.vertical != null ? item.video.vertical : item.format === 'Short';
        return (item.format === 'Short' ? 'yt-short' : 'yt-video') + (v ? '-v' : '-h');
      }
      if (item.video) return 'file-' + (item.platform || 'video');
      if (item.platform === 'Reviews') return 'review';
      if (!item.thumb && item.text) return 'text-' + (item.platform || 'community');
      return 'image-' + (item.platform || 'other');
    }

    // The library is finite, so a long scroll MUST revisit content - but it
    // must not replay the same sequence (Dorian 2026-08-12: "lots of copies
    // of product views"). Each LAP re-interleaves with the kind ring and the
    // items inside each kind both rotated, so a card lands among different
    // neighbours every time and no kind clusters.
    var lapOrders = {};
    var lapLanes = {};
    // PER-PRODUCT SHARES of a mixed bundle feed (makeSharePlan). null on every
    // other feed, and then the order is dealt exactly as it always was.
    var sharePlan = null;
    // with shares: whether a piece is the card's own product's - the card and
    // its endless tail draw only from that product (heroTailPool)
    var shareCardOwns = null;
    // the card's unseen loops seatLoopsInBlockOne placed on the desktop grid
    var seatedLoops = [];

    // Each lane's items IN SCROLL ORDER, plus every item's position within its
    // own lane. The carousel needs this to give each card a DIFFERENT slice of
    // its lane; without it every card of a lane starts from the same place.
    function lanesForLap(lap, lapOrder) {
      if (lapLanes[lap]) return lapLanes[lap];
      var lists = {}, pos = {};
      for (var i = 0; i < lapOrder.length; i++) {
        var it = lapOrder[i];
        if (!it) continue;
        var l = laneOf(it);
        if (!lists[l]) lists[l] = [];
        pos[it.id] = lists[l].length;
        lists[l].push(it);
      }
      lapLanes[lap] = { lists: lists, pos: pos };
      return lapLanes[lap];
    }

    // WHICH SIBLINGS THIS CARD'S RAIL HOLDS.
    //
    // It used to scan the whole scroll forward from the card and take the
    // first six of its kind. Every card of a lane therefore started from
    // nearly the same place, and once the short lanes run out the rotation
    // puts same-kind cards two or three apart - so two T&E cards a swipe
    // apart showed the SAME photo first. Measured on the shipped belt feed:
    // of 22 same-lane pairs within one screen-ful, 10 led with an identical
    // picture and 3 rails were identical the whole way through.
    //
    // Now each card takes its own window of its lane, strided by the rail
    // length so successive cards TILE the lane instead of stacking on it.
    // Same feed, same measurement: 0 of 22 lead with the same picture and no
    // two rails are identical. Some overlap deeper in remains and cannot not
    // remain - a lane of ten cannot produce two disjoint rails of six - but
    // the repeat a shopper actually notices is the first one.
    function railPoolFor(item, oi, lap, lapOrder) {
      var lane = laneOf(item);
      var lanes = lanesForLap(lap, lapOrder);
      var list = lanes.lists[lane] || [];
      var n = list.length;
      var pool = [], seenIds = {};
      seenIds[item.id] = true;
      // The slides must not be the very posts sitting just below this card.
      // The old fixed 12-card offset did that by side effect; stating it
      // outright keeps it true whatever the stride does.
      var below = 0;
      for (var b = 1; b < lapOrder.length && below < 2; b++) {
        var nxt = lapOrder[(oi + b) % lapOrder.length];
        if (!nxt || laneOf(nxt) !== lane) continue;
        seenIds[nxt.id] = true;
        below++;
      }
      var p = pos_of(lanes, item);
      var start = n ? (p * (RELATED_COUNT + 1) + 3) % n : 0;
      for (var k = 0; k < n && pool.length < RELATED_COUNT; k++) {
        var rel = list[(start + k) % n];
        if (!rel || seenIds[rel.id]) continue;
        // Seen on the phone's card: not in a rail either. The stream keeps
        // it out of the cards; a rail is one sideways swipe from a card.
        if (rel.ck && heroShown[rel.ck]) continue;
        seenIds[rel.id] = true;
        pool.push(rel);
      }
      return pool;
    }

    function pos_of(lanes, item) {
      var v = lanes.pos[item.id];
      return typeof v === 'number' ? v : 0;
    }
    // `base` and `seed`, when given, make this a PURE re-deal of that list for
    // this lap (redealLap below: no memo, no plan) - the share plan deals each
    // product's later passes with it. The body is the lap re-deal it always was.
    function orderForLap(lap, base, seed) {
      if (!base) {
        if (lap === 0) return order;
        // a mixed feed with shares keeps its own laps, out of reach of the
        // colour change's purge of lapOrders (typeof: see buildOrder)
        var plan = typeof sharePlan === 'undefined' ? null : sharePlan;
        if (plan) return plan.lap(lap);
        if (lapOrders[lap]) return lapOrders[lap];
      }
      var byKind = {}, kinds = [];
      (base || order).forEach(function (it) {
        var k = laneOf(it);
        if (!byKind[k]) { byKind[k] = []; kinds.push(k); }
        byKind[k].push(it);
      });
      // THE LANE IS RE-BLENDED FOR EACH LAP, so a preference the shopper
      // expressed AFTER the feed was built is answered on the way round -
      // picking a colourway on the page re-ranks what comes next without
      // moving a card that is under their thumb right now. Same place, same
      // rule and same invariants as the first lap's blend.
      if (window.AGMFPref && window.AGMFPref.blend) {
        try {
          kinds.forEach(function (k) { byKind[k] = window.AGMFPref.blend(byKind[k]); });
        } catch (e) { /* a preference is a nicety; the feed is not */ }
      }
      var idx = {}, left = 0;
      kinds.forEach(function (k) {
        var list = byKind[k];
        var r = lap % list.length;
        byKind[k] = list.slice(r).concat(list.slice(0, r));
        idx[k] = 0; left += list.length;
      });
      // Same dealing rule as the first lap, from a per-lap seed: every kind
      // once per round, drawn in a fresh order - not the same ring rotated
      // by one, which kept the same neighbours a step apart.
      var rnd = seededRandom(((base ? seed : orderSeed) ^ (lap * 0x9E3779B9)) >>> 0);
      var motionKinds = kinds.filter(function (k) { return !!byKind[k][0].video; });
      var stillKinds = kinds.filter(function (k) { return !byKind[k][0].video; });
      var out = [];
      while (left > 0) {
        var round = shuffledBy(motionKinds, rnd).concat(shuffledBy(stillKinds, rnd));
        for (var i = 0; i < round.length; i++) {
          var k = round[i];
          if (idx[k] < byKind[k].length) { out.push(byKind[k][idx[k]++]); left--; }
        }
      }
      separateMotion(out);
      if (!base) lapOrders[lap] = out;
      return out;
    }

    // Today's lap re-deal of any list: the same kinds re-blended, each kind
    // rotated by the lap, a per-lap seed, the videos spread. The body stays in
    // orderForLap - one body, so a product's passes and a feed without shares
    // cannot drift apart, and the harnesses that cut orderForLap out of this
    // file (scripts/test-agmf-*.cjs) still get the whole of it.
    function redealLap(base, lap, seed) {
      return orderForLap(lap, base || [], seed);
    }

    // ── PER-PRODUCT SHARES OF A MIXED FEED (Dorian 2026-10-08) ──────────────
    // A bundle's page can show the feeds of several products mixed into one
    // (Agilite Media's mirror: feed.mirror.of, the card's own product first).
    // Mixed by how many pieces each has, K-Zero SF Plate Carrier + Pincer
    // Placard + Mag-Release Dump Pouch opened 5/11/4 (measured on the live
    // feeds): the bundle's own carrier was a quarter of the first twenty
    // cards. So the mix is set in Agilite Media, as a share per product - 75%
    // K-Zero SF Plate Carrier, 10% Pincer Placard, 15% Mag-Release Dump Pouch -
    // and Dorian approved "shares as rates, not by cutting pieces": every piece
    // of every product still ships and still shows; a small share shows them
    // further down, a few per lap, lap after lap.
    //
    // HOW. Each product is dealt on its own by today's rules - its unseen deck
    // (the card's unseen loops at the head of the card product's), then its
    // seen deck, kinds in rotation, the preference blend inside each kind -
    // into its own PASS; its later passes are today's lap re-deal of that pass
    // (redealLap). The feed is then built LAP by lap. Every lap takes the next
    // q pieces of each product, carrying on where that product's last lap
    // stopped, so a small share ROTATES through all of its pieces; and mixes
    // them by a smooth weighted round-robin - the unseen slices first, the
    // seen slices after ("seen content goes last", inside every lap). Every
    // seed comes from the session's and the product's handle, never
    // Math.random, so a reload in the session and the recycler deal the same.
    //
    // THE LAP is the longest in which no product has to show a piece twice:
    // L = floor(min n/p) over the products (n pieces, p share). At 75/10/15 on
    // 48/36/43 pieces that is one whole pass of K-Zero SF, 64 cards, dealt
    // 48/6/10. EVERY product - the one that runs out first included - is
    // owed (k+1)*L*p pieces by the end of lap k, and each lap pays what is
    // owed, whole pieces by largest remainder, never more of a product than
    // it has. A fraction one lap cannot pay is paid in the next, so the
    // long-run shares are exact and a 1% product still comes round (see quotasAt).
    //
    // THE ONE INVARIANT. Every lap is exactly L = order.length long and is
    // never rebuilt. The stream, the block sizer, the idx/lap stamps, the rails
    // and the lightbox all find a card's item as orderForLap(lap)[idx]. So the
    // laps are kept HERE, where the colour change's purge of lapOrders cannot
    // reach them, and a lap that cannot be built at length L is today's
    // re-deal of lap 0 instead (which is L long).
    //
    // THE CONTRACT with Agilite Media: feed.mirror.shares = {handle: whole
    // percent}, only with two or more products; every item carries `from`, its
    // product's handle (never `source`, which is the creator). An item with no
    // `from`, or one not in the shares, is the card product's (of[0]) - that
    // includes the card loops addCardLoopItems() adds. No shares, fewer than two
    // products with pieces, or the card's product without a share: null, and
    // the feed is dealt exactly as it was.
    function makeSharePlan(feed) {
      var mirror = feed && feed.mirror;
      var raw = mirror && mirror.shares;
      if (!mirror || !Array.isArray(mirror.of) || !raw || typeof raw !== 'object') return null;
      var has = function (o, k) { return Object.prototype.hasOwnProperty.call(o, k); };
      var weight = {}, handles = [];
      mirror.of.forEach(function (h) {
        h = h == null ? '' : String(h);
        if (!h || has(weight, h)) return;
        var w = has(raw, h) ? Number(raw[h]) : 0;
        if (!(w > 0) || !isFinite(w)) return;
        weight[h] = w;
        handles.push(h);
      });
      var first = String(mirror.of[0] == null ? '' : mirror.of[0]);
      // the card's own product holds a share, or there is nothing to mix by:
      // it owns the card, its loops and every piece without a `from`
      if (handles.length < 2 || handles[0] !== first) return null;
      function srcOf(it) {
        var h = it && it.from != null ? String(it.from) : '';
        return has(weight, h) ? h : first;
      }
      function keyOf(it) { return it.ck ? 'ck:' + it.ck : 'id:' + it.id; }
      function hashOf(s) {
        var x = 2166136261;
        for (var i = 0; i < s.length; i++) x = Math.imul(x ^ s.charCodeAt(i), 16777619) >>> 0;
        return x >>> 0;
      }
      var CAP = 2400;       // laps; the stream stops long before (maxCycles)
      var live = [], src = {}, L = 0, W = 0, bind = null;
      var quotas = [], given = {}, seenCks = {}, loops = [], loopReach = 0;
      var laps = [], far = {};
      var moving = function (it) { return !!(it && (it.video || it.__motion)); };

      // PIECES PER LAP, the same rule for every product (2026-10-08). The
      // product that runs out first used to take a fixed round(L*p) every lap
      // and the others split what was left. Its fraction was never carried,
      // so at 99/1 on 48 pieces round(47.52) gave it the whole lap and the 1%
      // product NO card, lap after lap, for ever; and a 2% share came out at
      // 2.78%. Now every product is owed (k+1)*L*p by the end of lap k, less
      // what it has been given, and each lap pays the debts in whole pieces:
      // the floor first, then one more to the most owed until the lap is L -
      // largest remainder, carried, the same arithmetic as before for every
      // product but the first. Never more of a product than it has pieces
      // (two copies of a piece in one lap), and what that cap holds back is
      // simply still owed next lap. The debts are kept times W, so whole-
      // percent shares stay whole numbers. Laps are paid in order, once.
      function quotasAt(k) {
        while (quotas.length <= k) {
          var j = quotas.length, q = {}, owed = {}, used = 0, least = {}, i, h;
          for (i = 0; i < live.length; i++) {
            h = live[i];
            owed[h] = (j + 1) * L * weight[h] - given[h] * W;
            q[h] = Math.max(0, Math.min(src[h].n, Math.floor(owed[h] / W)));
            least[h] = 0;
          }
          // THE CARD'S UNSEEN LOOPS LEAD lap 0 whatever its share: it takes
          // at least as far into its pass as its last loop (loopReach - the
          // stills between them come along, and part them in buildLap), and
          // what that advance costs the others is paid back by the debts.
          if (j === 0 && loopReach && src[first]) {
            least[first] = Math.min(src[first].n, L, loopReach);
            q[first] = Math.max(q[first], least[first]);
          }
          for (i = 0; i < live.length; i++) used += q[live[i]];
          var pick = function (more) {
            var best = null;
            for (var x = 0; x < live.length; x++) {
              var c = live[x];
              if (more ? q[c] >= src[c].n : q[c] <= least[c]) continue;
              var r = owed[c] - q[c] * W;
              if (best === null || (more ? r > owed[best] - q[best] * W : r < owed[best] - q[best] * W)) best = c;
            }
            return best;
          };
          // the sum of the pieces is at least L (L = floor(min n/p)), so a lap
          // can always be filled; the guard only stops a malformed one, which
          // then comes out short and lap() re-deals lap 0 in its place
          for (var g = 0; used < L && g < L; g++) { h = pick(true); if (h === null) break; q[h]++; used++; }
          for (g = 0; used > L && g < L; g++) { h = pick(false); if (h === null) break; q[h]--; used--; }
          for (i = 0; i < live.length; i++) given[live[i]] += q[live[i]];
          quotas.push(q);
        }
        return quotas[k];
      }
      function quota(h, k) { return quotasAt(k)[h]; }
      function passOf(h, j) {
        var s = src[h];
        return s.pass[j] || (s.pass[j] = redealLap(s.pass[0], j, s.seed));
      }
      // The next `quota` pieces of a product, from where its last lap stopped.
      // Where its slice runs from one pass into the next, a piece this lap
      // already holds is OWED to the next lap instead - never twice in a lap.
      function take(h, k) {
        var s = src[h], want = quota(h, k), got = [], have = {};
        var owed = s.owed;
        s.owed = [];
        owed.forEach(function (it) {
          if (got.length < want && !have[keyOf(it)]) { have[keyOf(it)] = 1; got.push(it); }
          else s.owed.push(it);
        });
        // two passes' worth always holds every piece once
        for (var guard = 2 * s.n + 1; got.length < want && guard > 0; guard--) {
          var it = passOf(h, Math.floor(s.cursor / s.n))[s.cursor % s.n];
          s.cursor++;
          if (!it) continue;
          if (have[keyOf(it)]) { s.owed.push(it); continue; }
          have[keyOf(it)] = 1;
          got.push(it);
        }
        return got;
      }
      // A SMOOTH WEIGHTED ROUND-ROBIN over the products' slices, weighted by
      // their sizes: each product is picked exactly as often as it has pieces,
      // spread evenly (75/10/15 deals K K M K K K P ...). The pattern of
      // products is fixed; the guards only choose WHICH of the product's
      // pieces fills its slot (2026-10-08 - the videos used to be re-spread
      // over the whole part afterwards, which moved the products' slots):
      // - THE VIDEOS EVENLY, FROM THE FIRST ROW (Dorian 2026-09-02), as
      //   spreadMotion deals them: motion opens the part and the k-th moving
      //   card is due at card k*total/moving. A slot where one is due takes
      //   its product's next moving piece, any other slot its next still;
      //   never a moving one right after a moving one (Dorian 2026-08-31).
      //   A product with none of the wanted sort gives the other.
      // - THE KIND GUARD: of the product's next three of that sort, one of a
      //   kind other than the card before, so the same kind does not land
      //   twice in a row when that product has another kind to give.
      // `drop`: pieces already placed at the head of the lap (the card's
      // loops) - their product's slots are spent on them, so they count
      // against its share, and they open the part's motion.
      function mix(slices, prev, drop) {
        var hs = live.filter(function (h) { return slices[h] && slices[h].length; });
        var T = 0, cur = {}, qs = {}, slots = [], out = [], n, i, h;
        // per product: slots used, moving pieces it holds, moving and still
        // pieces it has dealt; and the part's moving cards so far
        var used = {}, has = {}, gave = {}, stills = {}, motion = 0, shown = 0;
        for (i = 0; i < hs.length; i++) {
          h = hs[i];
          cur[h] = 0; used[h] = 0; has[h] = 0; gave[h] = 0; stills[h] = 0;
          qs[h] = slices[h].slice();
          T += qs[h].length;
          qs[h].forEach(function (it) { if (moving(it)) has[hs[i]]++; else stills[hs[i]]++; });
          motion += has[h];
        }
        // the slots first, so each one can see whose the next one is
        for (n = 0; n < T; n++) {
          var best = null;
          for (i = 0; i < hs.length; i++) {
            cur[hs[i]] += slices[hs[i]].length;
            if (best === null || cur[hs[i]] > cur[best]) best = hs[i];
          }
          cur[best] -= T;
          slots.push(best);
        }
        // a video a product owes its own spread by the end of its next slot
        function behind(c, extra) { return gave[c] < Math.floor((used[c] + extra) * has[c] / slices[c].length); }
        for (n = 0; n < T; n++) {
          h = slots[n];
          var q = qs[h], it;
          used[h]++;
          if (drop && drop.indexOf(q[0]) !== -1) {
            it = q.shift();                       // already at the head of the part
          } else {
            // due by the part's spread, and this product not ahead of spreading
            // its own videos over its own slots (else the big product spends
            // them all early and the end of the lap waits on the small ones');
            // or this product a whole video BEHIND its own spread, or it ends
            // its slice holding nothing but videos and no still to part them
            var due = !moving(prev) && ((shown < motion && n >= Math.floor(shown * T / motion) &&
              gave[h] < Math.ceil(used[h] * has[h] / slices[h].length)) || behind(h, 0));
            // the next slot is another product's that must give a video - it
            // has no still left, or owes one - and this one does not owe: still
            var nx = slots[n + 1];
            if (due && nx && nx !== h && has[nx] > gave[nx] && (stills[nx] === 0 || behind(nx, 1)) && !behind(h, 0)) due = false;
            it = q.splice(pickIn(q, due, prev), 1)[0];
            out.push(it);
            prev = it;
          }
          if (moving(it)) { gave[h]++; shown++; } else stills[h]--;
        }
        return out;
      }
      // which of a product's queued pieces: of its next three moving ones (or
      // still ones), the first of another kind than `prev`; none of that sort
      // left, the same choice among the other sort
      function pickIn(q, wantMoving, prev) {
        var cand = [], t, c, sort;
        for (sort = 0; sort < 2 && !cand.length; sort++) {
          for (t = 0; t < q.length && cand.length < 3; t++) {
            if (moving(q[t]) === (sort === 0 ? wantMoving : !wantMoving)) cand.push(t);
          }
        }
        for (c = 0; c < cand.length; c++) if (!prev || laneOf(q[cand[c]]) !== laneOf(prev)) return cand[c];
        return cand.length ? cand[0] : 0;
      }
      // NEVER TWO MOVING CARDS SIDE BY SIDE while a still is left - where a
      // product had no still to give after a moving card. The second moving
      // card trades seats with the nearest still after it, the same product's
      // when there is one, so the pattern of products stays as mixed; with no
      // still after it, a spare one from before it is moved in between. This
      // replaces separateMotion here (2026-10-08): it re-dealt the whole part
      // moving-versus-still AFTER the mix and moved every product's slots - on
      // the shipped 75/10/15 mix most phones opened 16/2/2 instead of 15/2/3.
      // `prev`: the card just before the list (the lap or part before it).
      function keepApart(list, prev) {
        for (var i = 0; i < list.length; i++) {
          if (!moving(list[i]) || !moving(i ? list[i - 1] : prev)) continue;
          var same = -1, any = -1;
          for (var t = i + 1; t < list.length; t++) {
            if (moving(list[t])) continue;
            if (any < 0) any = t;
            if (srcOf(list[t]) === srcOf(list[i])) { same = t; break; }
          }
          if (any < 0) {
            // NOTHING STILL AFTER IT: borrow one from BEHIND (review 2026-10-08).
            // By the end of a part the stills have often all been spent on
            // still-still runs, and giving up here stacked a returning
            // visitor's videos at the end of the part - 8 to 11 moving cards
            // back to back on a phone, on video-heavy mixes. The nearest
            // earlier still that is not itself parting two moving cards moves
            // in between this pair; one that is would only open a pair where
            // it stood.
            var back = -1;
            for (var u = i - 2; u >= 0; u--) {
              if (moving(list[u])) continue;
              if (moving(u ? list[u - 1] : prev) && moving(list[u + 1])) continue;
              back = u;
              break;
            }
            if (back < 0) break;        // every still already parts a pair
            var lent = list.splice(back, 1)[0];
            // the removal moved the pair to i-2, i-1: the still goes between
            list.splice(i - 1, 0, lent);
            continue;
          }
          var s = same >= 0 ? same : any, it = list[i];
          list[i] = list[s];
          list[s] = it;
        }
        return list;
      }
      // EVEN RUNS WHERE A PAIR CANNOT BE AVOIDED (review 2026-10-11). With
      // more moving cards than stills + 1, keepApart parts every pair it can
      // and the rest piled up at the end of the part: 8 moving cards back to
      // back on a phone where runs of 2 were possible (belt bundle + Pincer
      // 80/20, 70% seen). Then the stills keep their order, the moving cards
      // theirs, and the moving ones are dealt over the gaps between the
      // stills as evenly as the counts allow - the card before the part
      // counts in the first gap when it moves. A part already at its fewest
      // is left exactly as keepApart made it. The first `keep` cards (the
      // card's loops leading lap 0) stay where they are.
      function evenRuns(list, prev, keep) {
        keep = keep || 0;
        var head = list.slice(0, keep), rest = list.slice(keep);
        var before = keep ? head[head.length - 1] : prev;
        var mv = [], st = [];
        rest.forEach(function (it) { (moving(it) ? mv : st).push(it); });
        var p = moving(before) ? 1 : 0, total = mv.length + p, gaps = st.length + 1;
        var bound = Math.max(1, Math.ceil(total / gaps));
        var run = p, longest = p;
        for (var i = 0; i < rest.length; i++) {
          run = moving(rest[i]) ? run + 1 : 0;
          if (run > longest) longest = run;
        }
        if (longest <= bound) return list;
        var out = head, m = 0;
        for (var g = 0; g < gaps; g++) {
          // the first gap gets the smaller share, and the card before it
          var share = Math.floor((g + 1) * total / gaps) - Math.floor(g * total / gaps) - (g === 0 ? p : 0);
          for (var c = 0; c < share && m < mv.length; c++) out.push(mv[m++]);
          if (g < st.length) out.push(st[g]);
        }
        while (m < mv.length) out.push(mv[m++]);
        return out;
      }
      function buildLap(k, prev) {
        var fresh = {}, seen = {}, loopsHere = [];
        live.forEach(function (h) {
          fresh[h] = [];
          seen[h] = [];
          take(h, k).forEach(function (it) {
            if (k === 0 && loops.indexOf(it) !== -1) { loopsHere.push(it); return; }
            (it.ck && seenCks[it.ck] ? seen[h] : fresh[h]).push(it);
          });
        });
        // THE CARD'S UNSEEN LOOPS LEAD lap 0, as they lead every feed
        // (buildOrder), and count against the card product's share. Two loops
        // are never side by side: each one after the first follows one of the
        // card product's own stills, so they are still its first motions.
        var lead = [];
        loopsHere.forEach(function (it, i) {
          if (i > 0) {
            for (var t = 0; t < fresh[first].length; t++) {
              if (!moving(fresh[first][t])) { lead.push(fresh[first].splice(t, 1)[0]); break; }
            }
          }
          lead.push(it);
        });
        if (lead.length) fresh[first] = lead.concat(fresh[first]);
        var freshPart = evenRuns(keepApart(lead.concat(mix(fresh, lead.length ? lead[lead.length - 1] : prev, lead)), prev), prev, lead.length);
        var last = freshPart.length ? freshPart[freshPart.length - 1] : prev;
        // the seen slices follow the unseen ones, never among them
        var seenPart = evenRuns(keepApart(mix(seen, last, null), last), last, 0);
        return freshPart.concat(seenPart);
      }

      return {
        owns: function (it) { return srcOf(it) === first; },
        // Lap 0 into `order`, or false (and `order` untouched) when there is
        // nothing to mix: buildOrder then deals the feed as it always did.
        start: function (fresh, tail, cardLoops, tailCks) {
          seenCks = tailCks || {};
          loops = (cardLoops || []).slice();
          var byFresh = {}, byTail = {};
          handles.forEach(function (h) { byFresh[h] = []; byTail[h] = []; });
          (fresh || []).forEach(function (it) { byFresh[srcOf(it)].push(it); });
          (tail || []).forEach(function (it) { byTail[srcOf(it)].push(it); });
          src = {};
          live = [];
          handles.forEach(function (h) {
            var seed = (orderSeed ^ hashOf(h)) >>> 0, rnd = seededRandom(seed);
            var unseen = [], seenDeck = [];
            dealDeck(byFresh[h], rnd, unseen);
            if (h === first) unseen = loops.concat(unseen);
            dealDeck(byTail[h], rnd, seenDeck);
            separateMotion(unseen);
            separateMotion(seenDeck);
            var pass0 = unseen.concat(seenDeck);
            if (!pass0.length) return;      // emptied by the language filter
            src[h] = { seed: seed, pass: [pass0], n: pass0.length, cursor: 0, owed: [] };
            live.push(h);
          });
          if (live.length < 2) return false;
          loopReach = 0;
          if (src[first]) src[first].pass[0].forEach(function (it, i) { if (loops.indexOf(it) !== -1) loopReach = i + 1; });
          W = 0;
          live.forEach(function (h) { W += weight[h]; });
          bind = live[0];
          live.forEach(function (h) { if (src[h].n * weight[bind] < src[bind].n * weight[h]) bind = h; });
          L = Math.max(live.length, Math.floor(src[bind].n * W / weight[bind]));
          quotas = [];
          given = {};
          live.forEach(function (h) { given[h] = 0; });
          laps = [];
          far = {};
          var lap0 = buildLap(0, null);
          if (lap0.length !== L) return false;
          order.length = 0;
          lap0.forEach(function (it) { order.push(it); });
          laps[0] = order;
          return true;
        },
        // Lap k (k >= 1), built in sequence and kept for the page.
        lap: function (k) {
          if (!(k > 0)) return order;
          if (!laps.length) return redealLap(order, k, orderSeed);   // not dealt yet
          if (laps[k]) return laps[k];
          if (k > CAP) return far[k] || (far[k] = redealLap(order, k, orderSeed));
          while (laps.length <= k) {
            var j = laps.length, out = null, before = laps[j - 1];
            // its first card after the last card of the lap before it
            try { out = buildLap(j, before[before.length - 1]); } catch (e) { out = null; }
            if (!out || out.length !== order.length) out = redealLap(order, j, orderSeed);
            laps[j] = out;
          }
          return laps[k];
        },
        // every piece of every product - what the card's showings count against
        pieces: function () {
          var all = [];
          live.forEach(function (h) { all = all.concat(src[h].pass[0]); });
          return all;
        },
        debug: function () {
          var out = { lap: L, laps: laps.length, binding: bind, shares: {}, pieces: {}, perLap: {}, taken: {} };
          live.forEach(function (h) {
            out.shares[h] = Math.round(1000 * weight[h] / W) / 10;
            out.pieces[h] = src[h].n;
            out.perLap[h] = quota(h, 0);
            out.taken[h] = src[h].cursor;
          });
          return out;
        }
      };
    }

    // ── WHERE EACH BLOCK STARTS AND HOW LONG IT IS ────────────────────────
    // A block used to be exactly CARDS_PER_CYCLE cards, so its contents were
    // `index * 6 + i` and needed no memory. They are variable now, so the
    // boundaries are DECIDED ONCE and remembered: the recycler rebuilds a
    // block from its index long after it was first built, and a block that
    // came back holding different cards than it showed before is precisely
    // the bug that once slid a T&E photo into the Judge.me rail.
    var cycleSpan = [];

    // A long-form YouTube video takes the whole row, however many columns
    // that is.
    // This is the SAME judgement the stylesheet makes, which is why the
    // stylesheet now reads the flag this stamps instead of guessing from the
    // markup: one predicate, so the count below and the layout cannot drift.
    // A WIDE long-form video takes the whole row. A TALL one does not.
    //
    // This asked `indexOf('yt-video') === 0`, and laneOf appends the frame
    // shape as a suffix - so it matched `yt-video-v` as happily as
    // `yt-video-h` and a PORTRAIT video was stretched across both columns.
    // Measured on the live feed: two cards at 936x1380, taller than the
    // viewport they sat in, against the wide ones' correct 936x658 (Dorian
    // 2026-08-31: "its not the right format for being alone and big ... the
    // one that have large aspect ratio not the long portrait one").
    //
    // Spanning is for a 16:9 frame, which is the one shape that is WASTED in
    // half a column. A tall video is exactly the shape every other card
    // already is, so it takes one slot like the rest - and, being motion, it
    // moves, so the deal keeps it out of a row that already has something
    // moving - which is what "never two videos aside" means now.
    // THE SHAPE DECIDES, NOT THE KIND. This asked for lane `yt-video-h`, which
    // is "a long-form video that happens to be wide" - so a WIDE card that
    // YouTube calls a Short sat in one column beside a card of another shape.
    // The belt has one: "MAGNETIX Battle Belt Sizing Tutorial", format Short,
    // frame 16:9 (Dorian 2026-08-31: "those kind of videos format should be
    // only as big ones (alone) and not along with another one, especially that
    // has different format").
    //
    // A wide frame is the one shape WASTED in half a column, and it is wasted
    // whatever the platform calls the clip. So: any video that is not vertical
    // takes the whole row.
    //
    // The fallback is the SHELL'S OWN, deliberately - renderItem decides the
    // player's box the same way a few hundred lines down, and if these two
    // disagree a card claims a whole row and then draws itself 3:4 in it, or
    // the reverse. One judgement, spelled the same way twice, because the two
    // places cannot share a variable.
    function spansRow(item) {
      if (!item || !item.video) return false;
      // Every mirrored file video is rendered by shortCard() in the shared
      // 3:4 shell. Instagram Reels and T&E clips normally have no explicit
      // `vertical` flag, so treating "missing" as landscape stretched a
      // correct 333x498 reel into a 682x1114 billboard. Magnetix did not
      // expose it because its curated loops carry explicit geometry. Only a
      // real YouTube landscape player uses the deliberate wide-video row.
      if (item.video.provider !== 'youtube') return false;
      var v = item.video.vertical;
      if (v == null) v = (item.format === 'Short' || item.format === 'GIF');
      return !v;
    }

    // ── A CARD THAT MOVES, AND DOES NOT OWN ITS ROW ─────────────────────
    // "Never put 2 videos aside" was enforced by PINNING every one of these
    // to column one. That held the rule and smuggled in two others nobody
    // asked for: every video sat down the left edge in a stripe, and each one
    // needed a still beside it or its row was half empty. Dorian 2026-08-31:
    // "right now the videos are only on left side and its always a video
    // together with a non video. It doesnt have to be like that! Only rule is
    // never 2 videos on the same row."
    //
    // So the rule moved OUT of the layout and into the ORDER (spaceOutMotion
    // below). A video may take either column; what keeps two out of one row
    // is that the deal never puts two in one. This predicate is now only
    // about the content - does this card move - and says nothing about where
    // it sits.
    function isMotion(item) {
      return !!(item && item.video) && !spansRow(item);
    }

    // THE FEED IS A STREAM. Flat index -> item, drawn from the base order
    // lap after lap, SKIPPING whatever the phone's product card has shown
    // (heroShown). Each assignment is made once and kept: a block is
    // planned on these items and the recycler rebuilds a cycle from the
    // same index, so an item must not move under a cycle that exists.
    // A later mark only changes what has not been dealt yet - or what
    // rewindStream throws back, for cycles nobody has scrolled to.
    //
    // Why not substitute in place: measured on the live feed, a fixed
    // number of slots filled from fewer allowed items always repeats one
    // per removed item, and chaining the borrows forward only moves the
    // repeats to the tail. A stream has no slot to fill, so nothing to
    // duplicate.
    var streamItem = [], streamBase = [], streamCursor = 0;
    // pieces a refill took early (onFeedCardDead): the stream skips them when
    // it gets there, exactly as it skips what the card has shown
    var placedCks = {};
    function baseAt(baseFlat) {
      var lo = orderForLap(Math.floor(baseFlat / order.length));
      return lo[baseFlat % order.length];
    }
    function itemAtFlat(flat) {
      var skipped = 0;
      while (streamItem.length <= flat) {
        var b = streamCursor++;
        var it = baseAt(b);
        // If the card has shown EVERYTHING, skipping forever is a hang;
        // past one whole lap of skips - or the whole pool, when that is longer
        // - the feed shows the shown. And below the floor of unshown pieces it
        // stops skipping altogether, rather than deal the last few over and
        // over. The floor counts the whole pool (poolLen), as shownInOrder
        // does - not the lap, which a mixed feed with shares can make shorter
        // than the floor itself. So does the run of skips (review 2026-10-08):
        // with shares a lap holds a slice of the library, and at 98/1/1 the
        // card product's pieces come round in runs longer than the lap -
        // bounded by the lap alone, the stream gave up there and re-dealt what
        // the card had shown while 84 pieces were still unshown. The floor
        // still ends the skipping below 12 unshown pieces, so a card that has
        // shown everything cannot hang the stream.
        if (it && it.ck && (heroShown[it.ck] || placedCks[it.ck] || deadKeys[it.ck])
            && skipped < Math.max(order.length, poolLen)
            && (poolLen - shownInOrder) >= STREAM_FLOOR) { skipped++; continue; }
        skipped = 0;
        streamItem.push(it);
        streamBase.push(b);
      }
      return streamItem[flat];
    }
    // Where an assigned stream flat came from in the base order - the lap
    // and index the card is stamped with, so rails and the lightbox resolve
    // the same item the stream dealt.
    function baseOfFlat(flat) { itemAtFlat(flat); return streamBase[flat]; }
    // Throw back every assignment from `fromFlat` on, so the next deal skips
    // what the card has shown since. Only ever called for cycles nobody has
    // scrolled to - see heroShowedItem.
    function rewindStream(fromFlat) {
      if (fromFlat >= streamItem.length) return false;
      streamCursor = streamBase[fromFlat];
      streamItem.length = fromFlat;
      streamBase.length = fromFlat;
      return true;
    }

    // ---- HOW WIDE IS A ROW ------------------------------------------------
    // Read from the stylesheet, never assumed. It is two everywhere today,
    // and it has already been three once this week - a block that ends on a
    // whole row of two ends mid-row on three, so the layout has to be the
    // one that says. --agmf-cols is
    // declared by pdp-desk-3col.css, which is why that file declares it
    // OUTSIDE its `agdesk-on` gate - the class arrives after the feed has
    // built its hero, and by then the first block may already be decided.
    //
    // Deliberately NOT cached. Caching would freeze whatever was readable at
    // the first block, and on a page whose stylesheet had not landed yet that
    // is 1 forever. Read per block instead: once every six-or-so cards, and
    // a block already decided keeps its shape because the table below is a
    // memory. Resizing across the breakpoint therefore leaves the blocks
    // built so far sized for the old width - they can orphan a card again
    // until they are rebuilt, which is cosmetic and only on a live resize.
    function columns() {
      var v = 0;
      try {
        v = parseInt(getComputedStyle(document.documentElement)
              .getPropertyValue('--agmf-cols'), 10);
      } catch (e) {}
      return v >= 1 ? v : 1;   // unset: mobile, and every page without the layout
    }

    // HOW MANY COLUMNS TO PLAN FOR, which is not how many to draw.
    // A block is sized ONCE and remembered for ever - its length decides
    // where the next block starts, and the recycler rebuilds from that - so
    // the sizing has to be independent of the width that happened to be on
    // screen when the block was first appended. Size below 1200px and
    // columns() reads 1, where every block is whole at six cards; widen the
    // window and those blocks render in two columns still carrying holes
    // they were never asked to avoid, and they come back identically on
    // every rebuild. Declared OUTSIDE the media query for that reason.
    function maxColumns() {
      var v = 0;
      try {
        v = parseInt(getComputedStyle(document.documentElement)
              .getPropertyValue('--agmf-cols-max'), 10);
      } catch (e) {}
      return v >= 1 ? v : columns();
    }

    // ── WHY A BLOCK CAN BE SEVEN CARDS ────────────────────────────────────
    // Two columns, six cards, one of them spanning: five singles, which is
    // two pairs and ONE LEFT ALONE with a hole beside it (Dorian 2026-08-30,
    // and measured - three of the first four blocks did it).
    //
    // Nothing fills that hole because a block is its own grid, and it has to
    // be: the recycler pins a block's height while it releases the contents,
    // so the scroll does not jump. A grid only lays out its own children, so
    // the next block cannot reach back into this one.
    //
    // The scroll is endless but it is BUILT in blocks, and the block is an
    // implementation unit, not a design one. So it flexes: take one more
    // card, and another if that one also spans, until the singles divide by
    // the number of columns and the block ends on a complete row. Same
    // content, same order - only the seam between two blocks moves, and a
    // seam is exactly the thing nobody can see.
    //
    // On one column none of this matters and none of it runs: everything
    // divides by one, so the loop never turns and a block is six cards.
    function spanFor(index) {
      if (cycleSpan[index]) return cycleSpan[index];
      var prev = index > 0 ? spanFor(index - 1) : null;
      var start = prev ? prev.start + prev.len : 0;
      // PLAN for the widest this page can get, not for what it is now.
      var cols = maxColumns();
      var len = CARDS_PER_CYCLE;
      var i;
      // Ask the SAME model the order pass uses. Parity of singles is not
      // enough: a wide video arriving mid-row would strand the cell beside
      // it, and counting singles cannot see that.
      function flatsFor(n) { var a = [], q; for (q = 0; q < n; q++) a.push(start + q); return a; }

      // A block is SETTLED when it fills its rows AND, once the order pass has
      // done what it can, no row is left holding two moving cards. Both, not
      // just the first: a block can be perfectly full and still show two
      // videos side by side because it had no still left to trade with, and
      // one more card is usually all it needs to find one.
      function blockIsSettled(f) {
        if (!blockIsFull(f, cols)) return false;
        var rows = layBlock(spaceOutMotion(f, cols), cols), n = cols, c, x, moving;
        for (c = 0; c < rows.length; c++) {
          if (rows[c][0] !== null && spansRow(itemAtFlat(rows[c][0]))) continue;
          moving = 0;
          for (x = 0; x < n; x++) {
            if (rows[c][x] !== null && isMotion(itemAtFlat(rows[c][x]))) moving++;
          }
          if (moving >= 2) return false;
        }
        return true;
      }

      // Bounded at two chances per column the row is short: past that we
      // would be reshaping the block rather than closing a row.
      //
      // THIS BUDGET IS NOT SUFFICIENT IN GENERAL, and I had written that it
      // was. Three full-row videos in a row exhaust it and the orphan comes
      // back - over every 12-card span pattern there is, that happens to a
      // tenth of blocks at two columns and an eighth at three. It is
      // sufficient HERE because buildOrder rotates strictly through kinds
      // and so never deals long videos consecutively: against the real
      // order, 37 of 37 blocks close at two columns and 32 of 32 at three,
      // and raising the budget changes not one of them. If that rotation
      // ever stops rotating, this line is where the orphan returns.
      //
      // At two columns the arithmetic is the one that shipped, verified
      // identical on all 4096 patterns - the block table does not move.
      // Budget raised from 2*(cols-1) to 2*cols: closing a row is no longer
      // just parity, a stranded pin needs a free card fetched in behind it.
      //
      // STILL NOT SUFFICIENT IN GENERAL, and the same caveat as before rather
      // than a new claim. Over every 8-card role pattern there is, adversarial
      // mixes (a third pinned, a third spanning - "fppppfff" and worse) run the
      // budget out and keep their hole. They are not this feed: buildOrder
      // rotates strictly through kinds, so pinned cards never arrive four in a
      // row. Against the REAL lane sequence the count goes 6 half-empty rows to
      // ZERO over 30 blocks, and the blocks grow only from 6/7 to 6/7/8/9.
      //
      // On ONE column everything divides by one, so the loop never turns and
      // a block is six cards - the phone is untouched by any of this.
      // TEST WHAT YOU SHIP. This used to increment `len` and leave the loop
      // on the COUNTER, so the stored length was the one it had just
      // incremented to and never evaluated - and that length could fail even
      // `blockIsFull`, the hard constraint, after full lengths had been
      // evaluated and thrown away chasing the soft one. Measured on the
      // shipped feed, block 18: lengths 7 and 9 were FULL, and it shipped 10,
      // which is not.
      //
      // So every candidate is judged and the best one is kept: settled if any
      // is settled, else merely full, else the length we started with.
      var extra = 2 * cols;
      var chosen = -1, firstFull = -1;
      for (i = 0; i <= extra; i++) {
        var cand2 = flatsFor(len + i);
        if (blockIsSettled(cand2)) { chosen = len + i; break; }
        if (firstFull < 0 && blockIsFull(cand2)) firstFull = len + i;
      }
      len = chosen >= 0 ? chosen : (firstFull >= 0 ? firstFull : len);
      cycleSpan[index] = { start: start, len: len };
      return cycleSpan[index];
    }

    // ── WHERE THE CARDS OF A BLOCK ACTUALLY LAND ─────────────────────
    // The grid’s own rules, in eleven lines: a wide video takes a whole row of
    // its own, and everything else drops into the earliest empty cell, which
    // is what `grid-auto-flow: dense` does.
    //
    // ONE model, used by both the block sizer (is this block full?) and the
    // order pass (would these two videos share a row?). They were briefly two
    // and it cost the afternoon: the order pass assumed plain left-to-right
    // flow while the stylesheet packed densely, and against the real order it
    // left 34 half-rows where the old pin had left 22.
    //
    // Rows of FLAT INDICES, not items, so a caller can move a card and lay it
    // out again.
    function layBlock(flats, forCols) {
      var cols = forCols || columns(), rows = [], i, c, x, item, placed, row;
      function blank() { var r = [], q; for (q = 0; q < cols; q++) r.push(null); rows.push(r); return r; }
      for (i = 0; i < flats.length; i++) {
        item = itemAtFlat(flats[i]);
        if (spansRow(item)) { row = blank(); for (c = 0; c < cols; c++) row[c] = flats[i]; continue; }
        placed = false;
        for (c = 0; c < rows.length && !placed; c++) {
          for (x = 0; x < cols; x++) if (rows[c][x] === null) { rows[c][x] = flats[i]; placed = true; break; }
        }
        if (!placed) blank()[0] = flats[i];
      }
      return rows;
    }

    function blockIsFull(flats, forCols) {
      var cols = forCols || columns(), rows = layBlock(flats, cols), c, x;
      for (c = 0; c < rows.length; c++) {
        for (x = 0; x < cols; x++) if (rows[c][x] === null) return false;
      }
      return true;
    }

    // ── NO ROW HOLDS TWO MOVING CARDS ─────────────────────────────
    // The one rule, and now the only one. It used to be a PIN - every motion
    // card locked to column 1 - which held it and quietly added two more
    // nobody asked for: every video down the left edge in a stripe, and each
    // needing a still beside it or its row went half empty (Dorian 2026-08-31:
    // "the videos are only on left side and its always a video together with a
    // non video. It doesnt have to be like that! Only rule is never 2 videos on
    // the same row").
    //
    // So a video may take either column, and the ORDER is what keeps two out
    // of one row: lay the block out, find a row holding two, and swap the
    // second one with a later still. Then lay it out again, because moving a
    // card moves everything after it. Bounded, and it returns the moment the
    // block is clean.
    //
    // A permutation, never a swap across blocks: each card keeps its own
    // idx/lap/ck, so the rails, the seen-ledger and the colour deck read
    // exactly what they read before - only the seat changes. Where a block
    // runs out of stills the videos do end up together; that is a block that
    // is nearly all video, and there is no seat left to move them to.
    // ── WHICH SIDE THE VIDEO TAKES ────────────────────────────────────────
    // Taking the pin off was not enough, and it was never going to be. The
    // feed deals motion and stills alternately, so every pair came out
    // (video, still) and the video landed on the LEFT every single time -
    // the same stripe the pin drew, now drawn by the running order instead
    // (Dorian 2026-08-31: "still all the videos are only on the left ... i
    // told you to randomize more").
    //
    // So the side is chosen, per row, and the row's two cards are swapped
    // when the choice differs from where they fell.
    //
    // DETERMINISTIC, never Math.random(). The recycler releases a block and
    // rebuilds it from its index later, and a block that comes back arranged
    // differently is the same class of bug as one that comes back holding
    // different cards. Hashing the row's own first card gives a stable answer:
    // the same row always lands the same way, and neighbouring rows do not.
    function rowHash(n) {
      var x = Math.imul(n + 1, 2654435761) >>> 0;
      x ^= x >>> 15;
      x = Math.imul(x, 2246822519) >>> 0;
      x ^= x >>> 13;
      return x >>> 0;
    }

    // Safe because it only ever swaps two cards that ALREADY share a row:
    // dense placement is decided by order, so exchanging two cards inside one
    // row leaves every row holding exactly what it held. "Never two videos on
    // a row" is untouched - the row's contents do not change, only their seats.
    function varySides(flats, forCols) {
      var cols = forCols || columns();
      if (cols < 2) return flats;
      var out = flats.slice(), rows = layBlock(out, cols), r, c, f, item;
      for (r = 0; r < rows.length; r++) {
        var motionAt = -1, ok = true;
        for (c = 0; c < cols; c++) {
          f = rows[r][c];
          if (f === null) { ok = false; break; }          // a short row keeps its seats
          item = itemAtFlat(f);
          if (spansRow(item)) { ok = false; break; }      // a wide video owns the row
          if (!isMotion(item)) continue;
          if (motionAt >= 0) { ok = false; break; }       // spaceOutMotion left one behind
          motionAt = c;
        }
        if (!ok) continue;
        if (motionAt < 0) {
          // No video in this row: two stills. Left them alone, and a fixed
          // kind ring put review on one side and T&E on the other for the
          // whole scroll. Swap by the same hash the video rows use.
          var wantStill = rowHash(rows[r][0]) % cols;
          if (wantStill === 0) continue;
          var a = out.indexOf(rows[r][0]);
          var b = out.indexOf(rows[r][wantStill]);
          if (a < 0 || b < 0) continue;
          var t0 = out[a]; out[a] = out[b]; out[b] = t0;
          continue;
        }
        var want = rowHash(rows[r][0]) % cols;
        if (want === motionAt) continue;
        var i = out.indexOf(rows[r][motionAt]);
        var j = out.indexOf(rows[r][want]);
        if (i < 0 || j < 0) continue;
        var tmp = out[i]; out[i] = out[j]; out[j] = tmp;
      }
      return out;
    }

    function spaceOutMotion(flats, forCols) {
      var cols = forCols || columns();
      if (cols < 2) return flats;
      var out = flats.slice(), guard = 0, i, j, k, order;

      // how many rows hold two moving cards - the thing being minimised
      function clashes(list) {
        var rows = layBlock(list, cols), n = 0, a, c, moving;
        for (a = 0; a < rows.length; a++) {
          if (rows[a][0] !== null && spansRow(itemAtFlat(rows[a][0]))) continue;
          moving = 0;
          for (c = 0; c < cols; c++) {
            if (rows[a][c] !== null && isMotion(itemAtFlat(rows[a][c]))) moving++;
          }
          if (moving >= 2) n++;
        }
        return n;
      }

      // The trade used to look FORWARD only, so a clash with no still after
      // it shipped untouched even when the same cards had a clean
      // arrangement - measured on the shipped feed, block 18 laid out two
      // rows of two videos while `S M f M f S M f M f` was available. It
      // looks backwards too now, forward first because a later still is the
      // less disruptive partner.
      //
      // A swap is accepted ONLY if it strictly reduces the count. That is
      // what terminates this: a plain backward search cycles (9 trades with
      // 7, then 7 back with 9) until the guard fires and ships the clash
      // anyway. A non-negative integer that must fall on every accepted
      // move cannot cycle, which leaves `guard` as a backstop rather than
      // the thing doing the work.
      var best = clashes(out);
      while (guard++ < 64 && best > 0) {
        var rows2 = layBlock(out, cols), second = -1, moved = false;
        for (i = 0; i < rows2.length && second < 0; i++) {
          if (rows2[i][0] !== null && spansRow(itemAtFlat(rows2[i][0]))) continue;
          var seen = 0;
          for (var c2 = 0; c2 < cols; c2++) {
            if (rows2[i][c2] === null) continue;
            if (!isMotion(itemAtFlat(rows2[i][c2]))) continue;
            seen++;
            if (seen === 2) { second = rows2[i][c2]; break; }
          }
        }
        if (second < 0) break;
        k = out.indexOf(second);
        order = [];
        for (j = k + 1; j < out.length; j++) order.push(j);
        for (j = k - 1; j >= 0; j--) order.push(j);
        for (var q = 0; q < order.length; q++) {
          j = order[q];
          var cand = itemAtFlat(out[j]);
          if (spansRow(cand) || isMotion(cand)) continue;
          var tmp = out[k]; out[k] = out[j]; out[j] = tmp;
          var now = clashes(out);
          if (now < best) { best = now; moved = true; break; }
          out[j] = out[k]; out[k] = tmp;              // no better: put it back
        }
        if (!moved) break;                            // nothing left that helps
      }
      return out;
    }

    // THE PHONE'S CARD SHOWED THIS. Remember it, and re-deal every feed
    // cycle that is still below the fold without it. Cycles already on or
    // above the screen keep what they show - a card must never change
    // under the shopper's thumb.
    // WHERE THE SHOPPER CAN SEE TO. On a desktop the feed scrolls inside the
    // media pane, whose bottom sits above the window's (964 against 1000 at
    // 1600x1000): a block in that band is hidden, and measuring against the
    // window counted it as on screen, so a card loop reported there stayed in
    // it and played twice, one row apart (K-Zero SF, 2026-10-08).
    function foldBottom() {
      var b = window.innerHeight;
      var pane = agmfScroller(section);
      if (pane) b = Math.min(b, pane.getBoundingClientRect().bottom);
      return b;
    }
    function heroShowedItem(ck) {
      if (!ck || heroShown[ck]) return;
      heroShown[ck] = 1;
      if (orderCks[ck]) shownInOrder++;
      saveHeroShown();
      // Only blocks NOBODY HAS REACHED are re-dealt. Below the fold is not
      // enough: a phone that read five blocks, went back up and swiped the
      // card to a loop had all five re-dealt - every card it had read moved
      // a slot (2026-10-08). recycle() marks a block reached the first time
      // its top comes above the fold; blocks are reached in order.
      var limit = foldBottom();
      var first = -1;
      for (var i = 0; i < cycles.length; i++) {
        if (cycles[i].reached) continue;
        if (cycles[i].el.getBoundingClientRect().top > limit) { first = i; break; }
      }
      if (first < 0 || !cycleSpan[first]) return;
      var start = cycleSpan[first].start;
      var holds = false;
      for (var f = start; f < streamItem.length; f++) {
        if (streamItem[f] && streamItem[f].ck === ck) { holds = true; break; }
      }
      if (!holds) return;
      if (!rewindStream(start)) return;
      for (var k = first; k < cycles.length; k++) {
        cycleSpan[k] = undefined;
      }
      // the whole feed thrown back: block 0 is planned again, a card shorter
      // where the shown piece stood, so the desktop's loops are seated again
      if (first === 0 && seatedLoops.length) seatLoopsInBlockOne(seatedLoops);
      for (k = first; k < cycles.length; k++) {
        var c = cycles[k];
        if (!c.materialized) continue;      // an empty one rebuilds when it comes near
        releaseVideos(c.el);
        releasePlaced(c.el);
        c.el.replaceChildren();
        c.el.appendChild(buildCycleContent(c.index));
        balanceCycle(c.el);
      }
    }

    // A STILL THAT FILLED A DEAD CARD'S HOLE was taken from the stream ahead
    // of its turn (placedCks keeps the stream from dealing it twice). When
    // its block is torn down - re-dealt or recycled - the still goes with it,
    // so its place in the stream is given back; otherwise it was never shown
    // again for the rest of the page (2026-10-08).
    function releasePlaced(el) {
      [].forEach.call(el.querySelectorAll('.agmf-card[data-agmf-placed]'), function (p) {
        if (p.dataset.agmfCk) delete placedCks[p.dataset.agmfCk];
      });
    }

    function buildCycleContent(index) {
      var frag = document.createDocumentFragment();
      var span = spanFor(index);
      var flats = [];
      for (var f = 0; f < span.len; f++) flats.push(span.start + f);
      flats = varySides(spaceOutMotion(flats));
      for (var i = 0; i < span.len; i++) {
        var flat = flats[i];
        // The STREAM's item for this flat (the card's showings skipped),
        // stamped with where it sits in the BASE order, so every lookup by
        // idx/lap - rails, the lightbox, auto-advance - resolves to it.
        var item = itemAtFlat(flat);
        var base = baseOfFlat(flat);
        var oi = base % order.length;
        var lapOrder = orderForLap(Math.floor(base / order.length));
        var card = renderItem(item);
        card.dataset.agmfIdx = String(oi);
        // The LAP matters as much as the index: every lap re-interleaves the
        // library, so lapOrder[oi] is a DIFFERENT item from order[oi] on any
        // lap past the first. Without this the carousel looked the card up in
        // the base order and built its rail from the wrong item's kind - a
        // T&E photo that slid into Judge.me reviews (Dorian 2026-08-26).
        card.dataset.agmfLap = String(Math.floor(base / order.length));
        // WHAT PIECE OF CONTENT this card is, as opposed to which record
        // carries it (the exporter's `ck`). Stamped here so anything reading
        // the feed - impressions, a seen-ledger, a later "show it again?"
        // rule - identifies content without reproducing the flow's internal
        // lap/order maths, which is exactly the drift that once slid a T&E
        // photo into the Judge.me rail.
        // FAIL CLOSED: only the exporter's `ck`, never the record id as a
        // stand-in. A record id would key the SAME film two ways (fourteen
        // YouTube items ship as the bare video id, one as youtube-<id>), and a
        // ledger written in two key spaces is worse than one that starts late
        // - an older feed simply records nothing until its export carries ck.
        var _ck = item && item.ck;
        if (_ck) card.dataset.agmfCk = String(_ck);
        var _ct = item && item.contentType;
        if (_ct) card.dataset.agmfType = String(_ct);
        // WHICH RECORD carries it, for the preview-only "open in AM" link
        // (snippets/am-dev-link.liquid). This is the library's record id and
        // nothing else reads it: the seen-ledger keys on `ck` above and must
        // keep to it - the two key spaces are different on purpose.
        var _rid = item && item.id;
        if (_rid) card.dataset.agmfId = String(_rid);
        // the layout reads this rather than inspecting the markup, so what
        // spans and what the block counted as spanning are one decision
        if (spansRow(item)) card.dataset.agmfSpan = '1';
        frag.appendChild(card);
      }
      return frag;
    }

    // MEDIA NEVER CHANGES FORMAT TO FILL AN ODD ROW (Dorian 2026-10-04).
    // A previous empty-slot guard promoted the last photo to two columns and
    // 62vh. K19 exposed the result as a giant picture unlike every permitted
    // 3:4 card. Keep this cleanup hook because recycled markup can survive a
    // hot theme reload, but never stamp a media card for promotion again.
    function balanceCycle(el) {
      if (!el.isConnected) {            // built before it joins the feed: ask again then
        setTimeout(function () { if (el.isConnected) balanceCycle(el); }, 0);
        return;
      }
      var stamped = el.querySelectorAll('[data-agmf-fill]');
      for (var i = 0; i < stamped.length; i++) stamped[i].removeAttribute('data-agmf-fill');
    }
    var balancedFeedW = -1;
    function balanceCycles() {
      var els = document.querySelectorAll('.agmf-cycle');
      for (var i = 0; i < els.length; i++) balanceCycle(els[i]);
      balancedFeedW = feedEl ? feedEl.clientWidth : -1;
    }
    // THE TRIGGER THAT CANNOT BE MISSED. The desktop layout moves the gallery
    // and the feed into its pane some time after they exist, and nothing
    // announces that move: the class arrives first, the grid comes later
    // (measured 2026-09-02: html.agdesk-on on, gallery still a rail when the
    // balance ran - no wide slide until a colour deal happened to re-run
    // it). So every pass compares the containers' widths with the widths they
    // were last balanced at; a change means a new layout and a fresh look at
    // the last row. Two clientWidth reads a frame.
    function balanceTick() {
      var ht = document.querySelector('.agmf-hero-track');
      if (ht && ht.__agmfBalance && ht.clientWidth !== ht.__agmfBalW) ht.__agmfBalance();
      if (feedEl && feedEl.clientWidth !== balancedFeedW) balanceCycles();
    }
    (function () {
      var was = document.documentElement.classList.contains('agdesk-on');
      new MutationObserver(function () {
        var is = document.documentElement.classList.contains('agdesk-on');
        if (is === was) return;
        was = is;
        balanceCycles();
        requestAnimationFrame(balanceCycles);
      }).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      window.addEventListener('resize', function () { setTimeout(balanceCycles, 220); });
    }());

    function appendCycle() {
      if (cycles.length >= maxCycles) {
        sentinel.classList.add('agmf-sentinel--end');
        sentinel.textContent = "That's everything for now.";
        return false;
      }
      var el = document.createElement('div');
      el.className = 'agmf-cycle';
      el.appendChild(buildCycleContent(cycles.length));
      balanceCycle(el);
      feedEl.appendChild(el);
      cycles.push({ el: el, index: cycles.length, materialized: true, height: 0 });
      return true;
    }

    // ---- runway on the document scroll ----
    function insideFeed() {
      var r = section.getBoundingClientRect();
      return r.top < window.innerHeight && r.bottom > 0;
    }

    function ensureRunway() {
      if (!insideFeed()) return;
      var target = Math.max(2200, window.innerHeight * 3);
      var guard = 0;
      while (document.documentElement.scrollHeight - (window.scrollY + window.innerHeight) < target
             && guard++ < 6) {
        if (!appendCycle()) break;
      }
    }

    // ---- video lifecycle: src near viewport, play only while visible ----
    function releaseVideos(rootEl) {
      rootEl.querySelectorAll('video').forEach(function (v) {
        try { v.pause(); v.removeAttribute('src'); v.load(); } catch (e) { /* ignore */ }
      });
    }

    // `cued`: the player boots ready but not playing. A pre-mount on a
    // constrained link (see the attach loop) takes this so it does not
    // stream unseen during the very boot window the visible player needs;
    // manageVideos() sends it playVideo when the shopper arrives, and a
    // muted play needs no gesture.
    function attachYt(shell, cued) {
      var id = shell.getAttribute('data-agmf-yt');
      if (!id || shell.querySelector('iframe')) return;
      var frame = document.createElement('iframe');
      frame.className = 'agmf-ytframe';
      frame.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
      // YouTube refuses embedded playback when no referrer arrives (error 153);
      // say it explicitly so a stricter page-level policy can never strip it
      frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      frame.allowFullscreen = true;
      delete shell.dataset.agmfYtPlaying;   // the poster covers it until it plays
      delete shell.dataset.agmfAwaitTap;    // it has been asked - it is a player now
      delete shell.dataset.agmfYtState;     // and no stale paused veil from last time
      frame.setAttribute('title', 'Agilite video');
      // ── THE PLAYER DRAWS NO CHROME; WE DO (Dorian 2026-09-06) ─────────
      // "clicking a video should put it full screen (or open the dialog on
      //  desktop), clicking the controls should work but ONLY when clicking
      //  on them, and the youtube button disabled in any case."
      //
      // Those three cannot be had from YouTube's own bar. A cross-origin
      // player is all or nothing: every click inside it belongs to YouTube,
      // so with `controls=1` a click on the PICTURE pauses instead of opening
      // (what he hit), and the "Watch on YouTube" button cannot be removed -
      // only covered, which is a guess about somebody else's layout.
      //
      // With `controls=0` the iframe paints a bare picture: no title, no
      // share, no wordmark, nothing to leak. Our shield takes every click, so
      // the picture opens large; the row below it is OURS - play/pause, a
      // seek bar and sound - so a control works only where a control is, and
      // every press is a DOM event we can name and record.
      //
      // (The previous note is kept below because it is the measurement that
      // sent us here.)
      // ── YOUTUBE'S OWN CONTROLS, AT EVERY WIDTH (Dorian 2026-09-03:
      //    "clicking youtube controls dont work, I would like to allow it
      //    except for the button that makes us arrive to an external youtube
      //    link", and then "when clicking a youtube video controls pause etc
      //    it opens the video full screen instead of activating the control").
      //
      // A cross-origin player is all-or-nothing: the parent cannot reach
      // inside it to hide one button, and a click that lands in it is never
      // seen out here. So the player draws its own bar and the two places
      // that lead to youtube.com are covered from OUR side instead - see
      // .agmf-ytshield--top / --brand below.
      //
      // This was desktop-only for one round, and that was the bug he hit
      // next: `agdesk-on` began at 1200 at the time, so below that there was
      // none of it, the player had no controls at all and our shield turned
      // every tap into
      // "expand the clip". Now the bar is real everywhere, and the native
      // fullscreen button (fs=1) is what makes a clip large - a labelled
      // button instead of a tap that could not be aimed.
      // A created loop OPENS at its own start: from 0 the player showed the
      // video's opening before the range handler sought to the cut.
      var loopStartAt = parseFloat(shell.dataset.agmfLoopStart);
      frame.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) +
        '?autoplay=' + (cued ? '0' : '1') + '&mute=1&playsinline=1&loop=1&playlist=' + encodeURIComponent(id) +
        (loopStartAt > 0 ? '&start=' + Math.floor(loopStartAt) : '') +
        // A CHROME-LESS PLAYER, DRESSED AS ONE (Dorian 2026-09-09: "lets
        // replace all controls to look exactly like native, including the link
        // to the video page"). `controls=0` takes YouTube's bar away again and
        // ours is drawn to its measurements instead - the red line, the same
        // play/pause and fullscreen glyphs, the running time, and the wordmark
        // that opens the video's own page. `fs=0` and `disablekb=1` go with
        // it: the player must own no gesture we are not drawing.
        //
        // What that buys back, which the real bar could not: the title strip
        // and the corner wordmark are gone with the chrome, so the two patches
        // that covered them are gone too; a press on the picture opens the
        // piece large again; a Short needs no crop to keep its controls on
        // screen, because ours are on the shell, not inside the player; and
        // the one way out is a link of OURS - so a departure is a click we
        // actually see instead of one inferred from focus.
        // CHROME-LESS AGAIN, ON PURPOSE (Dorian 2026-09-10, having seen the
        // real bar: "we reintroduced all kind of useless buttons like thumbs
        // up etc we dont even see them and the full screen button we dont see
        // it on the 3:4 cropping"). The crop is why YouTube's bar can never
        // work here: a 16:9 player cropped to 3:4 puts every control in that
        // bar outside the visible area, always. Only the CENTRE of the frame
        // is reliably on screen.
        //
        // So the split is by POSITION, not by taste. controls=0 takes the bar
        // away - and the thumbs up, share, watch-later and title strip with
        // it - and leaves the one native gesture that lives in the middle: a
        // press to play or pause. Everything that needs an edge is drawn by
        // us, on the shell, outside the crop.
        '&rel=0&iv_load_policy=3&fs=0&controls=0&disablekb=1&modestbranding=1' +
        '&enablejsapi=1&origin=' + encodeURIComponent(location.origin);
      shell.dataset.agmfYtControls = 'ours';
      // Sound is the SHOPPER's state, not the clip's: swiping to the next
      // video keeps it on if it was on, off if it was off (Dorian
      // 2026-08-16). A fresh frame inherits the current intent.
      shell.dataset.agmfSound = soundOn ? 'on' : 'off';
      shell.appendChild(frame);
      // when the boot began (measured against the first jsapi message), and
      // the state the CSS draws a ring for until then - see ytListen below
      shell.dataset.agmfBootAt = String(Date.now());
      // THE RING ONLY OVER A PAINTED POSTER (2026-09-08, a phone: "I get
      // sometimes this loading visualizer while the video is indeed actually
      // playing"). The player autoplays before it answers the handshake, and
      // on a slow link the lazy poster arrives AFTER that - so the playing
      // picture showed through the unpainted poster with the ring on top. A
      // shell with no poster, or one not painted yet, draws no ring: what
      // the shopper sees there is the player, and it shows its own progress.
      // The poster of a player being attached is fetched eagerly, so it can
      // cover the boot at all.
      var poster = shell.querySelector('.agmf-ytposter');
      var painted = !!(poster && poster.complete && poster.naturalWidth > 0);
      if (poster) {
        try { poster.loading = 'eager'; } catch (e) {}
        if (!painted) {
          poster.addEventListener('load', function () {
            if (!shell.dataset.agmfReady && shell.querySelector('iframe')) shell.dataset.agmfYtBooting = '1';
          }, { once: true });
        }
      }
      if (painted) shell.dataset.agmfYtBooting = '1'; else delete shell.dataset.agmfYtBooting;
      // a boot that overlaps a hidden tab is throttled by the browser and
      // says nothing about the link - it is never measured
      shell.dataset.agmfBootHidden = document.hidden ? '1' : '';
      if (cued) shell.dataset.agmfCued = '1'; else delete shell.dataset.agmfCued;
      frame.addEventListener('load', function () { ytListen(shell, frame); });
      syncYtSoundUi();
    }

    // ── THE HANDSHAKE RETRIES ON ITS OWN CLOCK (Dorian 2026-09-07, from a
    //    phone on 4G: "the youtube videos were not loading, or taking very
    //    long") ────────────────────────────────────────────────────────────
    // A YouTube embed says nothing until the page posts {event:'listening'}
    // to it, and it can only hear that once its player has booted - one to
    // three seconds on a wired line, five to fifteen on a phone. The only
    // thing that posted it was manageVideos(), which runs on scroll. So the
    // player was attached on the last scroll frame, asked to listen a few
    // times while it was still an empty document, and then the shopper
    // stopped to watch - and nothing asked again. Measured on the preview
    // build at 375px: attached in 0.02s, still silent 8s later, poster at
    // opacity 1; one hand-sent 'listening' and within 25ms it reported
    // playing and the poster lifted. It had been playing muted behind the
    // poster the whole time. The controls, the sound, the one-player rule
    // and the analytics all wait on the same first message.
    //
    // So the asking is a clock now, not a scroll: every half second for
    // every mounted player that has not answered, every two seconds after
    // the first forty, never for one that has. The iframe's load event asks
    // too, which is early but free.
    function ytListen(shell, frame) {
      try {
        frame.contentWindow.postMessage(
          JSON.stringify({ event: 'listening', id: shell.getAttribute('data-agmf-yt') }), '*');
      } catch (e) { /* mid-load - the clock asks again */ }
    }
    // the player and everything the page knew about it, gone - the poster
    // shows again and the next pass may attach afresh (releaseYt uses this)
    function dropYt(shell) {
      var f = shell.querySelector('iframe');
      if (f) f.remove();
      ['agmfReady', 'agmfPlayCmd', 'agmfMuteCmd', 'agmfPlayAt', 'agmfAdvanced',
       'agmfYtPlaying', 'agmfYtState', 'agmfYtBooting', 'agmfBootAt', 'agmfListenAt',
       'agmfBootHidden', 'agmfCued'].forEach(function (k) { delete shell.dataset[k]; });
    }
    var clockRaf = 0;
    setInterval(function () {
      if (document.hidden) return;
      var now = Date.now();
      var vh = window.innerHeight || 1, vw = window.innerWidth || 1;
      var askManager = false;
      document.querySelectorAll('[data-agmf-yt]').forEach(function (shell) {
        var frame = shell.querySelector('iframe');
        if (!frame) {
          // A VISIBLE SHELL WITH NO PLAYER. The manager attaches on scroll;
          // whatever left this one bare at rest, the clock asks the manager
          // rather than guessing why (audit 2026-09-08: nothing else could).
          if (shell.dataset.agmfDead) return;
          var r = shell.getBoundingClientRect();
          if (r.width > 0 && r.top < vh && r.bottom > 0 && r.left < vw && r.right > 0) askManager = true;
          return;
        }
        if (shell.dataset.agmfReady) return;
        var since = now - (parseInt(shell.dataset.agmfBootAt || '0', 10) || now);
        // THIRTY SECONDS WITHOUT A WORD IS A PLAYER THAT IS NOT COMING. It is
        // recorded once (the tracker files it as agmf_media_error, code
        // boot_timeout, with the link) and then dropped, so the poster and
        // its play button are honest again and the next pass or tap boots
        // a fresh one - with the player script now cached, that one usually
        // answers. The card is NOT killed: a slow link is not a dead video.
        if (since > 30000) {
          if (!shell.dataset.agmfBootTimedOut) {
            shell.dataset.agmfBootTimedOut = '1';
            try {
              var host = shell.closest('.agmf-hero-slide') || shell.closest('.agmf-card') || shell;
              host.dispatchEvent(new CustomEvent('agmf:media_error', {
                bubbles: true,
                detail: { provider: 'youtube', code: 'boot_timeout', ms: since,
                          link: constrainedLink() ? 'constrained' : 'open' }
              }));
            } catch (e) {}
          }
          dropYt(shell);
          return;
        }
        var asked = parseInt(shell.dataset.agmfListenAt || '0', 10);
        if (since > 20000 && now - asked < 2000) return;
        shell.dataset.agmfListenAt = String(now);
        ytListen(shell, frame);
      });
      // a timer, not requestAnimationFrame: the engine already knows rAF is
      // frozen in a background tab and throttled under battery saver, and a
      // phone is where this clock matters
      if (askManager && !clockRaf) {
        clockRaf = setTimeout(function () { clockRaf = 0; manageVideos(); }, 0);
      }
    }, 500);
    // a tab that goes hidden mid-boot: those boots are struck from the measure
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) return;
      document.querySelectorAll('[data-agmf-yt][data-agmf-yt-booting="1"]').forEach(function (shell) {
        shell.dataset.agmfBootHidden = '1';
      });
    });

    // ── THE LINK IS MEASURED, NOT ASSUMED ─────────────────────────────────
    // Three players two screens ahead, one of them kept playing below the
    // fold, is the pre-warm Dorian asked for on 2026-09-06 ("the video
    // should play in advance when getting to it") - and on a phone's radio
    // it is three players sharing the pipe the visible one needs. The intent
    // is kept where the link can carry it: on a constrained link the next
    // player is still mounted a screen ahead, booted and buffered, only
    // PAUSED until the shopper arrives instead of streaming unseen.
    //
    // Constrained = what the browser says about the link (cellular, data
    // saver, anything below its own '4g' grade, under 1.5 Mbps) OR what the
    // page just lived through: a player that took over four seconds from
    // attach to its first message marks the session, whatever the browser
    // claims. iPhones have no Network Information API; the measurement is
    // what covers them.
    var linkSlowSeen = false, lastYtBootMs = 0;
    // a slow boot on the previous product page is still this phone's link
    try { linkSlowSeen = sessionStorage.getItem('agmf-link-slow') === '1'; } catch (e) {}
    function constrainedLink() {
      if (linkSlowSeen) return true;
      var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (!c) return false;
      if (c.saveData) return true;
      if (c.type === 'cellular') return true;
      var et = String(c.effectiveType || '');
      if (et && et !== '4g') return true;
      // NOT `downlink`. It is Chrome's estimate from recent transfers and it
      // decays on an idle page: measured 0.15 Mbps on a wired desktop line
      // with effectiveType still '4g' (2026-09-08). effectiveType is the
      // grade Chrome computes with hysteresis for exactly this decision.
      return false;
    }
    function noteYtBoot(ms) {
      lastYtBootMs = ms;
      if (ms > 4000) {
        linkSlowSeen = true;
        try { sessionStorage.setItem('agmf-link-slow', '1'); } catch (e) {}
      }
    }
    var readyRaf = 0;

    function syncYtSoundUi() {
      document.querySelectorAll('[data-agmf-yt]').forEach(function (shell) {
        var btn = shell.querySelector('[data-agmf-ytsound]');
        if (!btn) return;
        var on = shell.dataset.agmfSound === 'on';
        btn.setAttribute('aria-pressed', String(on));
        btn.setAttribute('aria-label', on ? 'Turn sound off' : 'Turn sound on');
        var mI = btn.querySelector('.agmf-sound-muted');
        var oI = btn.querySelector('.agmf-sound-on');
        if (mI) mI.hidden = on;
        if (oI) oI.hidden = !on;
      });
    }

    // ---- EXPAND / COLLAPSE a clip ---------------------------------------
    // The expanded view used to be `position:fixed; inset:0` on the shell
    // where it stood. That is not a viewport overlay here: `.agmf-cycle`
    // carries `contain: layout`, which makes it the containing block for
    // fixed children, so "fullscreen" resolved against the cycle instead -
    // measured 2026-08-16 as a 640x4072 black box at y=968 in a 720px
    // viewport, with the close button off-screen and the page scroll-locked.
    // A black screen you cannot leave (Dorian).
    //
    // So the shell is MOVED to <body> while expanded and put back after. The
    // yt manager walks the whole document, not the section, so a portaled
    // shell keeps playing, pausing and muting exactly as before.
    // A PRESS FLASHES A BADGE, exactly as the real player does - and only a
    // press. An engine pause is not a person's action, and a badge blinking on
    // cards nobody touched as the feed scrolls would be the "controls on all
    // the time" complaint all over again. It clears itself; nothing has to
    // remember it, and no teardown list has to learn a new key.
    function ytBezel(shell, kind) {
      if (!shell) return;
      shell.__agmfBez = (shell.__agmfBez || 0) + 1;
      var seq = shell.__agmfBez;
      delete shell.dataset.agmfBezel;
      void shell.offsetWidth;               // restart the animation on a rapid second press
      shell.dataset.agmfBezel = kind;
      setTimeout(function () {
        if (shell.__agmfBez === seq) delete shell.dataset.agmfBezel;
      }, 1050);
    }

    function expandShell(shell) {
      if (!shell || shell.classList.contains("agmf-yt-fullscreen")) return;
      var ph = document.createElement("div");
      ph.className = "agmf-yt-slot";
      ph.hidden = true;
      if (shell.parentNode) shell.parentNode.insertBefore(ph, shell);
      shell.__agmfSlot = ph;
      document.body.appendChild(shell);
      rebootYt(shell);
      shell.classList.add("agmf-yt-fullscreen");
      shell.__agmfExpandedAt = Date.now();
      document.documentElement.classList.add("agmf-noscroll");
    }

    function collapseShell(shell) {
      if (!shell) return;
      shell.classList.remove("agmf-yt-fullscreen");
      document.documentElement.classList.remove("agmf-noscroll");
      var ph = shell.__agmfSlot;
      shell.__agmfSlot = null;
      if (ph && ph.parentNode) {
        ph.parentNode.insertBefore(shell, ph);
        ph.remove();
        rebootYt(shell);
      } else if (shell.parentNode === document.body) {
        // its card was recycled while expanded - drop it, the recycler
        // rebuilds that card from the feed anyway
        shell.remove();
      }
    }

    // A MOVED IFRAME IS A RELOADED PLAYER. Reparenting the shell (expand,
    // collapse) navigates its iframe again, so the player comes back at
    // 0:00, muted and silent - while the shell still said ready, unMuted
    // and playing, and nothing re-asked or re-applied (refutation pass
    // 2026-09-08). Forget what the old player had answered: the clock asks
    // the new one, and the manager applies play and sound afresh.
    function rebootYt(shell) {
      if (!shell || !shell.querySelector('iframe')) return;
      ['agmfReady', 'agmfPlayCmd', 'agmfMuteCmd', 'agmfPlayAt', 'agmfYtPlaying',
       'agmfYtState', 'agmfListenAt', 'agmfFloor'].forEach(function (k) { delete shell.dataset[k]; });
      shell.dataset.agmfBootAt = String(Date.now());
    }

    function collapseAnyExpanded() {
      var open = document.querySelector(".agmf-yt-fullscreen");
      if (open) collapseShell(open);
      // belt and braces: the scroll lock never outlives the overlay
      if (!document.querySelector(".agmf-yt-fullscreen")) {
        document.documentElement.classList.remove("agmf-noscroll");
      }
    }

    // Escape always works, whatever the overlay is doing.
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" || e.key === "Esc") collapseAnyExpanded();
    });
    function ytCommand(frame, func, args) {
      // The embed accepts jsapi commands over postMessage (enablejsapi=1).
      // Commands sent before the player is ready are dropped harmlessly, and
      // repeats are no-ops — so firing every pass converges on the right state.
      try {
        frame.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: func, args: args || '' }), '*');
      } catch (e) { /* frame mid-load — next pass retries */ }
    }

    // playback progress arrives as jsapi infoDelivery messages once a frame
    // has been sent 'listening' — that stream drives OUR timebar (YouTube's
    // own controls are all-or-nothing and stay off)
    window.addEventListener('message', function (e) {
      if (typeof e.data !== 'string' || e.data.charAt(0) !== '{') return;
      if (String(e.origin).indexOf('youtube') === -1) return;
      var data;
      try { data = JSON.parse(e.data); } catch (err) { return; }
      var info = data && data.info;
      document.querySelectorAll('.agmf-ytframe').forEach(function (frame) {
        if (frame.contentWindow !== e.source) return;
        var shell = frame.closest('[data-agmf-yt]');
        if (!shell) return;
        // the player says the video cannot play (deleted, private, embedding
        // off, region-blocked, referrer refused): the card goes, and says so
        if (data.event === 'onError') { mediaDead(shell, 'youtube', data.info); return; }
        // ANY jsapi message = the player is booted; commands are safe now.
        // Commanding a booting player threw isExternalMethodAvailable
        // TypeErrors inside YouTube's own base.js (console, 2026-08-12).
        if (!shell.dataset.agmfReady) {
          shell.dataset.agmfReady = '1';
          delete shell.dataset.agmfYtBooting;
          var bootAt = parseInt(shell.dataset.agmfBootAt || '0', 10);
          if (bootAt && shell.dataset.agmfBootHidden !== '1') noteYtBoot(Date.now() - bootAt);
          // play/pause, mute, the warm election and the poster used to wait
          // for the next scroll to apply; a shopper who stopped to watch
          // never scrolled. They apply on the frame after the handshake.
          if (!readyRaf) readyRaf = setTimeout(function () { readyRaf = 0; manageVideos(); }, 0);
        }
        // playing for real: the poster above the frame may go (v39.96)
        if (info && info.playerState === 1) shell.dataset.agmfYtPlaying = '1';
        // WHO PRESSED PLAY DOES NOT MATTER. A click inside the player is
        // invisible out here, but the state it produces is not: the jsapi
        // reports it either way, so this is how a shopper's use of YouTube's
        // own bar still reaches the analytics (agmf-track.js listens).
        if (info && typeof info.playerState === 'number' && shell.dataset.agmfYtState !== String(info.playerState)) {
          shell.dataset.agmfYtState = String(info.playerState);
          // AND THE LABEL FOLLOWS IT. The button shipped saying "Pause" and was
          // rewritten only by a press on itself, so a screen reader read
          // "Pause" over a clip the engine had stopped. Same event as the CSS
          // reads, so the two cannot drift apart.
          var running = info.playerState === 1 || info.playerState === 3 || info.playerState === 0;
          var lbl = shell.querySelector('[data-agmf-ytplay]');
          if (lbl) lbl.setAttribute('aria-label',
            (running && shell.dataset.agmfPaused !== '1') ? 'Pause' : 'Play');
          var NAMED = { '-1': 'unstarted', 0: 'ended', 1: 'play', 2: 'pause', 3: 'buffering', 5: 'cued' };
          try {
            shell.dispatchEvent(new CustomEvent('agmf:ytstate', {
              bubbles: true,
              detail: {
                state: NAMED[String(info.playerState)] || String(info.playerState),
                at_s: Math.round(info.currentTime || 0),
                dur_s: Math.round(info.duration || parseFloat(shell.dataset.agmfDur) || 0) || null,
                controls: shell.dataset.agmfYtControls || 'ours'
              }
            }));
          } catch (err) {}
        }
        if (!info || typeof info.currentTime !== 'number') return;
        // created loops: hold the player inside its [start, end) range -
        // seek back whenever playback leaves the cut (v38)
        var ls = parseFloat(shell.dataset.agmfLoopStart);
        var le = parseFloat(shell.dataset.agmfLoopEnd);
        if (!isNaN(ls) && le > ls) {
          if (info.currentTime >= le || info.currentTime < ls - 0.35) {
            ytCommand(frame, 'seekTo', [ls, true]);
          } else {
            // progress reports arrive ~4/s - waiting for one to SAY the end
            // passed overshoots by a few hundred ms. Schedule the seek for
            // the exact remaining time instead (fresh reports re-tune it).
            if (shell.__agmfLoopT) clearTimeout(shell.__agmfLoopT);
            var waitMs = (le - info.currentTime) * 1000 - 60;
            if (waitMs >= 0 && waitMs < 1500) {
              shell.__agmfLoopT = setTimeout(function () {
                ytCommand(frame, 'seekTo', [ls, true]);
              }, waitMs);
            }
          }
        }
        if (info.duration) shell.dataset.agmfDur = String(info.duration);
        shell.dataset.agmfAt = String(info.currentTime);
        var dur = parseFloat(shell.dataset.agmfDur) || 0;
        var fill = shell.querySelector('.agmf-yt-bar-fill');
        if (fill && dur > 0) fill.style.width = Math.min(100, (info.currentTime / dur) * 100) + '%';
        // v40.1: the buffered stripe behind the red - the real bar shows it
        // and it comes free in this message, costing no call of its own.
        var load = shell.querySelector('.agmf-yt-bar-load');
        if (load && typeof info.videoLoadedFraction === 'number') {
          load.style.width = Math.min(100, Math.max(0, info.videoLoadedFraction * 100)) + '%';
        }
      });
    });

    // Inside a rail, a half-swiped neighbour is still geometrically "on
    // screen" - so two videos played at once and the one you swiped away
    // kept going (Dorian 2026-08-12). ACTIVE = the slide whose centre is
    // nearest the track's centre; everything else pauses.
    function slideIsActive(el) {
      // The PRODUCT GALLERY is a rail too. It was missing from this test, so
      // every gallery slide answered "active" and a video swiped away kept
      // playing - audible on phones where a sliver of it still showed at the
      // edge of the card (Dorian 2026-08-16).
      var slide = el.closest ? el.closest('.agmf-slide, .agmf-hero-slide') : null;
      if (!slide) return true;                       // not in a rail
      var track = slide.parentNode;
      if (!track || !track.getBoundingClientRect) return true;
      var tr = track.getBoundingClientRect();
      var sr = slide.getBoundingClientRect();
      // ONLY A RAIL HAS A SLIDE IT HAS SCROLLED TO. On the desktop layout the
      // product card is not a rail any more, it is a two-column GRID, and no
      // cell of a multi-column grid can sit within 35% of a slide-width of the
      // track's centre - measured on the live page, the closest slide is 316px
      // off against a 215px tolerance. So EVERY hero slide answered "not
      // active": a video in the product card attached its iframe, finished the
      // handshake and was immediately told to pause, and a baked clip sat
      // there as a black box. Ask the centring question only where there is
      // overflow to scroll; below, `shown` is the real visibility test and
      // electPlayer still allows only one video to play page-wide.
      if (track.scrollWidth > track.clientWidth + 1 &&
          Math.abs((sr.left + sr.width / 2) - (tr.left + tr.width / 2)) >= sr.width * 0.35) return false;
      // and it must really be on screen: a 2px sliver is not a playing card
      var shown = Math.min(sr.right, window.innerWidth) - Math.max(sr.left, 0);
      return shown > sr.width * 0.55;
    }

    // ---- AUTOSWAP in the FEED ------------------------------------------
    // The product card's version lives in heroAutoAdvance(); this is the
    // other surface and answers to its own setting. Moving on happens INSIDE
    // the card the shopper is already looking at - its carousel goes to the
    // next item of the same kind. The page is never scrolled for them.

    // Somewhere to go: the card became a carousel and holds more than one
    // real slide (children = 2 edge clones + the real ones).
    function railOf(el) {
      var slide = el.closest ? el.closest('.agmf-slide') : null;
      var track = slide && slide.parentNode;
      if (!track || !track.classList || !track.classList.contains('agmf-track')) return null;
      return track.children.length > 3 ? track : null;
    }

    function feedAdvanceWanted(el) {
      if (!AUTO.feed) return false;
      if (el.closest('.agmf-hero')) return false;        // the other surface
      if (el.closest('[data-agmf-loop]')) return false;  // a loop is never advanced
      return !!railOf(el);
    }

    function advanceRail(el) {
      var track = railOf(el);
      if (!track) return;
      var w = track.clientWidth || 1;
      // An EXACT slide, not "one width further on". CSS snap is off (v39.45 -
      // the engine drives the rail itself), and the settle handler only
      // straightens the edges, so a scroll that starts a few pixels off a
      // boundary would come to rest a few pixels off one.
      var i = railIndex(track);
      // The settle handler walks a move back if it lands more than one step
      // from where the last gesture started. Tell it where THIS one starts,
      // or a swipe five minutes ago decides where a finished video goes.
      track.__agmfFrom = i;
      track.scrollTo({ left: (i + 1) * w, behavior: 'smooth' });
    }

    // Every feed video ships `loop` so a card never dead-ends on a black
    // frame. A looping video never fires 'ended', so where auto-advance
    // governs the video, looping comes off - and goes back on the moment it
    // stops governing (setting off, or the card never became a carousel).
    function syncFeedAdvance(v) {
      // Off = touch nothing, so the shipped `loop` default stands. Hero
      // videos are the OTHER surface and handle their own looping - this
      // ran document-wide and would have undone that.
      if (!AUTO.feed || v.closest('.agmf-hero')) return;
      var wanted = feedAdvanceWanted(v);
      v.loop = !wanted;
      if (wanted && !v.__agmfEndWired) {
        v.__agmfEndWired = 1;
        v.addEventListener('ended', function () {
          if (feedAdvanceWanted(v)) advanceRail(v);
        });
      }
    }

    // A YouTube shell has no 'ended' to listen for - the embed loops by URL
    // and reports position through the jsapi bridge. Same rule as the hero:
    // inside 1.5s of the end counts as finished. Only runs while the setting
    // is on, and only for shells that have a carousel to move along.
    function checkFeedYtEnds() {
      document.querySelectorAll('[data-agmf-yt]').forEach(function (shell) {
        if (shell.dataset.agmfAdvanced || !feedAdvanceWanted(shell)) return;
        // dur/at SURVIVE a release, so a shell parked near its end still
        // reads as finished while detached. Only a player that is attached,
        // handshaken and currently wanted-playing is really playing - which
        // is also the only one whose card the shopper can see.
        if (!shell.querySelector('iframe') || !shell.dataset.agmfReady ||
            shell.dataset.agmfPlayCmd !== 'playVideo') return;
        var dur = parseFloat(shell.dataset.agmfDur), at = parseFloat(shell.dataset.agmfAt);
        if (!(dur > 0) || !(at >= 0) || dur - at >= 1.5) return;
        shell.dataset.agmfAdvanced = '1';
        advanceRail(shell);
      });
    }

    // How much of an element the shopper can actually see. Shared by the
    // playback election below and by applySound() - one measure, so the video
    // that plays and the video that speaks can never disagree.
    function visibleAreaOf(el) {
      var vh = window.innerHeight || 1, vw = window.innerWidth || 1;
      var r = el.getBoundingClientRect();
      if (!slideIsActive(el)) return 0;
      return Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0)) *
             Math.max(0, Math.min(r.right, vw) - Math.max(r.left, 0));
    }
    // HOW MUCH OF ITSELF a piece shows, 0..1 - the measure sound and playback
    // elect on (2026-09-08, a phone: "a sound of the video started too soon
    // while still seeing a video a bit higher"). Raw area favoured tall
    // cards: a Short peeking 45% in from below (3:4, ~500px) out-measured a
    // landscape clip fully in view above it (~210px), so the sound and the
    // playback moved down while the shopper was still watching the upper
    // one. A hair of area breaks ties between two pieces fully in view.
    function visibleShareOf(el) {
      var vh = window.innerHeight || 1, vw = window.innerWidth || 1;
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) return 0;
      var area = visibleAreaOf(el);
      if (!area) return 0;
      return area / (r.width * r.height) + (area / (vw * vh)) * 0.001;
    }
    // ONE ELECTION FOR SOUND AND PLAYBACK. `cands` = [{el, share, current}].
    // The one showing the most of itself wins - except that the piece
    // already holding the floor keeps it while at least half of it is in
    // view, unless a challenger shows clearly more (a quarter more). Without
    // that, the floor changed hands at the exact pixel where two shares
    // crossed, mid-scroll, which is the "too soon" the shopper felt.
    function electAmong(cands) {
      var best = null, current = null, asked = null;
      for (var i = 0; i < cands.length; i++) {
        var c = cands[i];
        if (c.share <= 0) continue;
        if (!best || c.share > best.share) best = c;
        if (c.current && (!current || c.share > current.share)) current = c;
        // A TAP ON A SPEAKER IS A VOTE for that piece: while any of it is in
        // view it holds the floor, whatever the shares say (the refutation
        // pass 2026-09-08: the tap used to flip a page-wide switch and the
        // election then unmuted the piece ABOVE the one tapped).
        if (c.asked && c.share > 0.1 && (!asked || c.share > asked.share)) asked = c;
      }
      if (asked) return asked.el;
      if (best && current && best !== current && current.share >= 0.5 && best.share < current.share + 0.25) return current.el;
      return best ? best.el : null;
    }

    // ONE VIDEO PLAYS AT A TIME, PAGE-WIDE.
    //
    // Sound was made exclusive in August; playback never was. Several videos
    // can be "visible" at once - a rail card and the one below it, the
    // product card and a feed card - and each of them started itself, so
    // scrolling left a trail of things still moving behind the shopper. Only
    // their audio had been silenced (Dorian 2026-08-30: "the one before
    // should stop, not only its sound").
    //
    // The winner is whichever player the shopper can see most of, across BOTH
    // kinds - a YouTube embed and an mp4 compete on the same measure, because
    // to a shopper they are both just the video that is playing. Unlike the
    // audio holder, a baked clip CAN win: it is silent, but it is motion, and
    // motion is the thing being made exclusive here.
    // A LOOP IS NOT IN THE ELECTION (Dorian 2026-10-07: "The gifs, only one is
    // playing at same time but it should not it should play at same time (not
    // like other videos)"). One-at-a-time is for videos - things with a story
    // and a sound. A loop is a moving picture: silent, a couple of seconds,
    // made to repeat, so every loop on screen plays together. It also never
    // takes the floor, so a loop beside a real video cannot stop that video.
    // `data-agmf-loop` is the one mark: feed cards (renderItem) and product-card
    // slides both carry it for every isLooping() piece - a GIF-format clip or
    // a cut with a real range, baked mp4 or YouTube embed alike.
    function isLoopEl(el) {
      return !!(el && el.closest && el.closest('[data-agmf-loop]'));
    }

    function electPlayer() {
      // BEHIND A DIALOG THE PAGE IS SILENT. The desktop lightbox plays its
      // own copy of the piece; the card underneath kept the floor and spoke
      // behind it (refutation pass 2026-09-08).
      if (document.querySelector('.agmf-lb, .agmf-lb-frame')) return null;
      var cands = [];
      // `current` is THE PIECE THE LAST ELECTION CHOSE - stamped below by
      // manageVideos - never "whatever was told to play": the warm player is
      // told to play too, and reading it as the floor holder handed the floor
      // to the card entering from below at the very crossover pixel the
      // hysteresis was written to prevent (the refutation pass found it).
      document.querySelectorAll('video[data-agmf-src]').forEach(function (v) {
        if (!v.getAttribute('src')) return;      // not attached: cannot play
        if (isLoopEl(v)) return;                 // loops play on their own
        cands.push({ el: v, share: visibleShareOf(v), current: !!v.__agmfFloor, asked: false });
      });
      document.querySelectorAll('[data-agmf-yt]').forEach(function (shell) {
        if (!shell.querySelector('iframe') || !shell.dataset.agmfReady) return;
        if (isLoopEl(shell)) return;             // loops play on their own
        cands.push({ el: shell, share: visibleShareOf(shell),
                     current: shell.dataset.agmfFloor === '1',
                     asked: shell.dataset.agmfAsked === '1' });
      });
      return electAmong(cands);
    }
    // the stamp the next election reads as "current"
    function stampFloor(player) {
      document.querySelectorAll('video[data-agmf-src]').forEach(function (v) { v.__agmfFloor = (v === player); });
      document.querySelectorAll('[data-agmf-yt]').forEach(function (shell) {
        if (shell === player) shell.dataset.agmfFloor = '1'; else delete shell.dataset.agmfFloor;
      });
    }

    var YT_MOUNT_CAP = 3;
    // on a constrained link: two players, one screen and a third ahead, and
    // nothing kept playing where nobody can see it (see constrainedLink)
    function ytMountCap() { return constrainedLink() ? 2 : YT_MOUNT_CAP; }
    function ytReach() { return constrainedLink() ? 1.3 : 2.2; }
    function manageVideos() {
      var vh = window.innerHeight || 1;
      var vw = window.innerWidth || 1;
      var player = electPlayer();
      stampFloor(player);
      document.querySelectorAll('video[data-agmf-src]').forEach(function (v) {
        var r = v.getBoundingClientRect();
        // horizontal-aware: a slide parked off to the side of its carousel
        // must not hold a src (7 slides x 1 track = 7 preloads otherwise)
        // A HIDDEN ELEMENT IS NOT A NEARBY ONE. Every test here is a
        // comparison against a rect, and a `display:none` element reports
        // {0,0,0,0} - which satisfies ALL of them. The desktop layout hides
        // eight of every rail’s nine slides, so each one attached a real
        // player and no release test could ever take it back (its rect stays
        // zero, so `nearKeep` is zero-satisfied too). Dozens of invisible
        // players per page: they never showed, they never played -
        // slideIsActive rejects them - they just cost boots, and every
        // YouTube boot re-requests captions until the endpoint 429s.
        var rendered = r.width > 0 || r.height > 0;
        var nearX = rendered && r.left < vw * 1.5 && r.right > -vw * 0.5;
        // baked clips are small and posterless - attach a little earlier so
        // the shopper never meets an empty black box
        var reach = v.closest('.agmf-card--clip') ? 3 : 2;
        var near = nearX && r.top < vh * reach && r.bottom > -vh;   // attach zone
        var visible = near && r.top < vh * 0.9 && r.bottom > vh * 0.1 &&
                      r.left < vw && r.right > 0 && slideIsActive(v);  // play zone
        if (near && !v.getAttribute('src')) {
          v.setAttribute('src', v.dataset.agmfSrc);
          if (!v.dataset.agmfErrBound) {
            v.dataset.agmfErrBound = '1';
            v.addEventListener('error', function () {
              if (v.getAttribute('src')) mediaDead(v, 'file', v.error && v.error.code);
            });
          }
          // baked clips carry their crop IN the pixels: trust the file, not
          // the exporter's vertical hint (Dorian 2026-08-12)
          if (v.closest('.agmf-card--clip') && !v.dataset.agmfShaped) {
            v.addEventListener('loadedmetadata', function () {
              if (!v.videoWidth || !v.videoHeight) return;
              v.dataset.agmfShaped = '1';
              var box = v.closest('.agmf-media');
              if (box) {
                box.style.aspectRatio = v.videoWidth + ' / ' + v.videoHeight;
                box.classList.add('agmf-media--exact');
              }
            }, { once: true });
          }
        }
        // AUTOSWAP: re-asked every pass, because whether a card HAS a next
        // item only becomes true when the carousel is built, which happens
        // on sight rather than at render.
        syncFeedAdvance(v);
        // Visible is not enough any more: it also has to be THE one.
        if (visible && (v === player || isLoopEl(v)) && v.getAttribute('src')) {
          if (v.paused && !prefersReduced()) {
            var p = v.play();
            if (p && p.catch) p.catch(function () { /* autoplay veto — poster stays */ });
          }
        } else if (!v.paused) {
          v.pause();
        }
        if (!near && v.getAttribute('src')) {
          try { v.pause(); v.removeAttribute('src'); v.load(); } catch (e) { /* ignore */ }
        }
      });
      // YouTube shells: same lifecycle, iframe-flavoured. Attached = playing
      // (muted); detached = released. Tighter zone than mp4s — embeds are heavy.
      // ── YOUTUBE: PRE-WARMED, CAPPED (Dorian 2026-09-06: "the video should
      //    play in advance when getting to it; thumbnails as little as
      //    possible") ────────────────────────────────────────────────────
      // The next players are mounted while the shopper is still on the
      // current card (two screens ahead), and the nearest one below the fold
      // keeps running muted, so a card arrives with its player booted,
      // buffered and already moving; the poster covers a player only until
      // it reports a playing state. Never more than YT_MOUNT_CAP players
      // exist at once - each is a full YouTube player in memory - and the
      // closest ones win; one farther away and off screen is released to
      // make room.
      var ys = [];
      document.querySelectorAll('[data-agmf-yt]').forEach(function (shell) {
        var r = shell.getBoundingClientRect();
        var shown = r.width > 0 || r.height > 0;
        var inX = r.left < vw * 1.5 && r.right > -vw * 0.5;
        var nearAttach = shown && inX && r.top < vh * ytReach() && r.bottom > -vh * 0.6;
        // ON A CONSTRAINED LINK THE CAP SERVES THE SCROLL. A rail's neighbour
        // sits just off the right edge and used to take a pre-mount slot; with
        // two slots, the visible slide and its neighbour filled both, and the
        // next card DOWN - where the thumb is going - booted on arrival
        // (audit 2026-09-08). A swipe is a deliberate gesture the rail's own
        // scroll pass attaches for; the scroll direction gets the pre-warm.
        // ...EXCEPT ON THE RAIL THE SHOPPER IS HOLDING (2026-09-08, the
        // same afternoon: "the infinite feed on the product card is not
        // happening anymore"). The product card's endless tail IS a row of
        // horizontal neighbours, and with none of them pre-mounted every
        // Short they swiped to on a phone booted from cold - five to fifteen
        // seconds of ring per slide reads as a dead feed. The next slide of
        // a rail that is on screen still pre-mounts, cued and one boot at a
        // time; only the neighbours of rails the shopper is not on wait.
        if (nearAttach && (r.left >= vw || r.right <= 0) && constrainedLink()) nearAttach = railOnScreen(shell);
        var nearKeep = shown && r.top < vh * 2.8 && r.bottom > -vh * 1.4 &&
                       r.left < vw * 2 && r.right > -vw;
        var dist = Math.abs((r.top + r.height / 2) - vh / 2) + (inX ? 0 : vw);
        ys.push({ shell: shell, r: r, nearAttach: nearAttach, nearKeep: nearKeep,
                  dist: dist, frame: shell.querySelector('iframe') });
      });
      function releaseYt(y) {
        dropYt(y.shell);    // poster shows again; re-attaches on return
        y.frame = null;
      }
      // is the rail this shell belongs to the one in front of the shopper
      function railOnScreen(shell) {
        var track = shell.closest('[data-agmf-hero-track], .agmf-track');
        if (!track) return false;
        var tr = track.getBoundingClientRect();
        return tr.top < vh * 0.9 && tr.bottom > vh * 0.1;
      }
      // how much of a shell the viewport shows, in px^2 - the one measure
      // the cap arbitrates on
      function visOf(y) {
        return Math.max(0, Math.min(y.r.bottom, vh) - Math.max(y.r.top, 0)) *
               Math.max(0, Math.min(y.r.right, vw) - Math.max(y.r.left, 0));
      }

      // THE SIZING TUTORIAL WAITS TO BE ASKED (Dorian 2026-09-13: "the video
      // that is in the sizing section, i dont want to autoplay it"). Every
      // other player on this page is feed content the shopper is scrolling
      // THROUGH, where arriving already in motion is the whole point. The spec
      // panel's video is not that: it sits inside a tab somebody opened to
      // READ, and a tutorial that starts talking by itself interrupts the
      // reading it was opened for. So this one mounts nothing until it is
      // asked. Its poster and play button are already drawn by the shell
      // markup, and both ways of asking still work untouched - the play button
      // (which sets agmfUserPlay and calls straight back here) and a tap on
      // the picture, which attaches on its own path.
      function holdsForTap(shell) {
        try {
          var waiting = shell.classList.contains('agmf-spec-video') &&
                        shell.dataset.agmfUserPlay !== '1';
          // AND IT HAS TO LOOK LIKE IT IS WAITING. A shell with no player
          // hides its centre play disc - right for a feed card, whose poster
          // is a teaser for something that will start itself - while the
          // control bar still draws its playing face. Held back, that reads as
          // a video that started and then froze, with nothing to press
          // (Dorian 2026-09-13: "its not playing but its not in pause neither
          // and its like it started"). This stamp is what the stylesheet uses
          // to put the disc back and take the bar away until it is asked.
          if (waiting) shell.dataset.agmfAwaitTap = '1';
          else delete shell.dataset.agmfAwaitTap;
          return waiting;
        } catch (e) { return false; }
      }
      ys.forEach(function (y) { if (y.frame && !y.nearKeep) releaseYt(y); });
      if (!prefersReduced()) {
        var mounted = ys.filter(function (y) { return y.frame; });
        var want = ys.filter(function (y) {
          return !y.frame && y.nearAttach && !y.shell.dataset.agmfDead && !holdsForTap(y.shell);
        })
                     .sort(function (a, b) { return a.dist - b.dist; });
        var tight = constrainedLink();
        want.forEach(function (y) {
          var onScreen = y.r.top < vh * 0.9 && y.r.bottom > vh * 0.1 && y.r.left < vw && y.r.right > 0;
          // ONE BOOT AT A TIME ON A CONSTRAINED LINK (audit 2026-09-08): a
          // second player booting beside the first shares the pipe with it.
          // The one on screen is never made to wait; a pre-mount is.
          if (tight && !onScreen && mounted.some(function (m) { return !m.shell.dataset.agmfReady; })) return;
          if (mounted.length >= ytMountCap()) {
            // THE SLOT GOES TO WHAT IS SHOWN MORE. The old rule released the
            // farthest player unless it was "on screen", and on screen meant
            // any pixel: a player hanging 19px in from above, or the cued
            // pre-mount peeking in from below, kept its slot while a fully
            // visible card went without (measured 2026-09-08). Now the
            // least-shown mounted player gives way to a candidate shown
            // more; when neither shows, the nearer one wins.
            mounted.sort(function (a, b) { return (visOf(a) - visOf(b)) || (b.dist - a.dist); });
            var far = mounted[0];
            var farVis = far ? visOf(far) : 0, yVis = visOf(y);
            if (!far || (farVis > 0 && farVis >= yVis) || (farVis === 0 && yVis === 0 && far.dist <= y.dist)) return;
            releaseYt(far);
            mounted.shift();
          }
          // a pre-mount on a constrained link boots cued: ready, buffered
          // by the player's own lookahead, not streaming where nobody looks
          attachYt(y.shell, tight && !onScreen);
          y.frame = y.shell.querySelector('iframe');
          if (y.frame) mounted.push(y);
        });
      }
      // the one to keep warm: the nearest mounted player below the fold
      var warm = null;
      ys.forEach(function (y) {
        // strictly BELOW the play zone (which starts at 0.9vh): a warm player
        // with its top at 0.5-0.9vh was both warm and visible, so it ran on
        // screen beside the elected one - two videos moving, and the second
        // marked as if it held the floor (refutation pass 2026-09-08)
        if (!y.frame || y.r.top < vh * 0.9 || y.r.top > vh * 1.7) return;
        if (constrainedLink()) return;   // booted and buffered, but not streaming unseen
        if (!warm || y.dist < warm.dist) warm = y;
      });
      ys.forEach(function (y) {
        var shell = y.shell, r = y.r, frame = y.frame;
        if (!frame) return;
        var vis = r.top < vh * 0.9 && r.bottom > vh * 0.1 &&
                  r.left < vw && r.right > 0 && slideIsActive(shell);
        if (!shell.dataset.agmfReady) { ytListen(shell, frame); return; }
        // REDUCED MOTION SUPPRESSES AUTOPLAY, NOT A REQUEST. Without the
        // second half of this, a press on the centre disc under
        // prefers-reduced-motion was undone on the very next pass: the press
        // deletes agmfPlayCmd, the pause branch below is satisfied, and the
        // clip stops again. The disc has had that defect since it was built.
        var autoOk = !prefersReduced() || shell.dataset.agmfUserPlay === '1';
        var wantPlay = autoOk && shell.dataset.agmfPaused !== '1' &&
                       ((vis && (shell === player || isLoopEl(shell))) || y === warm)
          ? 'playVideo' : 'pauseVideo';
        shell.dataset.agmfWarm = y === warm && !vis ? '1' : '';
        var now = Date.now();
        if (wantPlay === 'playVideo') {
          var last = parseInt(shell.dataset.agmfPlayAt || '0', 10);
          if (shell.dataset.agmfPlayCmd !== wantPlay || now - last > 1500) {
            ytCommand(frame, 'playVideo');
            shell.dataset.agmfPlayCmd = wantPlay;
            shell.dataset.agmfPlayAt = String(now);
          }
        } else if (shell.dataset.agmfPlayCmd !== wantPlay) {
          ytCommand(frame, 'pauseVideo');
          shell.dataset.agmfPlayCmd = wantPlay;
        }
        shell.dataset.agmfSound = soundOn ? 'on' : 'off';
        shell.dataset.agmfVisible = vis ? "1" : "";
        if (!vis && shell.dataset.agmfAsked) delete shell.dataset.agmfAsked;
        if (!vis && shell.dataset.agmfUserPlay) delete shell.dataset.agmfUserPlay;
      });
      applySound(player);
    }

    // ---- the rating opens the reviews --------------------------------
    // The stars under the product name are a link to the proof behind them.
    // This lived inside buildAccBar() at first, which is PARKED (the sticky
    // tab bar Dorian removed), so it never registered and a click did
    // nothing - and `accWrap`, assigned in that same parked function, is
    // always null. It asks the DOM instead of trusting that variable.
    document.addEventListener("click", function (e) {
      var badge = e.target.closest ? e.target.closest("[data-agmf-hero-badge]") : null;
      if (!badge || (e.target.closest && e.target.closest("a, button"))) return;
      var acc = document.querySelector(".agmf-acc--reviews");
      if (!acc) return;
      acc.open = true;
      // The accordion is in the BUY column, which on desktop is its own
      // scroller - so this is a delta measured against that pane, not a
      // document position. As a document position it was thousands of pixels
      // into a page with a few hundred, and every click landed on the footer.
      var lift = agmfScroller(acc)
        ? 12
        : (typeof headerOffset === "function" ? headerOffset() : 0) + 12;
      var startedAt = agmfScroller(acc) ? agmfScroller(acc).scrollTop : window.scrollY;
      agmfScrollBy(acc, acc.getBoundingClientRect().top - lift, !prefersReduced());
      // A smooth scroll that never starts leaves the shopper where they were,
      // with the accordion open somewhere below - which reads as "the click
      // did nothing". If nothing has moved shortly after, jump.
      setTimeout(function () {
        var host = agmfScroller(acc);
        var now = host ? host.scrollTop : window.scrollY;
        var left = acc.getBoundingClientRect().top - lift;
        if (Math.abs(now - startedAt) < 4 && Math.abs(left) > 8) {
          agmfScrollBy(acc, left, false);
        }
      }, 380);
      manageVideos();
    });
    // ---- ONE AUDIO HOLDER for the whole page ---------------------------
    // Every player that can speak - our own <video> clips and the YouTube
    // embeds - competes here, and exactly one wins: the one showing the most
    // pixels. Deciding per-player is what let two videos speak at once, and
    // the old "is any yt shell flagged on" test could also silence a native
    // clip that WAS the visible one.
    function applySound(player) {
      // ONE DECISION: the piece that plays is the piece that speaks. Sound
      // used to run its own election on the same measure, which agreed with
      // playback until hysteresis gave each a memory of its own; now the
      // manager hands its elected player in, and the sound-button path asks
      // for the same election. A baked clip is silent by construction and a
      // warm player runs below the fold to be ready - neither ever speaks.
      var holder = player === undefined ? electPlayer() : player;
      if (holder && holder.closest && holder.closest('.agmf-card--clip')) holder = null;
      if (holder && holder.dataset && holder.dataset.agmfWarm === '1') holder = null;
      document.querySelectorAll('.agmf-media video').forEach(function (v) {
        v.muted = v.closest('.agmf-card--clip') ? true : !(soundOn && v === holder);
      });
      document.querySelectorAll('[data-agmf-yt]').forEach(function (shell) {
        var frame = shell.querySelector("iframe");
        if (!frame || !shell.dataset.agmfReady) return;
        var cmd = (soundOn && shell === holder) ? 'unMute' : 'mute';
        if (shell.dataset.agmfMuteCmd !== cmd) {
          ytCommand(frame, cmd);
          shell.dataset.agmfMuteCmd = cmd;
        }
      });
      section.querySelectorAll('.agmf-sound').forEach(function (btn) {
        // yt toggles are governed by syncYtSoundUi, not the native sound state
        if (btn.closest && btn.closest('[data-agmf-yt]')) return;
        var mutedIco = qs(btn, '.agmf-sound-muted');
        var onIco = qs(btn, '.agmf-sound-on');
        btn.setAttribute('aria-pressed', String(soundOn));
        btn.setAttribute('aria-label', soundOn ? 'Turn sound off' : 'Turn sound on');
        if (mutedIco) mutedIco.hidden = soundOn;
        if (onIco) onIco.hidden = !soundOn;
      });
    }

    section.addEventListener('click', function (e) {
      var soundBtn = e.target.closest ? e.target.closest('.agmf-sound') : null;
      if (soundBtn) {
        if (soundBtn.closest && soundBtn.closest('[data-agmf-yt]')) return; // doc listener owns yt sound
        soundOn = !soundOn;
        if (soundOn) {
          document.querySelectorAll('[data-agmf-yt]').forEach(function (s) { s.dataset.agmfSound = 'off'; });
          syncYtSoundUi();
          manageVideos();
        }
        applySound();
        return;
      }
      var arrow = e.target.closest ? e.target.closest('[data-agmf-arrow]') : null;
      if (arrow) {
        var host = arrow.closest('.agmf-card');
        var tr = host && host.querySelector('.agmf-track');
        if (tr) tr.scrollBy({ left: (parseInt(arrow.getAttribute('data-agmf-arrow'), 10) || 1) * tr.clientWidth, behavior: 'smooth' });
        return;
      }

    });

    // ---- full review ON the card (Dorian): a tap lays the complete review
    // over the card's own photo behind a dark veil; the text scrolls inside
    // the card. A second tap (or the close button) collapses it. ----
    // the slide the shopper is LOOKING at: the tapped slide when there is
    // one, else the track's visible slide; edge clones retarget to their
    // real twin (the settle teleport would strand an overlay on a clone).
    function visibleRealSlide(outer, from) {
      var slide = from && from.closest ? from.closest('.agmf-slide') : null;
      if (!slide) {
        var track = outer.querySelector('.agmf-track');
        if (track && track.clientWidth) {
          var kids = track.children;
          var i = Math.max(0, Math.min(kids.length - 1, railIndex(track)));

          slide = kids[i] || null;
        }
      }
      if (slide && slide.classList.contains('agmf-slide--clone') && slide.parentNode) {
        var sib = slide.parentNode.children;
        slide = slide === sib[0] ? sib[sib.length - 2] : sib[1];
      }
      return slide;
    }

    function toggleReviewOpen(card, from) {
      // anchor to the slide the shopper is looking at: a carouselized card
      // holds several review heroes and the DOM-first one is the off-screen
      // lead clone - an overlay appended there is invisible ("tap does
      // nothing", 2026-08-12). NEVER search outside that slide: a text-only
      // review must not borrow a sibling photo-review's hero (v35 review).
      var outer = (card.closest && card.closest('.agmf-card--slidable')) || card;
      var slide = visibleRealSlide(outer, from);
      var scope = slide || outer;
      var existing = scope.querySelector('.agmf-rev-open');
      // sweep the whole card - a misanchored overlay must never stack
      outer.querySelectorAll('.agmf-rev-open').forEach(function (o) { o.remove(); });
      if (existing) return;
      var innerCard = slide && slide.querySelector('.agmf-card--review');
      var item = (innerCard && innerCard.__agmfItem) || card.__agmfItem || outer.__agmfItem;
      if (!item) return;
      var revDate = fmtDate(item.publishedAt);
      var ov = document.createElement('div');
      ov.className = 'agmf-rev-open';
      ov.innerHTML =
        '<button class="agmf-rev-open-close" type="button" data-agmf-revopen-close aria-label="Close">&#10005;</button>' +
        '<div class="agmf-rev-open-scroll">' + reviewStars(item) +
        (item.title ? '<h3 dir="auto">' + esc(item.title) + '</h3>' : '') +
        (item.text ? '<p dir="auto">' + esc(item.text) + '</p>' : '') +
        '<div class="agmf-review-meta"><span>' + (item.platform === 'Reviews' ? 'Verified buyer' : item.platform === 'T&E' ? 'T&E Community' : 'Community') + '</span>' +
        (revDate ? '<span class="agmf-review-dot" aria-hidden="true"></span><span>' + revDate + '</span>' : '') +
        '</div></div>';
      var host = scope.querySelector('.agmf-review-hero') || scope;
      host.appendChild(ov);
    }

    // ---- full article ON the card (Dorian 2026-08-12): READ THE ARTICLE
    // opens a within-card reader - hero, headline, the stored summary and a
    // small attributed link to the original - never a navigation. ----
    function toggleArticleOpen(card, from) {
      // v39.9 (Dorian): a real READING DIALOG, not an in-card veil - scrim +
      // scrollable sheet; the article's own pictures (mirrored) are woven
      // between the paragraphs. No outbound link.
      var existing = document.querySelector('.agmf-art-dialog');
      if (existing) { existing.remove(); document.documentElement.classList.remove('agmf-noscroll'); return; }
      var outer = (card.closest && card.closest('.agmf-card--slidable')) || card;
      var slide = visibleRealSlide(outer, from);
      var innerCard = slide && slide.querySelector('.agmf-card--article');
      var item = (innerCard && innerCard.__agmfItem) || card.__agmfItem || outer.__agmfItem;
      if (!item) return;
      var src = item.source || {};
      var ago = fmtAgo(item.publishedAt);
      var paras = String(item.articleBody || item.text || '').split(/\n\n+/).filter(function (x) { return x.trim(); });
      var pics = (item.articleImages || []).slice(0, 8);
      // weave: a picture after every ceil(paras/(pics+1)) paragraphs
      var body = '';
      var step = pics.length ? Math.max(1, Math.ceil(paras.length / (pics.length + 1))) : 0;
      var pi = 0;
      paras.forEach(function (para, i) {
        body += '<p dir="auto">' + esc(para) + '</p>';
        if (step && pi < pics.length && (i + 1) % step === 0) {
          body += '<img class="agmf-artd-img" loading="lazy" alt="" src="' + esc(pics[pi++]) + '">';
        }
      });
      while (pi < pics.length) {
        body += '<img class="agmf-artd-img" loading="lazy" alt="" src="' + esc(pics[pi++]) + '">';
      }
      var dlg = document.createElement('div');
      dlg.className = 'agmf-art-dialog agmf-art-open';
      var lead = paras.length ? paras[0] : '';
      var rest = paras.slice(1);
      var restHtml = '';
      var step2 = pics.length ? Math.max(1, Math.ceil(rest.length / pics.length)) : 0;
      var pj = 0;
      rest.forEach(function (para, i) {
        restHtml += '<p dir="auto">' + esc(para) + '</p>';
        if (step2 && pj < pics.length && (i + 1) % step2 === 0) {
          restHtml += '<figure class="agmf-artd-fig"><img loading="lazy" alt="" src="' + esc(pics[pj++]) + '"></figure>';
        }
      });
      while (pj < pics.length) {
        restHtml += '<figure class="agmf-artd-fig"><img loading="lazy" alt="" src="' + esc(pics[pj++]) + '"></figure>';
      }
      var srcChip =
        '<span class="agmf-artd-chip">' +
        (src.avatar ? '<img class="agmf-artd-fav" src="' + esc(src.avatar) + '" alt="">' : '') +
        esc(src.name || 'Press') + (ago ? '<span class="agmf-artd-dot"></span>' + ago : '') + '</span>';
      dlg.innerHTML =
        '<div class="agmf-artd-scrim" data-agmf-artopen-close aria-hidden="true"></div>' +
        '<div class="agmf-artd-sheet" role="dialog" aria-modal="true">' +
        '<span class="agmf-artd-progress" aria-hidden="true"><i></i></span>' +
        '<span class="agmf-artd-grab" aria-hidden="true"></span>' +
        '<button class="agmf-artd-close" type="button" data-agmf-artopen-close aria-label="Close">&#10005;</button>' +
        '<div class="agmf-artd-scroll">' +
        '<div class="agmf-artd-hero' + (item.thumb ? '' : ' agmf-artd-hero--flat') + '">' +
        (item.thumb ? '<img class="agmf-artd-heroimg" alt="" src="' + esc(item.thumb) + '">' : '') +
        '<span class="agmf-artd-shade" aria-hidden="true"></span>' +
        '<div class="agmf-artd-herocopy">' + srcChip +
        '<h3 dir="auto">' + esc(item.title || '') + '</h3></div></div>' +
        '<div class="agmf-artd-body">' +
        (lead ? '<p class="agmf-artd-lead" dir="auto">' + esc(lead) + '</p>' : '') +
        restHtml +
        '<div class="agmf-artd-end">' + srcChip + '</div>' +
        '</div></div>';
      // read progress on the sheet's own scroller
      var scroller = dlg.querySelector('.agmf-artd-scroll');
      var fill = dlg.querySelector('.agmf-artd-progress > i');
      scroller.addEventListener('scroll', function () {
        var max = scroller.scrollHeight - scroller.clientHeight;
        fill.style.width = (max > 0 ? Math.min(100, (scroller.scrollTop / max) * 100) : 0) + '%';
      }, { passive: true });
      document.body.appendChild(dlg);
      document.documentElement.classList.add('agmf-noscroll');
    }

    // ---- ONE TAP, ONE MEANING (Dorian 2026-09-06: "double click on a review
    // both agilikes and opens the text; a double click should only agilike,
    // a single click opens") -------------------------------------------------
    // The agilike is read on pointerup: two taps on one piece within 320ms.
    // Every "open" used to fire on the FIRST click, so by the time the second
    // tap was recognised as a like, the text / clip / reader was already open.
    // Opens now wait 330ms - just past the like window - and a like cancels
    // them. A control (button, link, sound, play, bar) is not content and
    // stays immediate; only the content tap itself is deferred.
    var tapPending = null, tapDoubledAt = 0;
    function cancelTap() {
      if (tapPending) { clearTimeout(tapPending.timer); tapPending = null; }
    }
    function deferTap(host, fn) {
      if (Date.now() - tapDoubledAt < 450) return;       // the second half of a double
      cancelTap();
      tapPending = { host: host, timer: setTimeout(function () { tapPending = null; fn(); }, 330) };
    }
    // agmf-desktop-lightbox.js opens on its own click (capture phase); it
    // routes through the same arbiter when this global is present
    try { window.AGMF_deferTap = deferTap; } catch (e) {}
    // ---- yt interactions live at DOCUMENT level: accordion videos sit
    // outside the feed section, and section events bubble here anyway ----
    document.addEventListener('click', function (e) {
      var ysBtn = e.target.closest ? e.target.closest('[data-agmf-ytsound]') : null;
      if (ysBtn) {
        var ytHost = ysBtn.closest('[data-agmf-yt]');
        if (!ytHost) return;
        // the tap is a vote for THIS piece (see electAmong): it holds the
        // floor while any of it is in view, whichever piece shows more
        document.querySelectorAll('[data-agmf-yt]').forEach(function (s) { if (s !== ytHost) delete s.dataset.agmfAsked; });
        ytHost.dataset.agmfAsked = '1';
        // ONE intent for the whole feed - every shell mirrors it, and
        // manageVideos unmutes only the card actually on screen
        soundOn = !soundOn;
        document.querySelectorAll('[data-agmf-yt]').forEach(function (s) {
          s.dataset.agmfSound = soundOn ? 'on' : 'off';
          delete s.dataset.agmfMuteCmd;      // force the command to re-send
        });
        syncYtSoundUi();
        applySound();
        manageVideos();
        return;
      }
      var revCloseBtn = e.target.closest ? e.target.closest('[data-agmf-revopen-close]') : null;
      if (revCloseBtn) {
        var openOv = revCloseBtn.closest('.agmf-rev-open');
        if (openOv) openOv.remove();
        return;
      }
      var artCloseBtn = e.target.closest ? e.target.closest('[data-agmf-artopen-close]') : null;
      if (artCloseBtn) {
        var artOv = artCloseBtn.closest('.agmf-art-open');
        if (artOv) artOv.remove();
        document.documentElement.classList.remove('agmf-noscroll');
        return;
      }
      // the WHOLE article card opens the reader (Dorian 2026-08-12), not
      // just the READ button; buttons (carousel arrows) stay excluded, and
      // inside the open reader only its own controls act - links navigate.
      var artCard = e.target.closest ? e.target.closest('.agmf-card--article') : null;
      if (artCard && !e.target.closest('button')) {
        if (e.target.closest('.agmf-art-open')) {
          if (!e.target.closest('a')) { var artT1 = e.target; deferTap(artCard, function () { toggleArticleOpen(artCard, artT1); }); }
          return;
        }
        if (e.target.closest('a')) {
          if (e.ctrlKey || e.metaKey || e.shiftKey) return;   // native new-tab wins
          e.preventDefault();
        }
        var artT2 = e.target; deferTap(artCard, function () { toggleArticleOpen(artCard, artT2); });
        return;
      }
      var revCard = e.target.closest ? e.target.closest('.agmf-card--review') : null;
      if (revCard && revCard.__agmfItem && !e.target.closest('a, button')) {
        var revT = e.target; deferTap(revCard, function () { toggleReviewOpen(revCard, revT); });
        return;
      }
      // FULL SCREEN, ASKED FOR PROPERLY. The browser's own fullscreen is the
      // real thing, and it is what desktop and Android get. iPhones refuse it
      // on anything but a <video> - an iframe can never be element-fullscreen
      // there - so the CSS overlay we already have is the fallback, and it is
      // the same large view the press used to give.
      var fsBtn = e.target.closest ? e.target.closest('[data-agmf-ytfs]') : null;
      if (fsBtn) {
        var fsShell = fsBtn.closest('[data-agmf-yt]');
        if (fsShell) {
          if (document.fullscreenElement || document.webkitFullscreenElement) {
            (document.exitFullscreen || document.webkitExitFullscreen || function () {}).call(document);
          } else {
            var ask = fsShell.requestFullscreen || fsShell.webkitRequestFullscreen;
            var went = false;
            if (ask) {
              try {
                var out = ask.call(fsShell);
                went = true;
                // A RESOLVED CALL IS NOT A KEPT PROMISE. Safari accepts the
                // call and then refuses, so the overlay has to be reachable
                // from the failure as well as from the absence.
                if (out && out.catch) out.catch(function () { expandShell(fsShell); });
              } catch (err) { went = false; }
            }
            if (!went) expandShell(fsShell);
          }
        }
        return;
      }
      var closeBtn = e.target.closest ? e.target.closest('[data-agmf-ytclose]') : null;
      if (closeBtn) {
        collapseShell(closeBtn.closest('[data-agmf-yt]'));
        return;
      }
      // OUR play/pause. It is a real button, so it is the only thing a
      // press on it can mean - which is the whole point of drawing the
      // controls ourselves.
      var playBtn = e.target.closest ? e.target.closest('[data-agmf-ytplay]') : null;
      if (playBtn) {
        // THE SECOND HALF OF A DOUBLE PRESS IS NOT A SECOND INTENTION. The
        // guard that protects opening sits eleven branches downstream, so a
        // double press here ran two full toggles and the clip flickered back
        // to where it started.
        if (e.detail >= 2) return;
        var playShell = playBtn.closest('[data-agmf-yt]');
        var playFrame = playShell && playShell.querySelector('iframe');
        var wasPaused = playShell.dataset.agmfPaused === '1';
        var want = wasPaused ? 'playVideo' : 'pauseVideo';
        playShell.dataset.agmfPaused = wasPaused ? '' : '1';
        // SAY IT AGAIN, TWICE. A command sent before the player finishes its
        // handshake is dropped on the floor, and this press has no safety net:
        // the visibility pass re-sends at a 1.5s cadence only for the shells it
        // drives, and it never issues a command for a card that autoplayed
        // from its own URL (measured: agmfPlayCmd stays unset there). One
        // dropped command would leave the button saying Play over a video that
        // is not playing. Repeats are no-ops, so three is free.
        if (playFrame) {
          ytCommand(playFrame, want);
          [350, 1000].forEach(function (delay) {
            setTimeout(function () {
              if (!playShell.isConnected) return;
              // only if the shopper has not changed their mind since
              if ((playShell.dataset.agmfPaused === '1') !== (want === 'pauseVideo')) return;
              var f = playShell.querySelector('iframe');
              if (f) ytCommand(f, want);
            }, delay);
          });
        }
        // the pass state-diffs its commands; forget what it last sent or it
        // believes the player is already where this press just moved it
        delete playShell.dataset.agmfPlayCmd;
        // A PRESS OF PLAY IS A VOTE. The floor holds ONE playing clip, elected
        // by visible area, and until now only the speaker cast a vote for it -
        // so pressing play on a clip the engine had paused was taken straight
        // back on the next pass. Same two lines the speaker uses; this is the
        // MOTION floor, not the audio one, so nothing here can unmute.
        if (want === 'playVideo') {
          document.querySelectorAll('[data-agmf-yt]').forEach(function (o) {
            if (o !== playShell) delete o.dataset.agmfAsked;
          });
          playShell.dataset.agmfAsked = '1';
          playShell.dataset.agmfUserPlay = '1';
        }
        ytBezel(playShell, wasPaused ? 'play' : 'pause');
        playBtn.setAttribute('aria-label', wasPaused ? 'Pause' : 'Play');
        manageVideos();
        return;
      }
      // THE PAUSED-STATE BUTTON. Always play, never toggle: a clip the
      // engine paused (it scrolled out of the one-at-a-time slot) never had
      // `agmfPaused` set, so a toggle would have paused an already-paused
      // video and the button would have looked broken exactly where it is
      // needed most. The repeats are the same safety net the play button
      // carries - a command sent before the player finishes its handshake is
      // dropped on the floor.
      var resumeBtn = e.target.closest ? e.target.closest('[data-agmf-ytresume]') : null;
      if (resumeBtn) {
        var reShell = resumeBtn.closest('[data-agmf-yt]');
        if (!reShell) return;
        reShell.dataset.agmfPaused = '';
        delete reShell.dataset.agmfPlayCmd;   // the pass state-diffs; forget what it sent
        // the same vote the row's button casts - without it the visibility
        // pass pauses this clip again a moment later and the press looks broken
        document.querySelectorAll('[data-agmf-yt]').forEach(function (o) {
          if (o !== reShell) delete o.dataset.agmfAsked;
        });
        reShell.dataset.agmfAsked = '1';
        reShell.dataset.agmfUserPlay = '1';
        ytBezel(reShell, 'play');
        // A SHELL THAT WAITED TO BE ASKED HAS NO PLAYER YET. The spec panel's
        // video does not mount until it is pressed, so at this moment there is
        // nothing to send a command to: without this the press attached a
        // player through the pass below - sometimes CUED, because the pass
        // decides that from the link and the scroll - and the shopper was left
        // looking at a first frame with a pause button over it, started and
        // not playing (Dorian 2026-09-13: "its not playing but its not in
        // pause neither and its like it started"). Asked for by hand is never
        // cued: mount it here, uncued, and let the repeats below do the rest.
        // Mounting is attempted here AND inside each repeat below. attachYt
        // returns immediately when a player already exists, so the retries
        // cost nothing when the first one worked - and when it did not, for
        // whatever reason the page was in at that moment, the press still
        // ends in a playing video instead of a frozen first frame. Wrapped
        // because a press must never be the thing that throws.
        function armPlay() {
          var f = reShell.querySelector('iframe');
          if (!f) {
            try { attachYt(reShell, false); } catch (err) {}
            f = reShell.querySelector('iframe');
          }
          if (f) ytCommand(f, 'playVideo');
          return f;
        }
        var reFrame = armPlay();
        // The repeats are no longer conditional on a player existing WHEN THE
        // PRESS HAPPENED - they re-read the frame anyway, and a command sent
        // before the player finishes its handshake is dropped on the floor, so
        // the one that lands is usually one of these.
        [350, 1000, 2000].forEach(function (delay) {
          setTimeout(function () {
            if (!reShell.isConnected || reShell.dataset.agmfPaused === '1') return;
            armPlay();
          }, delay);
        });
        manageVideos();
        return;
      }
      var bar = e.target.closest ? e.target.closest('[data-agmf-ytbar]') : null;
      if (bar) {
        var barShell = bar.closest('[data-agmf-yt]');
        var barFrame = barShell && barShell.querySelector('iframe');
        var dur = barShell && parseFloat(barShell.dataset.agmfDur);
        if (barFrame && dur > 0) {
          var rect = bar.getBoundingClientRect();
          var frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
          ytCommand(barFrame, 'seekTo', [frac * dur, true]);
          var fill = bar.querySelector('.agmf-yt-bar-fill');
          if (fill) fill.style.width = (frac * 100) + '%';
          // A SEEK MEANS "SHOW ME THAT PART", so it resumes. While paused the
          // player is behind a cover (see the CSS note: a paused YouTube
          // iframe paints its own overlay), and seekTo leaves a paused player
          // paused - so without this you would be moving a picture nobody can
          // see, and the card would look stuck.
          if (barShell.dataset.agmfPaused === '1') {
            barShell.dataset.agmfPaused = '';
            delete barShell.dataset.agmfPlayCmd;
            ytCommand(barFrame, 'playVideo');
            var pressed = barShell.querySelector('[data-agmf-ytplay]');
            if (pressed) pressed.setAttribute('aria-label', 'Pause');
            barShell.dataset.agmfUserPlay = '1';
            ytBezel(barShell, 'play');
          }
        }
        return;
      }
      // A CONTROL IS NOT THE CARD (Dorian 2026-08-31: "the control of the
      // video dont work it put full screen"). The sound button, the progress
      // bar and the close X are handled by their own listeners, which return
      // as soon as they act - but THIS listener is a separate one and still
      // ran, found the shell around the button, and expanded the clip. So a
      // click on any control never reaches the expand rule.
      if (e.target.closest && e.target.closest('.agmf-ytc, .agmf-sound, [data-agmf-ytsound], [data-agmf-ytbar], [data-agmf-ytclose], [data-agmf-ytplay], [data-agmf-ytfs], [data-agmf-ytresume]')) return;
      var ytShell = e.target.closest ? e.target.closest('[data-agmf-yt]') : null;
      if (ytShell && !ytShell.querySelector('iframe')) {
        deferTap(ytShell, function () { if (ytShell.isConnected && !ytShell.querySelector('iframe')) attachYt(ytShell); });
        return;
      }
      // tapping a Short expands it — a CSS overlay, not element-fullscreen,
      // because iPhones only allow the latter on <video>
      if (ytShell && ytShell.classList.contains('agmf-yt-fullscreen')) {
        // already expanded: a tap on the black margin (the iframe swallows
        // its own clicks) is the other way out. But a DOUBLE click sends two
        // clicks in a row - the first opened it, and the second was closing
        // it again a moment later, so a double tap looked like a flash
        // (Dorian 2026-08-24). The second click of a pair, and anything
        // within 600ms of opening, is ignored.
        if (e.detail >= 2) return;
        if (Date.now() - (ytShell.__agmfExpandedAt || 0) < 600) return;
        deferTap(ytShell, function () { collapseShell(ytShell); });
        return;
      }
      // opening is a SINGLE click only - the second half of a double click
      // must never re-enter
      if (ytShell && e.detail >= 2) return;
      // ── ON DESKTOP A CLICK PLAYS OR PAUSES (Dorian 2026-08-31: "the
      //    controls doesnt work on youtube vids on desktop") ───────────────
      // The feed here is a grid of slots, not a stack you swipe, so a clip
      // filling the screen is not what a click means - but with expanding
      // switched off it meant NOTHING, and the video sat there ignoring the
      // mouse. Measured: the embed carries `controls=0` and `.agmf-ytshield`
      // covers all 484x645 of it, so a click never reaches the player either.
      //
      // Both were tried. YouTube's own bar answers the second at the cost of
      // the first - it owns every pixel of the player, so the picture stops
      // opening - and it also re-fights applySound(), which elects ONE audio
      // holder for the page and re-asserts mute every pass. Our own row keeps
      // all three: the picture opens, a control works where a control is, and
      // the sound stays ours.
      // DESKTOP: the dialog is the large view, and agmf-desktop-lightbox.js
      // has already claimed this click (capture phase). Nothing to do here.
      if (document.documentElement.classList.contains('agdesk-on')) return;
      // PHONE: THE TAP PLAYS OR PAUSES (Dorian 2026-09-10: "in mobile when
      // clicking one time on the video it should also play/pause it"). What it
      // no longer does is fill the screen - that was the gesture he cancelled,
      // because it fired instead of whatever control was being aimed at, the
      // same complaint as 2026-08-31 and 2026-09-06. Full screen is a button
      // now; the tap is free to mean the obvious thing.
      //
      // Desktop can let the press fall through to YouTube's own gesture in the
      // middle of the frame. A phone should not depend on that: the embed's
      // touch behaviour under `controls=0` is YouTube's to change and cannot be
      // tested from here, and a tap that silently does nothing is the very
      // complaint being fixed. So on a phone the shield keeps the tap and we
      // decide what it means.
      //
      // It means the card's OWN play/pause button - pressed, not re-implemented,
      // so this shares the bezel, the three command repeats that survive a
      // handshake race, the floor vote and the label with the button itself.
      // A synthesised press carries detail 0, so that branch's double-press
      // guard lets it through.
      if (ytShell) {
        var tapPlay = ytShell.querySelector('[data-agmf-ytplay]');
        if (tapPlay) deferTap(ytShell, function () { tapPlay.click(); });
      }
      return;
    });

    // ---- LEAVING FOR YOUTUBE (Dorian 2026-09-09: "how can we track if
    // people clicked the youtube button and leave our page") ---------------
    // A click inside a cross-origin player is never visible out here, so this
    // cannot be observed directly - it is INFERRED, from the only two things
    // the browser does tell us: focus moved into the player, and then the page
    // went away. Either the tab was hidden (the wordmark and the title open a
    // new tab) or the page unloaded (a same-tab navigation).
    //
    // What it is worth: focus only reaches the iframe when somebody pressed
    // something in it, so a departure in the seconds after is almost always a
    // press on the title, the channel or the wordmark. It is a strong reading,
    // not a certainty, and it is reported under a name that says so.
    // It cannot fire on our own links or the back button, because the focused
    // element then is not the player.
    (function () {
      var LEAD_MS = 8000;
      var touched = null;      // {frame, at} - the last player the shopper pressed
      function ytFrame(el) {
        return el && el.tagName === 'IFRAME' && /youtube/.test(el.src || '') ? el : null;
      }
      function noteFocus() {
        var f = ytFrame(document.activeElement);
        if (f) touched = { frame: f, at: Date.now() };
      }
      window.addEventListener('blur', noteFocus, true);
      document.addEventListener('focusout', noteFocus, true);
      var told = {};
      function leaving(how) {
        if (!touched || Date.now() - touched.at > LEAD_MS) return;
        var frame = touched.frame;
        if (!frame || !frame.isConnected) return;
        var shell = frame.closest ? frame.closest('[data-agmf-yt]') : null;
        var id = shell ? shell.getAttribute('data-agmf-yt') : null;
        if (!id || told[id + how]) return;
        told[id + how] = 1;
        var host = (shell && shell.closest && (shell.closest('.agmf-card') || shell.closest('.agmf-hero-slide'))) || shell;
        var item = null;
        try { item = window.AGMF && window.AGMF.itemFor ? window.AGMF.itemFor(host) : null; } catch (e) {}
        try {
          (host || document).dispatchEvent(new CustomEvent('agmf:yt_exit', {
            bubbles: true,
            detail: {
              video_id: id,
              how: how,                       // 'new tab' | 'left the page'
              ck: (item && item.ck) || (host && host.dataset ? host.dataset.agmfCk : null) || null,
              record_id: item && item.id != null ? String(item.id) : null,
              content_type: (item && item.contentType) || (host && host.dataset ? host.dataset.agmfType : null) || null,
              ms_watched: Math.round(parseFloat((shell && shell.dataset.agmfAt) || 0) * 1000) || null
            }
          }));
        } catch (e) {}
      }
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') leaving('new tab');
      });
      window.addEventListener('pagehide', function () { leaving('left the page'); });
    }());

    // ---- AN AGILIKE (Dorian 2026-09-03) --------------------------------
    // "When double cliking a content it counts as an Agilike, so you display
    //  a heart with a scorpion logo and you count it in AM as one agilike
    //  with the record of who liked etc - so we can see that visitor x has
    //  agiliked this content and this etc."
    //
    // The gesture was already here and did one thing: draw a white heart and
    // forget. Now it is a RECORD. Three changes:
    //   - the heart carries the scorpion, so what is drawn is the brand's
    //     like and not a generic one;
    //   - it works on any content, not only a feed card: the product card's
    //     slides and the desktop dialog are content too;
    //   - it announces itself as `agmf:agilike` on the element. The engine
    //     sends nothing itself - agmf-track.js hears that event and puts it
    //     on the wire with the visitor and session the analytics envelope
    //     already carries, which is the "who liked" half of the ask.
    // Once per piece per page: a second double-tap re-plays the heart (it
    // should feel alive) but does not send a second record.
    var agilikeSent = {};
    var lastTap = 0, lastTapCard = null;
    function agilikeHeartInto(host) {
      var heart = qs(host, '.agmf-heart');
      if (!heart) {
        heart = document.createElement('div');
        heart.className = 'agmf-heart';
        heart.setAttribute('aria-hidden', 'true');
        heart.innerHTML =
          '<svg class="agmf-heart-shape" viewBox="0 0 24 24" aria-hidden="true">' +
          '<path d="M12 21s-7.5-4.7-9.3-9.1C1.3 8.4 3.4 5 6.8 5c2 0 3.5 1.1 5.2 3.1C13.7 6.1 15.2 5 17.2 5c3.4 0 5.5 3.4 4.1 6.9C19.5 16.3 12 21 12 21z"/></svg>' +
          (scorpionUrl ? '<img class="agmf-heart-mark" src="' + esc(scorpionUrl) + '" alt="" aria-hidden="true">' : '');
        host.appendChild(heart);
      }
      heart.classList.remove('agmf-heart--show');
      void heart.offsetWidth;
      heart.classList.add('agmf-heart--show');
    }
    function agilike(host) {
      agilikeHeartInto(host);
      var item = null;
      try { item = window.AGMF && window.AGMF.itemFor ? window.AGMF.itemFor(host) : null; } catch (e) {}
      var key = (item && item.ck) || host.dataset.agmfCk || host.dataset.agmfTrackCk || '';
      if (!key || agilikeSent[key]) return;      // the heart replays, the record does not
      agilikeSent[key] = 1;
      try {
        host.dispatchEvent(new CustomEvent('agmf:agilike', {
          bubbles: true,
          detail: {
            ck: key,
            record_id: item && item.id != null ? String(item.id) : (host.dataset.agmfId || null),
            content_type: (item && item.contentType) || host.dataset.agmfType || host.dataset.agmfTrackType || null,
            platform: (item && item.platform) || null
          }
        }));
      } catch (e) {}
    }
    document.addEventListener('pointerup', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (!e.target.closest) return;
      var host = e.target.closest('.agmf-card, .agmf-hero-slide, .agmf-lb-stage');
      if (!host) return;
      // a panel, a suggestion tile, the Ask card, the delivery card and
      // anything else with its own job are not content (Dorian 2026-09-07:
      // "an AI card shouldn't be agilikeable"). The desktop lightbox needs no
      // equivalent entry for agmf-dcard: it only opens when the click lands on
      // .agmf-media/img/video, and the delivery card has none of those.
      if (host.classList.contains('agmf-spec') || host.classList.contains('agmf-suggest') ||
          host.classList.contains('agmf-ask') || host.classList.contains('agmf-dcard') ||
          host.classList.contains('agmf-scard')) return;
      if (e.target.closest('button, a, input, .agmf-sugg-row, .agmf-sugg-tile, .agmf-acc, ' +
                           '.agmf-ytc, .agmf-yt-bar, .agmf-sound, [data-agmf-ytsound], [data-agmf-ytclose]')) return;
      var now = Date.now();
      if (host === lastTapCard && now - lastTap <= 320) {
        tapDoubledAt = now;      // the open this pair's first tap scheduled is off
        cancelTap();
        agilike(host);
        lastTap = 0; lastTapCard = null;
      } else {
        lastTap = now; lastTapCard = host;
      }
    }, { passive: true });

    // ---- the recycler (videos released before teardown) ----
    function recycle() {
      var vTop = window.scrollY;
      var vBottom = vTop + window.innerHeight;
      var margin = window.innerHeight * 3;
      var fold = foldBottom();
      cycles.forEach(function (c) {
        var top = c.el.getBoundingClientRect().top + vTop;
        if (!c.reached && top - vTop < fold) c.reached = true;   // see heroShowedItem
        var height = c.materialized ? c.el.offsetHeight : c.height;
        var near = (top < vBottom + margin) && (top + height > vTop - margin);
        if (c.materialized && !near) {
          c.height = c.el.offsetHeight;
          releaseVideos(c.el);
          releasePlaced(c.el);
          c.el.style.height = c.height + 'px';
          c.el.classList.add('agmf-recycled');
          c.el.replaceChildren();
          c.materialized = false;
        } else if (!c.materialized && near) {
          c.el.appendChild(buildCycleContent(c.index));
          balanceCycle(c.el);
          c.el.style.height = '';
          c.el.classList.remove('agmf-recycled');
          c.materialized = true;
        }
      });
    }

    // ---- session + resume ----
    function readSession() {
      try {
        var raw = sessionStorage.getItem(sessionKey);
        if (!raw) return null;
        var st = JSON.parse(raw);
        if (!st || typeof st.offset !== 'number') return null;
        if (Date.now() - (st.at || 0) > SESSION_TTL_MS) { sessionStorage.removeItem(sessionKey); return null; }
        return st;
      } catch (e) { return null; }
    }

    function sectionTop() { return section.getBoundingClientRect().top + window.scrollY; }

    function saveSession() {
      try {
        var offset = window.scrollY - sectionTop();
        if (offset < 40) return;
        var thumb = '';
        var media = feedEl.querySelectorAll('img, video[poster]');
        for (var i = 0; i < media.length; i++) {
          var r = media[i].getBoundingClientRect();
          if (r.top >= 0 && r.top < window.innerHeight) {
            thumb = media[i].tagName === 'VIDEO'
              ? (media[i].getAttribute('poster') || '')
              : (media[i].currentSrc || media[i].src || '');
            break;
          }
        }
        sessionStorage.setItem(sessionKey, JSON.stringify({
          offset: Math.round(offset), at: Date.now(), thumb: thumb,
        }));
      } catch (e) { /* storage unavailable — resume simply won't offer */ }
    }

    function updateResumeBar() {
      if (!resumeBar) return;
      var st = readSession();
      var above = section.getBoundingClientRect().top > window.innerHeight * 0.5;
      var show = !!st && above;
      resumeBar.hidden = !show;
      if (show) {
        var thumbEl = qs(resumeBar, '[data-agmf-resume-thumb]');
        if (thumbEl && st.thumb) thumbEl.style.backgroundImage = 'url("' + st.thumb.replace(/"/g, '') + '")';
        // TTB's floating banner owns bottom:0 (z-index 998); sit above it.
        // NOT offsetParent — always null for position:fixed elements.
        var lift = 0;
        var ttb = document.getElementById('ttb-floating-banner');
        if (ttb) {
          var box = ttb.getBoundingClientRect();
          if (box.height > 0 && getComputedStyle(ttb).display !== 'none') lift = box.height + 8;
        }
        var sticky = document.querySelector('[data-agmf-sticky]');
        if (sticky && !sticky.hidden) {
          var sb = sticky.getBoundingClientRect();
          if (sb.height > 0) lift += sb.height + 8;
        }
        resumeBar.style.setProperty('--agmf-resume-lift', lift + 'px');
      }
    }

    if (resumeBar) {
      resumeBar.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('[data-agmf-resume-close]')) {
          try { sessionStorage.removeItem(sessionKey); } catch (err) { /* ignore */ }
          resumeBar.hidden = true;
          return;
        }
        var st = readSession();
        if (!st) { resumeBar.hidden = true; return; }
        // As a DELTA from where the section is now: sectionTop() is a document
        // position, and inside a pane the document is not what moves.
        var delta = section.getBoundingClientRect().top + st.offset;
        // long jumps are instant: smooth over feed distances is rAF-driven and slow
        var far = Math.abs(delta) > window.innerHeight * 3;
        agmfScrollBy(section, delta, !(far || prefersReduced()));
        onScroll();
      });
    }

    if (returnBtn) {
      returnBtn.addEventListener('click', function () {
        saveSession();
        // To the very TOP of the PDP — the strip is the product card now
        // (Dorian 2026-08-11), so tapping it lands on the product itself.
        // Long jumps go instant: smooth scrolling over feed distances never
        // completes.
        var host = agmfScroller(section);
        var far = (host ? host.scrollTop : window.scrollY) > window.innerHeight * 3;
        agmfScrollToStart(section, !(far || prefersReduced()));
      });
    }
    // THE STRIP NAMES THE VARIANT ON THE PAGE (Dorian 2026-10-11: "Make sure
    // we have a correct thumbnail"). The return strip - thumb, title, price -
    // was painted once by Liquid from the product's default and changed only
    // when a choice was made in OUR sheet. Measured on the preview (five
    // pages, phone): a colour clicked on the page itself, a builder swatch,
    // colour-pref's Black - the strip kept the default picture AND its
    // price ($611.91 for the $593.91 Black bundle). It now follows every
    // variant:change of this product, and opens on the variant the page
    // holds. A variant with no picture of its own keeps the one shown.
    if (returnBtn) (function stripFollowsVariant() {
      var pd = null;
      try { pd = JSON.parse(document.querySelector('[data-agmf-product-data]').textContent); } catch (err) { return; }
      if (!pd || !pd.variants || !pd.variants.length) return;
      var byId = {};
      pd.variants.forEach(function (v) { byId[String(v.id)] = v; });
      var thumb = returnBtn.querySelector('.agmf-return-thumb');
      var price = returnBtn.querySelector('.agmf-return-price');
      function show(v) {
        if (!v) return;
        if (thumb && v.image) thumb.style.backgroundImage = 'url("' + v.image + '")';
        if (price && v.price) price.textContent = v.price;
      }
      // what the page holds, read in the sheet's order (initDrawer's
      // pageVariant): the picker's own form - what Add to cart adds - then the
      // product bridge (a gear builder's base item, which can lag), then any
      // add-to-cart form; only this product's variants count
      function held() {
        var ids = [];
        var picker = document.querySelector('variant-picker, .variant-picker');
        var formId = picker && picker.getAttribute('form');
        var form = (formId && document.getElementById(formId)) || (picker && picker.closest && picker.closest('form'));
        var own = form && form.querySelector('[name="id"]');
        if (own) ids.push(own.value);
        var bridge = window.AGMF_PRODUCT_BRIDGE;
        if (bridge && typeof bridge.getSelectedVariantId === 'function') {
          try { ids.push(bridge.getSelectedVariantId()); } catch (err) { /* the forms answer */ }
        }
        var forms = document.querySelectorAll('form[action*="/cart/add"] [name="id"]');
        for (var f = 0; f < forms.length; f++) ids.push(forms[f].value);
        for (var i = 0; i < ids.length; i++) {
          if (ids[i] != null && byId[String(ids[i])]) return byId[String(ids[i])];
        }
        return null;
      }
      // another product's variant (a quick add, a suggestion) is not ours
      document.addEventListener('variant:change', function (e) {
        var v = e && e.detail && e.detail.variant;
        show(v && v.id != null ? byId[String(v.id)] : null);
      });
      show(held());
    }());

    if (topBtn) {
      topBtn.addEventListener('click', function () {
        saveSession();
        // "Back to top" meant the window, which on desktop is already at the
        // top and stays there - the button did nothing at all.
        agmfScrollToStart(section, !prefersReduced());
      });
    }

    // THE PRODUCT'S NAME IS A WAY BACK TOO (Dorian 2026-09-10: "when clicking
    // the name of the product, it should go back to the top also on the feed
    // on the left side").
    //
    // On desktop the feed scrolls in its own host beside the product, so
    // sending the window to the top leaves it where it was - the same reason
    // the top button had to stop calling window.scrollTo. This asks for the
    // one answer that already knows about both.
    //
    // DESKTOP ONLY, as asked: on one column the title is already what you are
    // looking at, and jumping the page under what was probably the start of a
    // scroll would be worse than doing nothing. Read per click, so turning a
    // phone sideways is not a stale decision.
    var productTitle = document.querySelector('.product-title');
    if (productTitle) {
      productTitle.addEventListener('click', function () {
        try {
          if (!window.matchMedia('(min-width: 990px)').matches) return;
        } catch (err) {
          return;                       // no matchMedia: leave the page alone
        }
        saveSession();
        var host = agmfScroller(section);
        var far = (host ? host.scrollTop : window.scrollY) > window.innerHeight * 3;
        agmfScrollToStart(section, !(far || prefersReduced()));
      });
      // It does something when clicked, so it has to look like it does.
      productTitle.classList.add('agmf-title-return');
    }

    // hand the module-scope rail code the two things it cannot see
    onRailSettled = function () { manageVideos(); };

    // A BLOCK THAT LOST A CARD GETS ONE BACK (see mediaDead). Desktop grid
    // only - one column has no row to leave half empty. The block is short
    // exactly when its one-cell cards are odd (the same count the lone-card
    // CSS makes), so one more one-cell card closes it: dense flow puts it in
    // the hole. The replacement is a STILL - never a video, so the "never two
    // moving things in a row" rule cannot break - taken from the part of the
    // stream not dealt yet and marked placed, so it is shown here INSTEAD of
    // later rather than twice. Nothing suitable left: the lone card stays, on
    // its backdrop (pdp-desk-3col.css).
    onFeedCardDead = function (cycleEl) {
      if (!cycleEl || !cycleEl.isConnected || columns() < 2 || !order.length) return;
      var singles = 0;
      [].forEach.call(cycleEl.children, function (c) {
        if (c.classList.contains('agmf-card') && !c.hasAttribute('data-agmf-span') &&
            !c.matches('.agmf-ask, .agmf-suggest, .agmf-spec')) singles++;
      });
      if (singles % 2 === 0) return;
      // one lap ahead, or the whole pool where shares make a lap shorter
      for (var b = streamCursor, end = streamCursor + Math.max(order.length, poolLen); b < end; b++) {
        var it = baseAt(b);
        if (!it || it.cardOnly || it.video || !it.thumb || spansRow(it)) continue;
        if (it.ck && (deadKeys[it.ck] || heroShown[it.ck] || placedCks[it.ck])) continue;
        if (it.ck && document.querySelector('.agmf-card[data-agmf-ck="' + String(it.ck).replace(/["\\]/g, '\\$&') + '"]')) continue;
        var card = renderItem(it);
        if (!card) continue;
        // stamped as buildCycleContent stamps, so every lookup by idx/lap
        // (rails, the lightbox, AGMF.itemFor) resolves to this item
        card.dataset.agmfIdx = String(b % order.length);
        card.dataset.agmfLap = String(Math.floor(b / order.length));
        // marked, so a block torn down later gives its place back (releasePlaced)
        if (it.ck) { card.dataset.agmfCk = String(it.ck); card.dataset.agmfPlaced = '1'; placedCks[it.ck] = 1; }
        if (it.contentType) card.dataset.agmfType = String(it.contentType);
        if (it.id) card.dataset.agmfId = String(it.id);
        cycleEl.appendChild(card);
        balanceCycle(cycleEl);
        manageVideos();
        return;
      }
    };
    railReducedMotion = function () { return prefersReduced(); };

    function prefersReduced() {
      try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
      catch (e) { return false; }
    }

    // ---- related carousels: every card is slidable as soon as it appears.
    // Affordable because slide media is LAZY (videos attach src only near the
    // viewport; images are loading=lazy) and the recycler drops far cards.
    var RELATED_COUNT = 6;
    var activated = typeof WeakSet !== 'undefined' ? new WeakSet() : { has: function(){return false;}, add: function(){} };

    function activateNearby() {
      var vh = window.innerHeight || 1;
      feedEl.querySelectorAll('.agmf-card:not(.agmf-card--slidable):not(.agmf-suggest):not(.agmf-spec)')
        .forEach(function (c) {
          var r = c.getBoundingClientRect();
          if (r.top < vh * 1.6 && r.bottom > -vh * 0.5) activateCarousel(c);
        });
    }

    function mostVisibleCard() {
      var vh = window.innerHeight || 1;
      var winner = null, best = 0;
      feedEl.querySelectorAll('.agmf-card').forEach(function (c) {
        var r = c.getBoundingClientRect();
        var vis = Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0));
        if (vis > best) { best = vis; winner = c; }
      });
      return best > vh * 0.45 ? winner : null;
    }

    function activateCarousel(card) {
      if (!card || activated.has(card) || card.classList.contains('agmf-suggest') ||
          card.classList.contains('agmf-spec')) return;
      activated.add(card);
      var oi = parseInt(card.dataset.agmfIdx, 10);
      var lap = parseInt(card.dataset.agmfLap, 10);
      if (isNaN(lap)) lap = 0;
      // resolve against the SAME lap order the card was rendered from, or the
      // rail is built for a different item than the one on screen
      var lapOrder = orderForLap(lap);
      var item = lapOrder[isNaN(oi) ? -1 : oi];
      if (!item) return;
      var lane = laneOf(item);
      // a long-form video is ONE decision, not a rail (Dorian 2026-08-12):
      // yt videos never carouselize; Shorts keep their slidable rail
      if (lane.indexOf('yt-video') === 0) return;
      var pool = railPoolFor(item, oi, lap, lapOrder);
      // a rail is only worth it with enough UNSEEN siblings: a lane of five
      // product renders became a carousel of the same five renders on every
      // one of those cards (Dorian 2026-08-12)
      if (pool.length < 3) return;
      // studio product renders are a set, not a story - the hero already
      // shows every colourway; rails of them read as duplicates in the feed
      if (lane === 'image-Assets') return;

      var track = document.createElement('div');
      track.className = 'agmf-track';
      // INFINITE loop (Dorian 2026-08-11): edge CLONES make both directions
      // endless — sliding LEFT from the start lands on the last same-kind
      // item, and past the last item the loop returns to the first. Settling
      // on a clone teleports (instant, invisible) to its real twin.
      var lead = document.createElement('div');
      lead.className = 'agmf-slide agmf-slide--clone';
      lead.appendChild(renderItem(pool[pool.length - 1]));
      track.appendChild(lead);
      var first = document.createElement('div');
      first.className = 'agmf-slide';
      while (card.firstChild) first.appendChild(card.firstChild);
      // The card is a CONTAINER of many items now, not one item, so its loop
      // stamp moves down to the slide that actually holds that clip. Left on
      // the outer card it sits above every sibling slide, and auto-advance -
      // which asks `closest('[data-agmf-loop]')` - would read the whole
      // carousel as a loop and never move off any of them.
      if (card.dataset.agmfLoop) {
        first.dataset.agmfLoop = '1';
        delete card.dataset.agmfLoop;
      }
      track.appendChild(first);
      pool.forEach(function (it) {
        var slide = document.createElement('div');
        slide.className = 'agmf-slide';
        slide.appendChild(renderItem(it));
        track.appendChild(slide);
      });
      var tailClone = document.createElement('div');
      tailClone.className = 'agmf-slide agmf-slide--clone';
      tailClone.appendChild(renderItem(item));
      track.appendChild(tailClone);
      ownHorizontalGesture(track);
      track.addEventListener('scroll', function () {
        // horizontal scrolls never fire the WINDOW scroll listener, so the
        // video lifecycle stalled mid-swipe: slide 2 sat paused pre-frame
        // (black) and slide 3+ never attached until a vertical nudge
        // (Dorian 2026-08-12). Drive it from the track itself, rAF-throttled.
        if (!track.__agmfMv) {
          track.__agmfMv = requestAnimationFrame(function () {
            track.__agmfMv = 0;
            manageVideos();
          });
        }
        if (track.__agmfSettle) clearTimeout(track.__agmfSettle);
        track.__agmfSettle = setTimeout(function () {
          var w = track.clientWidth || 1;
          var idx = railIndex(track);
          // a hard flick used to fly past several clips - walk back to a
          // single step from where the gesture started
          var from = track.__agmfFrom;
          if (typeof from === 'number' && Math.abs(idx - from) > 1) {
            idx = from + (idx > from ? 1 : -1);
            track.__agmfFrom = idx;
            track.scrollTo({ left: railOffset(track, idx), behavior: 'smooth' });
            return;   // that correction settles again and re-checks the edges
          }
          track.__agmfFrom = idx;
          // ── A WRAP IS NOT A FLICK ──────────────────────────────────────
          // The jump below moves the rail the whole width of the track, and
          // the scroll it causes brings us straight back here. With
          // `__agmfFrom` still pointing at the edge we just left, the guard
          // above read that as a hard flick and "walked it back to a single
          // step" - a SMOOTH scroll across every slide, landing on the one
          // the shopper started from. Swipe left from the first card and it
          // ran through the whole rail and came home (Dorian 2026-08-31).
          // Traced on the live build: idx 0 -> jump to 7 -> next settle sees
          // from=0, sets __agmfFrom=1 and scrolls there.
          //
          // So the destination is recorded BEFORE the jump. The settle that
          // follows then sees idx and from agreeing and leaves the rail alone.
          //
          // Counted in the same space as railIndex/railOffset - VISIBLE
          // slides, not raw children. Those agree today and stop agreeing the
          // moment anything in the track is display:none, which is exactly
          // what the desktop layout does to the clones.
          var visible = railCount(track);
          var lastReal = visible - 2;          // ...the trailing clone is last
          if (idx <= 0 && lastReal >= 1) {
            track.__agmfFrom = lastReal;
            track.scrollTo({ left: railOffset(track, lastReal), behavior: 'auto' });
          } else if (idx >= visible - 1) {
            track.__agmfFrom = 1;
            track.scrollTo({ left: railOffset(track, 1), behavior: 'auto' });
          }
        }, 130);
      }, { passive: true });
      // (the arrow cue pill was removed on Dorian's direction 2026-08-12 -
      // the 2s dwell tease is the only slide hint)
      card.appendChild(track);
      card.insertAdjacentHTML('beforeend',
        '<button type="button" class="agmf-arrow agmf-arrow--prev" data-agmf-arrow="-1" aria-label="Previous">&#8249;</button>' +
        '<button type="button" class="agmf-arrow agmf-arrow--next" data-agmf-arrow="1" aria-label="Next">&#8250;</button>');
      card.classList.add('agmf-card--slidable');
      // start on the REAL first slide, one width past the leading clone
      track.scrollLeft = railOffset(track, 1);
      manageVideos();
    }



    // ---- gear-suggestions card: the panel's own accessory picks, as a card ----
    var suggestBuilt = false;
    var specBuilt = false;
    var specPanel = null;
    var productReviews = null;
    var heroImages = null;
    var heroPinned = false;
    var heroFeedTypesOff = [];
    // THE PHONE'S CARD KEEPS GOING (Dorian 2026-09-02). Agilite Media's card
    // layout may end with an ENDLESS opening naming content types; after the
    // arranged slides the card keeps offering those, drawn from the feed, for
    // as long as the shopper swipes. Absent = the card ends where it ends.
    var cardEndless = null;
    // WHAT THE CARD HAS SHOWN THIS PAGE, by content key. A piece the shopper
    // swiped into up there must not come round again in the feed below - the
    // one rule Dorian set for it. Page-scoped on purpose: across visits the
    // seen ledger + feed.seenPolicy decide, with the settings he chose.
    var heroShown = {};
    // Kept for the SESSION (sessionStorage, like scroll-resume): a reload
    // must not bring back into the feed what the card showed a minute ago,
    // and on a resumed page the first cycles are planned with it already
    // known - no rewind needed.
    var HERO_SHOWN_KEY = 'AGMF_HeroShown:' + handle;
    function readHeroShown() {
      try {
        var raw = sessionStorage.getItem(HERO_SHOWN_KEY);
        var list = raw ? JSON.parse(raw) : [];
        var out = {};
        if (Array.isArray(list)) list.forEach(function (ck) { if (ck) out[String(ck)] = 1; });
        return out;
      } catch (err) { return {}; }
    }
    function saveHeroShown() {
      try { sessionStorage.setItem(HERO_SHOWN_KEY, JSON.stringify(Object.keys(heroShown).slice(-400))); }
      catch (err) { /* private mode: the page still works, the rule just does not survive a reload */ }
    }
    // The feed is not allowed to shrink to a handful of unshown items dealt
    // over and over. Below this many unshown pieces the stream stops
    // skipping - the same instinct as feed.seenPolicy's floor.
    var STREAM_FLOOR = 12;
    // poolLen: every piece the stream can deal - the order, or with shares
    // every piece of every product (buildOrder) - which the floor counts from
    var orderCks = {}, shownInOrder = 0, poolLen = 0;
    function countShownInOrder() {
      var n = 0;
      for (var ck in heroShown) if (orderCks[ck]) n++;
      shownInOrder = n;
    }

    // ONE cached product-JSON fetch for every suggest surface. `_fd=0` stops
    // Shopify's myshopify->primary-domain redirect (cross-origin = CORS wall
    // on the preview; the retry storm then hit 429). Failures cache too - a
    // drawer mutation must never refetch a dead handle (Dorian 2026-08-12).
    var productJsCache = {};
    function fetchProductJs(h) {
      if (!productJsCache[h]) {
        productJsCache[h] = fetch('/products/' + encodeURIComponent(h) + '.js?_fd=0',
          { credentials: 'same-origin' }).then(function (r) {
            return r.ok ? r.json() : null;
          });
        productJsCache[h].catch(function () { /* cached rejection */ });
      }
      return productJsCache[h];
    }

    // THE LIVE GEAR BUILDER OWNS THIS JOB NOW (Dorian 2026-08-31): the
    // theme already renders the easy-suggestion widget directly under Add to
    // cart, and that is where he wants it - so the feed no longer builds its
    // own copy. The function stays, unused, because the card it makes is
    // wired to the panel data and re-enabling it is one call.
    var SUGGEST_CARD_IN_FEED = false;

    function buildSuggestCard() {
      if (!SUGGEST_CARD_IN_FEED) return;
      if (suggestBuilt) return;
      if (!feedEl.querySelector('.agmf-cycle')) return;   // boot re-calls after cycles exist
      var gs = window.__AGB_GEAR_SUGG;
      var entry = gs && gs.byProduct && gs.byProduct[handle];
      var accs = (entry && entry.accessories) || [];
      if (!accs.length) return;
      suggestBuilt = true;
      var card = document.createElement('article');
      card.className = 'agmf-card agmf-suggest';
      card.innerHTML =
        '<div class="agmf-setup-head">' +
        '<p class="agmf-setup-eyebrow">Recommended</p>' +
        '<h3 class="agmf-setup-title">Complete Your Setup</h3>' +
        '<p class="agmf-setup-intro">Picked for this product.</p>' +
        '</div>' +
        '<div class="agmf-sugg-pages" data-agmf-sugg-pages></div>' +
        '<div class="agmf-sugg-dots" data-agmf-sugg-dots role="tablist" aria-label="More gear pages"></div>' +
        '<div class="agmf-sugg-actions">' +
        '<button type="button" class="agmf-sugg-add" data-agmf-sugg-add disabled>ADD SELECTED</button>' +
        '</div>';
      var pagesEl = qs(card, '[data-agmf-sugg-pages]');
      var dotsEl = qs(card, '[data-agmf-sugg-dots]');
      var PER_PAGE = 4;

      function pageFor(i) {
        var n = Math.floor(i / PER_PAGE);
        var pages = pagesEl.querySelectorAll('.agmf-sugg-page');
        while (pages.length <= n) {
          var pg = document.createElement('div');
          pg.className = 'agmf-sugg-page';
          pagesEl.appendChild(pg);
          pages = pagesEl.querySelectorAll('.agmf-sugg-page');
        }
        return pages[n];
      }

      var lastSuggDots = null;
      function refreshDots() {
        var pages = pagesEl.querySelectorAll('.agmf-sugg-page');
        if (pages.length < 2) {
          if (lastSuggDots !== '') { lastSuggDots = ''; dotsEl.innerHTML = ''; }
          return;
        }
        var active = Math.round(pagesEl.scrollLeft / Math.max(1, pagesEl.clientWidth));
        var suggKey = active + '/' + pages.length;
        if (suggKey === lastSuggDots) return;
        lastSuggDots = suggKey;
        var html = '';
        for (var i = 0; i < pages.length; i++) {
          html += '<button type="button" class="agmf-sugg-dot' +
            (i === active ? ' agmf-sugg-dot--on' : '') +
            '" data-agmf-sugg-dot="' + i + '" aria-label="Page ' + (i + 1) + '"></button>';
        }
        dotsEl.innerHTML = html;
      }
      pagesEl.addEventListener('scroll', function () {
        if (pagesEl.__agmfDotRaf) return;
        pagesEl.__agmfDotRaf = requestAnimationFrame(function () {
          pagesEl.__agmfDotRaf = 0;
          refreshDots();
        });
      }, { passive: true });
      dotsEl.addEventListener('click', function (e) {
        var b = e.target.closest('[data-agmf-sugg-dot]');
        if (!b) return;
        pagesEl.scrollTo({ left: parseInt(b.getAttribute('data-agmf-sugg-dot'), 10) * pagesEl.clientWidth,
                           behavior: 'smooth' });
      });

      var addBtn = qs(card, '[data-agmf-sugg-add]');
      var pending = 0;

      function refreshAddBtn() {
        var n = card.querySelectorAll('input[data-agmf-variant]:checked').length;
        addBtn.disabled = n === 0;
        addBtn.textContent = n ? 'ADD SELECTED (' + n + ')' : 'ADD SELECTED';
        card.classList.toggle('agmf-has-sel', n > 0);
      }
      card.addEventListener('change', function (e) {
        var cb = e.target;
        if (cb && cb.classList && cb.classList.contains('agmf-sugg-check')) {
          var tile = cb.closest('.agmf-sugg-tile');
          if (tile) tile.classList.toggle('agmf-sugg-tile--on', cb.checked);
        }
        refreshAddBtn();
      });

      accs.slice(0, 12).forEach(function (a, ai) {
        var h = a && (a.handle || a.productHandle);
        if (!h) return;
        pending += 1;
        var tile = document.createElement('label');
        tile.className = 'agmf-sugg-tile';
        tile.innerHTML =
          '<input type="checkbox" class="agmf-sugg-check" hidden>' +
          '<span class="agmf-sugg-tile-media"></span>' +
          '<span class="agmf-sugg-tile-name"></span>' +
          '<span class="agmf-sugg-tile-price"></span>' +
          '<span class="agmf-sugg-tick" aria-hidden="true">&#10003;</span>';
        pageFor(ai).appendChild(tile);
        fetchProductJs(h)
          .catch(function () { return null; })
          .then(function (p) {
            pending -= 1;
            var v = null;
            if (p) {
              for (var i = 0; i < (p.variants || []).length; i++) {
                if (p.variants[i].available) { v = p.variants[i]; break; }
              }
              v = v || (p.variants && p.variants[0]);
            }
            if (!p || !v) { tile.remove(); }
            else {
              if (p.featured_image) qs(tile, '.agmf-sugg-tile-media').style.backgroundImage = 'url("' + p.featured_image + '")';
              qs(tile, '.agmf-sugg-tile-name').textContent = p.title || h;
              qs(tile, '.agmf-sugg-tile-price').textContent = '$' + (v.price / 100).toFixed(2);
              var cb = qs(tile, 'input');
              cb.dataset.agmfVariant = String(v.id);
              cb.disabled = !v.available;
            }
            refreshDots();
            if (pending === 0 && !pagesEl.querySelector('.agmf-sugg-tile')) card.remove();
          })
          .catch(function () {
            pending -= 1;
            tile.remove();
            refreshDots();
            if (pending === 0 && !pagesEl.querySelector('.agmf-sugg-tile')) card.remove();
          });
      });

      addBtn.addEventListener('click', function () {
        var checked = card.querySelectorAll('input[data-agmf-variant]:checked');
        if (!checked.length) return;
        var props = {};
        try {
          if (window.AGB_Provenance) {
            props = window.AGB_Provenance.props({ role: 'accessory', source: 'media-feed' });
          }
        } catch (err) { /* provenance never blocks a sale */ }
        var items = [].map.call(checked, function (cb) {
          return { id: Number(cb.dataset.agmfVariant), quantity: 1, properties: props };
        });
        addBtn.disabled = true;
        addBtn.textContent = 'ADDING\u2026';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items }),
        }).then(function (r) {
          if (r.ok) {
            addBtn.textContent = 'ADDED \u2713';
            [].forEach.call(checked, function (cb) {
              cb.checked = false;
              var tile = cb.closest('.agmf-sugg-tile');
              if (tile) tile.classList.remove('agmf-sugg-tile--on');
            });
            setTimeout(refreshAddBtn, 1400);
            try { document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (err) { /* best-effort */ }
          } else {
            addBtn.textContent = 'ADD SELECTED';
            addBtn.disabled = false;
          }
        }).catch(function () { addBtn.textContent = 'ADD SELECTED'; addBtn.disabled = false; });
      });

      var firstCycle = feedEl.querySelector('.agmf-cycle');
      if (firstCycle && firstCycle.nextSibling) feedEl.insertBefore(card, firstCycle.nextSibling);
      else feedEl.appendChild(card);
    }

    // ---- spec panel words in the PAGE's language ----
    // The baked panel carries the primary-language tabs plus, per published
    // locale, the same tabs in that language (specPanel.byLocale, built from
    // Shopify's Translate & Adapt). The URL's locale decides which set shows
    // - NOT the device language and NOT the shopper's country - because
    // Liquid renders the rest of this page in the URL's locale, and the
    // panel must read in the same language as the title and buttons around
    // it. A locale with no translated tabs falls back to the primary words,
    // so the panel never goes blank on a language nobody translated yet.
    function specTabs(panel) {
      var lang = ((window.Shopify && window.Shopify.locale) || document.documentElement.lang || '')
        .toLowerCase().split('-')[0];
      var loc = panel && panel.byLocale ? panel.byLocale[lang] : null;
      if (loc && loc.tabs && loc.tabs.length) return loc.tabs;
      return panel ? panel.tabs : null;
    }

    // ---- spec panel card: the description's tabbed table, library-served ----
    function buildSpecCard() {
      if (specBuilt || !specPanel) return;
      var tabs = specPanel.tabs;
      if (!tabs || !tabs.length) return;
      if (specPanel.handle && specPanel.handle !== handle) return;
      specBuilt = true;
      tabs = specTabs(specPanel);   // the page language's words, now that a primary panel is known to exist
      var card = document.createElement('article');
      card.className = 'agmf-card agmf-spec';
      var tabBtns = '';
      for (var i = 0; i < tabs.length; i++) {
        tabBtns += '<button type="button" class="agmf-spec-tab' +
          (i === 0 ? ' agmf-spec-tab--on' : '') +
          '" data-agmf-spec-tab="' + i + '" role="tab">' + esc(tabs[i].name) + '</button>';
      }
      card.innerHTML = postHead(specPanel.title || 'Product details', 'Everything about this product') +
        '<div class="agmf-spec-tabs" role="tablist">' + tabBtns + '</div>' +
        '<div class="agmf-spec-body" data-agmf-spec-body></div>';
      var body = qs(card, '[data-agmf-spec-body]');

      function renderTab(i) {
        var t = tabs[i];
        if (!t) return;
        body.innerHTML = panelTabHtml(t);
        card.querySelectorAll('[data-agmf-spec-tab]').forEach(function (btn) {
          btn.classList.toggle('agmf-spec-tab--on',
            btn.getAttribute('data-agmf-spec-tab') === String(i));
        });
        manageVideos();   // a Sizing-tab video is a managed yt shell like any other
      }

      card.addEventListener('click', function (e) {
        var b = e.target.closest ? e.target.closest('[data-agmf-spec-tab]') : null;
        if (b) renderTab(parseInt(b.getAttribute('data-agmf-spec-tab'), 10));
      });
      renderTab(0);

      // pinned early like the suggestions card, outside the recycler's reach
      var sugg = feedEl.querySelector('.agmf-suggest');
      var firstCycle = feedEl.querySelector('.agmf-cycle');
      var anchor = sugg || firstCycle;
      if (anchor && anchor.nextSibling) feedEl.insertBefore(card, anchor.nextSibling);
      else feedEl.appendChild(card);
      // ONLY now may the CSS hide the description's own table
      document.documentElement.classList.add('agmf-spec-live');
    }

    // ---- spec panel as TOP accordions (prototype agilite-mobile-product-
    // page-v9): rendered where the hidden description table sits. Only when
    // the accordions actually commit may the CSS hide the original table.
    function buildSpecAccordions() {
      if (document.querySelector('.agmf-acc-wrap')) return;
      var panel = specPanel;
      var panelTabs = panel && specTabs(panel);
      var nativePanel = window.AGMF_PRODUCT_SOURCES
        ? window.AGMF_PRODUCT_SOURCES.productTabs(document, handle) : null;
      // A published panel is an optional editorial/translation override. The
      // no-setup baseline is the product's own custom.* metafields, rendered
      // by the section in the page's language, with any old description tabs
      // beside them (AGMF_PRODUCT_SOURCES.productTabs, 2026-10-05).
      if (!panel || !panelTabs || !panelTabs.length || (panel.handle && panel.handle !== handle)) {
        panel = nativePanel;
        panelTabs = panel && specTabs(panel);
      }
      if (!panel || !panelTabs || !panelTabs.length) return;
      specPanel = panel;
      var anchorEl = panel.native && panel.anchor
        ? panel.anchor
        : (nativePanel && nativePanel.anchor) || document.querySelector('.tab-customize, .product-info [data-block-type="description"], .product-info [data-block-type="accordion"]');
      if (!anchorEl || !anchorEl.parentNode) return;   // no table -> feed card serves alone
      var wrap = document.createElement('div');
      wrap.className = 'agmf-acc-wrap';
      var html = '';
      panelTabs.forEach(function (t, i) {
        // Details greets the shopper already open (Dorian 2026-08-19): part 1
        // reads without a click, and the Read more button carries the rest.
        var open = i === 0 ? ' open' : '';
        html += '<details class="agmf-acc"' + open + '>' +
          '<summary><span class="agmf-acc-title">' + esc(t.name) + '</span>' +
          '<span class="agmf-acc-icon" aria-hidden="true">&#8964;</span></summary>' +
          '<div class="agmf-acc-body">' + panelTabHtml(t) + '</div></details>';
      });
      wrap.innerHTML = html;
      anchorEl.parentNode.insertBefore(wrap, anchorEl);
      ((nativePanel && nativePanel.originals) || []).forEach(function (original) { original.hidden = true; });
      // The sticky tab bar is PARKED (Dorian 2026-08-12) - the return strip
      // carries the chrome alone for now. Re-enable by restoring this call;
      // buildAccBar/refreshAccBar/accBarTick stay intact and inert.
      // buildAccBar(wrap);
      // toggle does not bubble — capture sees descendants' toggles
      wrap.addEventListener('toggle', function () { manageVideos(); }, true);
      document.documentElement.classList.add('agmf-spec-live');

      // THE DESCRIPTION'S OWN VIDEO LEAVES THE BUY COLUMN (Dorian 2026-10-05:
      // "the video that was on the side in top of accordeon we don't want to
      // display it! It will be automatically on the feed since its a youtube
      // video"). Most descriptions open with a YouTube embed above the old tab
      // table; the table folds into these accordions, the embed stayed and sat
      // right on top of them. It is REMOVED, not hidden, so the lazy-iframe
      // restore never starts a player nobody sees, and so is every box around
      // it that held nothing else. A video INSIDE the accordions (a sizing
      // tutorial) is kept.
      dropDescriptionMedia(wrap);
      // NO WHITE SPACE LEFT BEHIND (Dorian 2026-10-05: "make sure you don't let
      // things like that !! white spaces etc ! in all of the products"). What
      // the table and the video leave in the description - a paragraph that
      // held only the video and a <br>, an empty <p>&nbsp;</p>, an empty div -
      // still took its line, and the video's own 560x315 box took a 315px gap.
      // Every element outside the accordions is folded away (2026-10-06: the
      // words too - foldOutside), and it is done AGAIN whenever the block changes: picking
      // a colour re-renders parts of the product column, which brought the
      // description's video and its gap straight back. The CSS twin
      // (agilite-media-flow.css, NO WHITE SPACE) keeps a re-rendered video
      // from taking room even before this runs.
      watchDescription(wrap);
      function dropDescriptionMedia(keep) {
        // .agb-main too: on a Gear Builder page the description sits straight
        // in the builder column, outside .product-info (K19 kept its video)
        var descs = document.querySelectorAll('.product-info [data-block-type="description"], .agb-main [data-block-type="description"]');
        Array.prototype.forEach.call(descs, function (desc) {
          Array.prototype.forEach.call(desc.querySelectorAll('iframe, video, embed, object'), function (media) {
            if (keep && keep.contains(media)) return;
            var box = media;
            while (box.parentElement && box.parentElement !== desc &&
                   !(keep && box.parentElement.contains(keep)) &&
                   !String(box.parentElement.textContent || '').trim()) {
              box = box.parentElement;
            }
            if (box !== media) { box.hidden = true; box.style.display = 'none'; }
            if (media.parentNode) media.parentNode.removeChild(media);
          });
          foldOutside(desc, keep);
        });
      }
      // ONLY THE ACCORDIONS (Dorian 2026-10-06: "it should be the full
      // metafield and only the metafields content"). Not just the empty lines:
      // every piece of the old description outside the accordions is folded -
      // a stray sentence ("Comes with QD buckles") was the description's copy,
      // and the accordions now carry the product's own words in full.
      function foldOutside(root, keep) {
        Array.prototype.forEach.call(root.children || [], function (node) {
          if (keep && (node === keep || node.contains(keep))) {
            if (node !== keep) foldOutside(node, keep);
            return;
          }
          if (node.hidden || node.style.display === 'none') return;
          node.hidden = true; node.style.display = 'none';
        });
      }
      function watchDescription(keep) {
        if (!window.MutationObserver) return;
        var pending = false;
        var mo = new MutationObserver(function () {
          if (pending) return;
          pending = true;
          requestAnimationFrame(function () { pending = false; dropDescriptionMedia(keep); });
        });
        Array.prototype.forEach.call(document.querySelectorAll('.product-info, .agb-main'), function (info) {
          mo.observe(info, { childList: true, subtree: true });
        });
      }

      // Judge.me reviews join the accordions as their own tab (Dorian
      // 2026-08-11): the widget loads async, so retry with backoff; if it
      // never appears, no tab is added and nothing else changes.
      function captureReviews(attempt) {
        // the REVIEW widget only — the title-row rating badge is ALSO a
        // .jdgm-widget and must stay where it is
        var widget = document.querySelector('.jdgm-widget:not(.jdgm-preview-badge), .jdgm-rev-widg');
        var hostSection = widget && (widget.closest('[id^="shopify-section-"]') || widget);
        // never move an ancestor of the accordions into itself (the badge
        // lives in the MAIN section, which CONTAINS this wrap)
        if (hostSection && hostSection.contains(wrap)) hostSection = null;
        if (!widget || !hostSection) {
          if (attempt < 10) setTimeout(function () { captureReviews(attempt + 1); }, Math.min(attempt * 800 + 400, 4000));
          return;
        }
        if (wrap.querySelector('.agmf-acc--reviews')) return;
        var det = document.createElement('details');
        det.className = 'agmf-acc agmf-acc--reviews';
        // the summary states the social proof up front (Dorian 2026-08-12)
        var revStars = '';
        if (productReviews && productReviews.count) {
          var rn = Math.round(productReviews.avg);
          var st = '';
          for (var si = 0; si < 5; si++) st += si < rn ? '★' : '☆';
          revStars = '<span class="agmf-acc-rating"><span class="agmf-acc-stars">' + st + '</span>' +
            productReviews.avg + ' (' + Number(productReviews.count).toLocaleString() + ')</span>';
        }
        det.innerHTML = '<summary><span class="agmf-acc-title">Reviews</span>' + revStars +
          '<span class="agmf-acc-icon" aria-hidden="true">&#8964;</span></summary>' +
          '<div class="agmf-acc-body agmf-acc-body--reviews"></div>';
        wrap.appendChild(det);
        det.querySelector('.agmf-acc-body--reviews').appendChild(hostSection);
        refreshAccBar();   // the Reviews tab joins the sticky bar
      }
      captureReviews(0);
      abbrevThemeSizes(0);
    }

    // side dots for the hero (Dorian 2026-08-12): a WINDOW of 5 dots -
    // shrunken edge dots whisper "more beyond" without counting it out; at
    // the true last slide no shrunken dot follows, so the end IS legible
    function heroDots(i, n) {
      if (n < 2) return '';
      var W = Math.min(5, n);
      var start = Math.min(Math.max(0, i - 2), n - W);
      var html = '';
      for (var k = start; k < start + W; k++) {
        var cls = 'agmf-dot';
        if (k === i) cls += ' agmf-dot--on';
        if ((k === start && start > 0) || (k === start + W - 1 && start + W < n)) cls += ' agmf-dot--more';
        html += '<span class="' + cls + '"></span>';
      }
      return html;
    }

    // ---- sticky accordion tab bar (Dorian 2026-08-12): appears once the
    // shopper scrolls BELOW the accordions, stays until they come back up.
    // Tabs anchor + open their accordion; Returns/Gov Sales stay out. ----
    var accBar = null;
    var accWrap = null;

    function refreshAccBar() {
      // rebuildable: the Reviews accordion arrives ASYNC (Judge.me capture)
      // and was missing from a bar built once at accordion-build time
      if (!accBar || !accWrap) return;
      var tabs = [];
      accWrap.querySelectorAll('.agmf-acc').forEach(function (d, i) {
        var t = (d.querySelector('.agmf-acc-title') || {}).textContent || '';
        if (/return|gov/i.test(t)) return;
        tabs.push({ i: i, t: t });
      });
      accBar.innerHTML = tabs.map(function (x) {
        return '<button type="button" class="agmf-acc-bar-tab" data-agmf-accjump="' + x.i + '">' + esc(x.t) + '</button>';
      }).join('');
    }

    function buildAccBar(wrap) {
      if (accBar) return;
      accWrap = wrap;
      accBar = document.createElement('div');
      accBar.className = 'agmf-acc-bar';
      refreshAccBar();
      accBar.addEventListener('click', function (e) {
        var b = e.target.closest ? e.target.closest('[data-agmf-accjump]') : null;
        if (!b || !accWrap) return;
        var d = accWrap.querySelectorAll('.agmf-acc')[parseInt(b.getAttribute('data-agmf-accjump'), 10)];
        if (!d) return;
        d.open = true;
        // PARKED code, kept correct: this bar is not built today, but a
        // document-position jump would land on the footer the moment it is,
        // the same way the rating badge did.
        agmfScrollBy(d, d.getBoundingClientRect().top - (accBar.offsetHeight + 62), true);
        manageVideos();
      });
      document.body.appendChild(accBar);
    }

    function abbrevThemeSizes(attempt) {
      // the theme's block picker prints full option values - shorten the
      // visible size labels only (inputs keep their real values). Retries:
      // the picker hydrates async.
      var hit = 0;
      document.querySelectorAll('.variant-picker label, .variant-picker .block-swatch, .variant-picker button').forEach(function (el) {
        if (el.childElementCount > 1) return;
        var t = (el.textContent || '').trim();
        var a = sizeAbbrev(t);
        if (a !== t) {
          var target = el.childElementCount === 1 ? el.firstElementChild : el;
          target.textContent = a;
          hit++;
        }
      });
      if (!hit && attempt < 6) setTimeout(function () { abbrevThemeSizes(attempt + 1); }, attempt * 500 + 300);
    }

    function accBarTick() {
      if (!accBar || !accWrap) return;
      // The tabs are a permanent wayfinder (Dorian 2026-08-12): present from
      // the top of the page, they step aside only while the accordions
      // themselves are on screen (you are already there), and come back
      // under the return strip once you scroll past into the feed.
      var vh = window.innerHeight || 1;
      var r = accWrap.getBoundingClientRect();
      var atSection = r.top < vh * 0.72 && r.bottom > 96;
      document.documentElement.classList.toggle('agmf-accbar-on', !atSection);
    }

    // ---- PRODUCT HERO (Dorian's v9 top): the stock gallery becomes a
    // product card — Shopify images or the saved library sequence, in a
    // 3:4 snap slider with a thumbs rail. The
    // gallery hides ONLY when the hero commits (agmf-hero-live). ----
    var heroBuilt = false;

    // WHAT THE ENDLESS TAIL MAY DRAW FROM: feed items of the chosen types,
    // that have something to show (a picture or a video), that the card is
    // not already showing, and that the exporter has not marked card-only.
    // On a mixed bundle feed with shares, only the card's own product's pieces
    // (decided with the shares, 2026-10-08): the card is that product's, so
    // neither its phone tail nor its desktop filler wanders into another
    // product's shorts and photos.
    // THE KINDS A 3:4 CARD CAN SHOW ON ITS OWN - Agilite Media's
    // ENDLESS_TYPE_IDS (FeedContentTypes.tsx), the list its card editor
    // offers for "keeps going on the phone".
    var TAIL_KINDS = ['created-loop', 'youtube-short', 'instagram-reel', 'instagram-photo',
      'instagram-carousel', 'community-photo', 'community-carousel', 'review-photo'];

    // WHAT THE ENDLESS CARD DRAWS FROM (Dorian 2026-10-08: "in the K zero sf
    // new page (and problably other pages, there is still no infinite 'feed'
    // in the card itself while in AM we set it to 'continue on phone'").
    // The chosen kinds come first. Under three pieces of them - K-Zero SF has
    // two Shorts and no Instagram photo, Re-FLEX none of its kinds - the card
    // used to draw no tail at all; it now tops up with the product's other
    // card kinds, never one the card has switched off, so "continue" always
    // continues while the product has anything to show.
    function tailPools(feedItems, want, fixed, typesOff) {
      var pool = heroTailPool(feedItems, want, fixed);
      var extra = [];
      if (pool.length < 3) {
        var others = {}, off = {};
        (typesOff || []).forEach(function (ty) { off[ty] = 1; });
        TAIL_KINDS.forEach(function (ty) { if (!want[ty] && !off[ty]) others[ty] = 1; });
        extra = heroTailPool(feedItems, others, fixed);
      }
      return { pool: pool, extra: extra };
    }

    // A BUNDLE'S LEAD PICTURE SHOWS ONCE. hero.lead (the bundle's own first
    // Shopify picture) always opens the card, wide; a card the bundle
    // arranged itself in Agilite Media can hold that same picture again
    // (Dorian 2026-10-08, composing a bundle's card from its products'
    // pictures). Same file = same picture: compared by the file name (the
    // section's URL is on the shop's domain, a card's may be on
    // cdn.shopify.com - one store's file names are unique, Shopify suffixes
    // a repeat), or by the Shopify image id. The lead now follows the colour
    // (2026-10-11), so EVERY colour's lead picture (lead.images) stays out
    // of the card: the chosen one is already above it, and the others are a
    // rival colour's. A card made of nothing else keeps its pictures.
    function withoutLead(images, lead) {
      if (!lead || !lead.src || !images || !images.length) return images;
      var file = function (u) { return String(u || '').split('?')[0].split('#')[0].split('/').pop(); };
      var files = {}, ids = {};
      [lead].concat(lead.images || []).forEach(function (L) {
        if (!L) return;
        if (L.src) files[file(L.src)] = 1;
        if (L.id) ids[String(L.id)] = 1;
      });
      var kept = images.filter(function (im) {
        if (!im) return false;
        if (im.id && ids[String(im.id)]) return false;
        if (im.ck && /^shopify-img:/.test(im.ck) && ids[im.ck.slice(12)]) return false;
        return !(im.src && files[file(im.src)]);
      });
      return kept.length ? kept : images;
    }

    function heroTailPool(feedItems, want, fixed) {
      var out = [], seen = {};
      (feedItems || []).forEach(function (it) {
        if (!it || it.cardOnly || (it.ck && deadKeys[it.ck])) return;
        if (shareCardOwns && !shareCardOwns(it)) return;
        if (!want[it.contentType]) return;
        if (!it.video && !it.thumb) return;
        // The card is 3:4 and refuses wide motion everywhere else; so here.
        if (it.video) {
          var vert = it.video.loop ? true
            : (it.video.vertical != null ? !!it.video.vertical
               : (it.format === 'Short' || it.format === 'GIF'));
          if (!vert) return;
        }
        var key = it.ck || ('id:' + it.id);
        if (fixed[key] || fixed['id:' + it.id] || seen[key]) return;
        seen[key] = 1;
        out.push(it);
      });
      return out;
    }

    // A LOOP THE CARD HOLDS IS A FEED PIECE TOO (2026-10-08). The rule that
    // our loops reach the feed when the card did not show them (buildOrder,
    // "OUR LOOPS ARE NOT CARD-ONLY") works on feed ITEMS - and a loop pinned
    // in the card can arrive with no item at all: Mag-Release's auto-deploy
    // loop is card slide 4 of 12 and nowhere in feed.items, so a phone that
    // never swiped three times never saw it. Such an entry already carries
    // what a feed card needs (its ck, its video, its caption), so it joins
    // the items as a CARD-ONLY loop - out of the feed once the card shows it,
    // at its front until then. Only for entries the card really uses, and
    // never twice (an item with the same ck or id wins).
    function addCardLoopItems(feedItems, images) {
      if (!Array.isArray(feedItems) || !images) return;
      var have = {};
      feedItems.forEach(function (it) {
        if (!it) return;
        if (it.ck) have['ck:' + it.ck] = 1;
        if (it.id != null) have['id:' + it.id] = 1;
      });
      images.forEach(function (im) {
        if (!im || !im.ck || !im.video || !isLooping(im)) return;
        if (have['ck:' + im.ck] || (im.itemId != null && have['id:' + im.itemId])) return;
        var v = im.video;
        if (!((v.provider === 'file' && v.src) || (v.provider === 'youtube' && v.id))) return;
        have['ck:' + im.ck] = 1;
        feedItems.push({
          id: im.itemId != null ? im.itemId : im.ck, ck: im.ck, shape: 'motion',
          format: im.format || 'GIF', contentType: im.contentType || 'created-loop',
          title: im.alt || '', text: '', thumb: im.thumb || im.src, video: v,
          caption: im.caption || null, platform: 'Agilite',
          source: { name: 'Agilite', kind: 'ours' }, tags: ['loop'],
          cardOnly: true, fromCardEntry: true
        });
      });
    }

    function buildProductHero(feedItems) {
      if (heroBuilt) return;
      var dataEl = document.querySelector('[data-agmf-hero]');
      var gallery = document.querySelector('.product-gallery');
      if (!dataEl || !gallery || !gallery.parentNode) return;
      var hero;
      try { hero = JSON.parse(dataEl.textContent); } catch (e) { return; }
      // Shopify media is the automatic baseline. Only a deliberately pinned
      // Agilite Media sequence replaces it; an unpinned list may be made from
      // tagged social posts and must not silently become the product gallery.
      var images = window.AGMF_PRODUCT_SOURCES
        ? window.AGMF_PRODUCT_SOURCES.selectHeroImages(hero.images, heroImages, heroPinned)
        : ((heroPinned && heroImages) || hero.images || heroImages || []).filter(function (im) { return im && im.src; });
      if (!images.length) return;
      images = withoutLead(images, hero.lead);
      addCardLoopItems(feedItems, images);
      if (window.AGMF_PRODUCT_SOURCES) {
        window.AGMF_PRODUCT_SOURCES.reconcileCardOnly(feedItems, images, heroFeedTypesOff);
      }
      heroBuilt = true;

      // The exporter owns the product card's selections. Tagged feed videos
      // are NOT an additional gallery: discovering them here bypassed the
      // saved type restrictions, caps and CDN-first order on new products.
      // An unconfigured card uses Shopify; a saved card uses its exact export.
      // So a pinned card takes the images EXACTLY as they arrive: no tagged
      // motion woven in, no re-deal. The exporter already fills a pin's
      // content slots, so anything the pin asked for is in `images` -
      // weaving more in is the theme second-guessing the person.
      var heroSeq = images.map(function (im) { return { im: im }; });
      // motion in the gallery IS library content, so it carries the real key
      function heroKey(it) {
        if (!it) return '';
        var a = it.ck ? ' data-agmf-ck="' + esc(it.ck) + '" data-agmf-surface="card"' : '';
        if (it.contentType) a += ' data-agmf-type="' + esc(it.contentType) + '"';
        if (it.id != null) a += ' data-agmf-id="' + esc(it.id) + '"';
        return a;
      }
      // A PINNED LOOP IS A CLIP, NOT ITS FIRST FRAME (Dorian 2026-09-07:
      // "why is the video clip not playing? it is supposed to be a loop,
      // made in AM"). Every hero entry used to become an <img> - the only
      // video slides were the auto-filled tail, which arrives as ITEMS. A
      // loop somebody pinned by hand arrived as an image entry whose src is
      // the loop's baked still, so the card showed a frozen frame of a clip
      // that plays perfectly well two screens down as a feed card.
      // Two ways in, in order: the entry carries `video` itself (the
      // exporter now writes it), else its `itemId` names a feed item that
      // does - which is how a feed exported BEFORE the exporter change still
      // plays. Rendered with the same videoShell + caption overlay the card
      // uses, so the loop looks the same wherever it appears.
      var byItemId = {};
      (feedItems || []).forEach(function (it) { if (it && it.id != null) byItemId[String(it.id)] = it; });
      // ...and a third: the entry's `ck` names the same record as a feed item
      // (2026-10-05). An unbaked YouTube loop pinned on the card was exported
      // as its thumbnail with only a ck, while the feed carried the playable
      // loop under that ck - so the card showed a picture of a loop.
      var byCk = {};
      (feedItems || []).forEach(function (it) { if (it && it.ck) byCk[String(it.ck)] = it; });
      function playable(it) {
        return !!(it && it.video && ((it.video.provider === 'file' && it.video.src) ||
          (it.video.provider === 'youtube' && it.video.id)));
      }
      function clipFor(im) {
        if (playable(im)) return im;
        var it = im.itemId != null ? byItemId[String(im.itemId)] : null;
        if (!playable(it)) it = im.ck ? byCk[String(im.ck)] : null;
        return playable(it) ? it : null;
      }
      var slides = '';
      heroSeq.forEach(function (entry) {
        if (entry.im) {
          var im = entry.im;
          var clip = clipFor(im);
          if (clip) {
            var clipItem = {
              id: clip.id != null ? clip.id : im.itemId,
              ck: im.ck || clip.ck,
              contentType: clip.contentType || im.contentType || 'created-loop',
              format: clip.format || im.format || (clip.video.loop ? 'GIF' : 'Video'),
              video: clip.video,
              thumb: clip.thumb || im.src,
              title: clip.title || im.alt || '',
              caption: clip.caption || im.caption || null
            };
            slides += '<div class="agmf-hero-slide"' + (isLooping(clipItem) ? ' data-agmf-loop="1"' : '') +
              heroKey(clipItem) + '>' + heroClipShell(clipItem) + '</div>';
            return;
          }
          var f = im.frame || null;
          var st = '';
          if (f) {
            st = 'object-fit:' + (f.fit === 'cover' ? 'cover' : 'contain') + ';' +
              'object-position:' + Math.round((f.focusX != null ? f.focusX : 0.5) * 100) + '% ' +
              Math.round((f.focusY != null ? f.focusY : 0.5) * 100) + '%;' +
              (f.background ? 'background:' + esc(f.background) + ';' : '');
          }
          // A PHOTOGRAPH FILLS THE CARD RATHER THAN SITTING IN IT.
          // The card is 3:4 and stays 3:4. A picture of another shape used to
          // letterbox on the sand - which is right for a cut-out product
          // render (a cropped belt is abstraction, not a product picture) and
          // wrong for a photograph. Agilite Media's card preview has zoomed
          // off-shape photographs since 2026-08-27; the storefront was never
          // told, so an operator cropped nothing, saw it filled in the
          // preview, and the shopper got it letterboxed (Dorian 2026-08-30).
          // `zoom` on the wire marks the photographs. Measured here rather
          // than at export because only the browser knows the real shape, and
          // an explicit frame is a DECISION that still wins over this default.
          var mayZoom = !f && im.zoom ? ' data-agmf-zoom="1"' : '';
          // The same content key the feed cards carry, so "they already saw
          // that on the card" is a question the ledger can answer at all.
          var slideCk = im.ck ? ' data-agmf-ck="' + esc(im.ck) + '" data-agmf-surface="card"' : '';
          // A LIBRARY PICTURE ON THE CARD CAN BE JUDGED; A SHOPIFY GALLERY SHOT
          // CANNOT (Dorian 2026-09-07). The product's own photographs have no
          // record behind them, so the review bar must keep skipping them -
          // there is nothing to accept or reject. A picture that came from the
          // library does have one, and it was being left out with them because
          // only the motion slides were ever stamped.
          if (im.itemId != null) {
            slideCk += ' data-agmf-id="' + esc(im.itemId) + '"';
            if (im.contentType) slideCk += ' data-agmf-type="' + esc(im.contentType) + '"';
          }
          // A NAME FOR THE PRODUCT'S OWN PHOTOGRAPHS. They come from Shopify,
          // not the library, so they have no `ck` - and analytics that cannot
          // name the first thing on the page is not analytics (Dorian
          // 2026-09-03 asked for a content id on everything). The key is kept
          // in a SEPARATE attribute on purpose: `data-agmf-ck` belongs to the
          // seen ledger, and a ledger written in two key spaces is worse than
          // one that starts late.
          if (!im.ck) {
            // Shopify's own shot carries an image id; the library's product
            // set arrives with neither id nor ck, so the file name stands in -
            // it is stable, it is readable in a query, and every slide gets a
            // name (measured: without this, 16 of 17 gallery slides were
            // unnameable and the analytics simply skipped the first thing on
            // the page).
            var k = im.id ? 'shopify-img:' + im.id
              : 'img:' + String(im.src || '').split('?')[0].split('/').pop();
            slideCk += ' data-agmf-track-ck="' + esc(k) + '" data-agmf-track-type="' +
              (im.id ? 'product_image' : 'library_image') + '"';
          }
          slides += '<div class="agmf-hero-slide"' + slideCk + '><img draggable="false" loading="lazy" alt="' + esc(im.alt || '') + '"' +
            mayZoom + (st ? ' style="' + st + '"' : '') + ' src="' + esc(im.src) + '"></div>';
          return;
        }
      });

      // Same rule and tolerance as the library's own preview, so the two
      // surfaces cannot drift again: only an off-shape picture zooms, and
      // anything already 3:4 is untouched because contain and cover agree
      // there. `complete` is checked first - a cached picture never fires
      // load, which is most of them once the page has been seen once.
      function fillIfOffShape(img) {
        if (!img || !img.naturalWidth || !img.naturalHeight) return;
        var aspect = img.naturalWidth / img.naturalHeight;
        if (Math.abs(aspect - 0.75) > 0.02) img.style.objectFit = 'cover';
      }
      function armZoom(root) {
        var imgs = root.querySelectorAll('img[data-agmf-zoom]');
        for (var z = 0; z < imgs.length; z++) {
          (function (img) {
            if (img.complete) fillIfOffShape(img);
            else img.addEventListener('load', function () { fillIfOffShape(img); }, { once: true });
          })(imgs[z]);
        }
      }

      var thumbs = '';
      images.forEach(function (im, i) {
        thumbs += '<button type="button" class="agmf-hero-thumb' + (i === 0 ? ' agmf-hero-thumb--on' : '') +
          '" data-agmf-hero-thumb="' + i + '" style="background-image:url(&quot;' + esc(im.src) + '&quot;)" aria-label="Image ' + (i + 1) + '"></button>';
      });

      var el = document.createElement('div');
      el.className = 'agmf-hero';
      el.innerHTML =
        '<div class="agmf-hero-head">' +
        '<h2 class="agmf-hero-title">' + esc(hero.title || '') + '</h2>' +
        '<div class="agmf-hero-badge" data-agmf-hero-badge>' +
        (productReviews && productReviews.count
          ? '<span class="agmf-hero-stars" title="Average of ' + productReviews.count + ' customer reviews">' +
            (function (n) { var s = ''; for (var i = 0; i < 5; i++) s += i < n ? '\u2605' : '\u2606'; return s; }(Math.round(productReviews.avg))) +
            '</span><span class="agmf-hero-rev-n">' + productReviews.avg + ' (' + Number(productReviews.count).toLocaleString() + ')</span>'
          : '') +
        '<span class="agmf-hero-sku" data-agmf-hero-sku hidden></span></div>' +
        '<div class="agmf-hero-price">' + esc(hero.price || '') + '</div>' +
        '</div>' +
        '<div class="agmf-hero-track" data-agmf-hero-track>' + slides + '</div>' +
        '<div class="agmf-hero-dots" data-agmf-hero-dots aria-hidden="true"></div>';
      gallery.parentNode.insertBefore(el, gallery);
      armZoom(el);          // photographs fill the 3:4 card; renders keep containing
      document.documentElement.classList.add('agmf-hero-live');

      // The theme's info blocks we hide (title, sku, price, rating) leave
      // ZERO-HEIGHT wrappers behind, and their bottom margins stacked into
      // an 80px hole between the card and the colour swatches. Measured, so
      // it stays right on any product: a block that renders nothing is
      // collapsed. (Dorian 2026-08-12; 80px -> 6px, verified in-page.)
      (function collapseEmptyBlocks(attempt) {
        var list = document.querySelector('.product-info__block-list');
        if (!list) return;
        if (window.AGMF_PRODUCT_SOURCES) window.AGMF_PRODUCT_SOURCES.collapseMetadata(list);
        // app blocks (rating widgets) hydrate late - re-check a few times
        if (attempt < 6) setTimeout(function () { collapseEmptyBlocks(attempt + 1); }, attempt * 400 + 300);
      }(0));

      // the SKU rides beside the title and FOLLOWS the chosen variant: the
      // theme's own <variant-sku> node is the source of truth (it re-renders
      // on every variant change), we just mirror it (Dorian 2026-08-12)
      (function skuMirror() {
        var slot = qs(el, '[data-agmf-hero-sku]');
        var srcEl = document.querySelector('variant-sku, .variant-sku');
        if (!slot || !srcEl) return;
        function sync() {
          var t = (srcEl.textContent || '').replace(/^\s*sku[:\s]*/i, '').trim();
          slot.textContent = t;
          slot.hidden = !t;
        }
        sync();
        if (window.MutationObserver) {
          new MutationObserver(sync).observe(srcEl, { childList: true, characterData: true, subtree: true });
        }
      }());

      // ---- ONE product title on DESKTOP (Dorian 2026-08-26) ----------------
      // The hero printed the product identity a second time, beside the theme's
      // own: two titles, two prices, on the same screen. Keep the THEME's - it
      // belongs with the buy controls - give it the hero's look, and move the
      // review badge (stars + count + SKU) underneath it. The hero's own head
      // is then hidden by CSS.
      //
      // DESKTOP ONLY, by two locks: this runs only above the media flow's own
      // 760px breakpoint, and every style rides in a @media(min-width:760px)
      // block. Below 760 the hero head renders exactly as it always has.
      //
      // Runs AFTER skuMirror on purpose: that closure captured the SKU slot
      // from inside the hero, so moving the badge first would break the
      // per-variant SKU update. Moving it now keeps the live reference.
      (function desktopOneTitle() {
        try {
          if (!window.matchMedia) return;
          var mq = window.matchMedia('(min-width:760px)');
          var badge = qs(el, '[data-agmf-hero-badge]');
          var heroHead = qs(el, '.agmf-hero-head');
          var themeTitle = document.querySelector('.product-info h1.product-title, .product-info .product-title');
          if (!themeTitle) return;               // no theme title -> keep the hero's

          function toDesktop() {
            var block = themeTitle.closest('.product-info__block-item') || themeTitle.parentElement;
            if (block) {
              // collapseEmptyBlocks() above hides zero-height blocks; this one
              // is the keeper now, so take it back and mark it.
              block.classList.remove('agmf-blank-block');
              block.classList.add('agmf-title-kept');
            }
            themeTitle.classList.add('agmf-title-kept-h');
            if (badge && badge.parentElement !== themeTitle.parentElement) {
              themeTitle.insertAdjacentElement('afterend', badge);
            }
            if (badge) badge.classList.add('agmf-badge-moved');
            document.documentElement.classList.add('agmf-desktop-one-title');
          }

          function toMobile() {
            // Put everything back exactly where the mobile layout expects it.
            // Without this, a DESKTOP load that is later narrowed (an iPad
            // rotating to portrait crosses 760px) left the hero head visible
            // again - but with its stars still sitting in the hidden buy
            // column. Reversible by construction, so the breakpoint can be
            // crossed any number of times.
            document.documentElement.classList.remove('agmf-desktop-one-title');
            themeTitle.classList.remove('agmf-title-kept-h');
            var block = themeTitle.closest('.product-info__block-item') || themeTitle.parentElement;
            if (block) block.classList.remove('agmf-title-kept');
            if (badge && heroHead && badge.parentElement !== heroHead) {
              // back into the hero head, between the title and the price
              var price = qs(el, '.agmf-hero-price');
              if (price) heroHead.insertBefore(badge, price);
              else heroHead.appendChild(badge);
              badge.classList.remove('agmf-badge-moved');
            }
          }

          function apply() { if (mq.matches) toDesktop(); else toMobile(); }
          apply();
          // addEventListener on a MediaQueryList is not in older Safari
          if (mq.addEventListener) mq.addEventListener('change', apply);
          else if (mq.addListener) mq.addListener(apply);
        } catch (e) { /* never block the hero over a layout nicety */ }
      }());

      var track = qs(el, '[data-agmf-hero-track]');
      var dotsEl = qs(el, '[data-agmf-hero-dots]');
      var slideCount = railCount(track);   // reachable slides, not hidden ones
      // paint only on CHANGE: rewriting identical dots every scroll frame
      // restarted their CSS transitions - the strip visibly blinked
      // (Dorian 2026-08-23). One string compare ends that.
      var lastDotsKey = '';
      function paintDots(i) {
        if (!dotsEl) return;
        var key = i + '/' + slideCount;
        if (key === lastDotsKey) return;
        lastDotsKey = key;
        dotsEl.innerHTML = heroDots(i, slideCount);
      }
      paintDots(0);

      // ---- COLOUR-SCOPED DECK (Dorian 2026-08-20): choosing a colour
      // re-deals the card - that colour's slides first, then GENERAL slides
      // (no colour on them), and other colours' slides leave the deck
      // entirely. No colour chosen = the arranged order, untouched. The
      // motion slides appended after the images carry no colour and count
      // as general.
      var heroDeck = [];
      (function collectDeck() {
        var els = track.children;
        for (var di = 0; di < els.length; di++) {
          // v55 DOUBLE TAGS (Dorian 2026-08-23: 'General and not multicam
          // only'): an entry can carry SEVERAL colours plus an `everyone`
          // flag - it rides its colour's path AND the general sequence.
          var entry = images[di] || {};
          var cols = entry.colours || (entry.colour ? [entry.colour] : []);
          heroDeck.push({
            el: els[di],
            colours: cols.map(function (cc) { return String(cc).toLowerCase(); }),
            everyone: !!entry.everyone,
            // its place in each colour's own lane (Agilite Media's card
            // editor, 2026-10-07), colour lowercased -> rank
            laneRank: (entry.laneRank && typeof entry.laneRank === 'object') ? entry.laneRank : null,
          });
        }
      }());
      // A BUNDLE'S OWN PICTURE LEADS ITS CARD (Dorian 2026-10-07: "for
      // desktop: A 2 columns lenght first picture of the bundle, then the
      // normal feed of the individual"). On a bundle page the feed - and so
      // this card - is the individual product's, mirrored by Agilite Media
      // (feed_mirror.py); the section hands over the bundle's first Shopify
      // picture as hero.lead. It goes in FRONT of the deck and is not part of
      // it: the deck pairs each slide with its entry by position, colour
      // deals append the deck behind it, so no deal can move or park it. Two
      // columns wide on the desktop grid (pdp-desk-3col.css), the first
      // slide of the rail on a phone.
      //
      // THE LEAD FOLLOWS THE COLOUR (Dorian 2026-10-11: "The big picture of
      // the bundle (1st pic) should be the main pic from the bundle and react
      // to selection / preselection of colors variant, now when i click
      // different color its not changing"). Each colour of a bundle has its
      // own main picture in Shopify (the variant's featured image); the
      // section lists the bundle's pictures (hero.lead.images) and names each
      // variant's (hero.lead.byVariant). The lead opens on the variant the
      // form holds - colour-pref or a ?variant= link may have chosen it before
      // this card was built - and swaps on every variant:change
      // (heroVariantJump). A variant with no picture of its own keeps the
      // picture shown.
      function leadPicFor(v) {
        var L = hero.lead;
        if (!L || !v || !L.images || !L.images.length) return null;
        var pid = (L.byVariant && v.id != null) ? L.byVariant[String(v.id)] : 0;
        if (!pid && v.featured_image && v.featured_image.id != null) pid = v.featured_image.id;
        if (!pid) return null;
        for (var li = 0; li < L.images.length; li++) {
          if (L.images[li] && L.images[li].src && String(L.images[li].id) === String(pid)) return L.images[li];
        }
        return null;
      }
      var leadSlide = null;
      if (hero.lead && hero.lead.src) {
        var formVariant = document.querySelector('form[action*="/cart/add"] input[name="id"]');
        var firstLead = (formVariant && formVariant.value && leadPicFor({ id: formVariant.value })) || hero.lead;
        leadSlide = document.createElement('div');
        leadSlide.className = 'agmf-hero-slide agmf-hero-slide--lead';
        leadSlide.setAttribute('data-agmf-track-ck', 'shopify-img:' + (firstLead.id || 'lead'));
        leadSlide.setAttribute('data-agmf-track-type', 'product_image');
        leadSlide.innerHTML = '<img draggable="false" alt="' + esc(firstLead.alt || '') + '" src="' + esc(firstLead.src) + '">';
        track.insertBefore(leadSlide, track.firstChild);
      }
      // Swaps the lead to `pic` once it has arrived, so the old picture stays
      // until the new one can replace it; the last colour chosen wins a race.
      // A picture that fails to load leaves the one shown. Returns whether
      // the lead is changing.
      // NOTHING ON SCREEN YET, NOTHING TO KEEP (review 2026-10-11): colour-pref
      // answers after the card is built, while the page is still held
      // (snippets/agmf-boot-hold.liquid, which waits for THIS img to load).
      // Preloading then let the hold reveal the page on Multicam and the
      // preferred colour replaced it 1-2 s later on a cold phone. While the
      // page is held, or the lead has not painted, the picture is swapped at
      // once, so the hold waits for the right one.
      function setLead(pic) {
        if (!leadSlide || !pic || !pic.src) return false;
        var img = leadSlide.querySelector('img');
        if (!img) return false;
        var token = leadSlide.__agmfLeadWant = pic.src;
        if (img.getAttribute('src') === pic.src) return false;
        function put() {
          img.src = pic.src;
          img.alt = pic.alt || '';
          leadSlide.setAttribute('data-agmf-track-ck', 'shopify-img:' + (pic.id || 'lead'));
          if (deckHoldsLead) reDeal(lastDeal);   // its twin in the deck changes
        }
        if (!(img.complete && img.naturalWidth) || document.documentElement.classList.contains('agmf-booting')) {
          put();
          return true;
        }
        var pre = new Image();
        try { pre.fetchPriority = 'high'; } catch (e) { /* older browsers */ }
        pre.onload = function () {
          if (leadSlide.__agmfLeadWant === token) put();
        };
        pre.src = pic.src;
        return true;
      }
      // THE LEAD'S TWIN STAYS OUT OF THE DECK (review 2026-10-11). withoutLead
      // keeps every colour's lead picture out of the card - unless they are
      // all the card has: the feed failed or came too late, and the card fell
      // back to the bundle's own Shopify pictures, which ARE the lead
      // pictures. Then the deck keeps them all, and the one the lead is
      // showing right now is parked (reDeal), so each colour shows once
      // whichever is chosen.
      var leadFiles = {};
      if (hero.lead) {
        [hero.lead].concat(hero.lead.images || []).forEach(function (L) {
          if (L && L.src) leadFiles[fileOf(L.src)] = 1;
        });
      }
      function fileOf(u) { return String(u || '').split('?')[0].split('#')[0].split('/').pop(); }
      function picFile(slide) {
        var im = slide && slide.querySelector && slide.querySelector('img');
        return im ? fileOf(im.getAttribute('src')) : '';
      }
      function isLeadTwin(slide) {
        if (!leadSlide) return false;
        var f = picFile(slide);
        return !!f && f === picFile(leadSlide);
      }
      var deckHoldsLead = !!leadSlide && heroDeck.some(function (d) { return !!leadFiles[picFile(d.el)]; });
      var lastDeal = null;
      // A DEAD SLIDE STAYS DEAD (2026-10-05). The deck keeps the slide
      // elements it captured at build time, and mediaDead() takes a slide
      // whose picture, file or player failed off the page. Re-dealing put
      // every captured element back with appendChild, so the first colour
      // change resurrected the broken slide - already marked dead, so
      // nothing would ever remove it again - and counted it in the dots.
      // Only slides still on the page are dealt; the tail's reattach obeys
      // the same rule.
      function heroSlideGone(slide) {
        return !slide || !slide.isConnected || !!(slide.dataset && slide.dataset.agmfDead);
      }
      function reDeal(colourValue) {
        lastDeal = colourValue || null;
        var want = colourValue ? String(colourValue).toLowerCase() : null;
        var visible = [], hidden = [];
        for (var di = 0; di < heroDeck.length; di++) {
          var d = heroDeck[di];
          if (heroSlideGone(d.el)) continue;
          // the picture the lead is showing is not shown twice
          if (deckHoldsLead && isLeadTwin(d.el)) { hidden.push(d); continue; }
          var mineD = want && d.colours.indexOf(want) !== -1;
          var generalD = d.everyone || d.colours.length === 0;
          if (!want || mineD || generalD) visible.push(d);
          else hidden.push(d);
        }
        if (want) {
          // mine first, general after - the general ones keep their arranged
          // order; a double-tagged slide counts as mine, so it never shows
          // twice. MINE follow the colour's own lane order when one was set
          // (Dorian 2026-10-07: "the order inside each variant lane should be
          // editable to make sense") - a slide never placed in that lane
          // follows, in the arranged order.
          var laneAt = function (d) {
            var r = d.laneRank && d.laneRank[want];
            return typeof r === 'number' ? r : 1e9;
          };
          visible.sort(function (a, b) {
            var am = a.colours.indexOf(want) !== -1 ? 0 : 1;
            var bm = b.colours.indexOf(want) !== -1 ? 0 : 1;
            if (am !== bm) return am - bm;
            if (am === 0 && laneAt(a) !== laneAt(b)) return laneAt(a) - laneAt(b);
            return heroDeck.indexOf(a) - heroDeck.indexOf(b);
          });
        }
        // data-agmf-parked names what the deal put away, so the lone-slide
        // rule in pdp-desk-3col.css counts only the slides actually shown.
        for (var v = 0; v < visible.length; v++) {
          visible[v].el.style.display = '';
          visible[v].el.removeAttribute('data-agmf-parked');
          track.appendChild(visible[v].el);
        }
        for (var h = 0; h < hidden.length; h++) {
          hidden[h].el.style.display = 'none';
          hidden[h].el.setAttribute('data-agmf-parked', '');
          track.appendChild(hidden[h].el);
        }
        slideCount = visible.length;
        lastDotsKey = '';
        paintDots(0);
        if (track.__agmfBalance) track.__agmfBalance();   // parity changed
        track.scrollTo({ left: 0, behavior: 'auto' });
        if (heroTail) heroTail.reattach();   // a re-deal moved the arranged slides behind it
        markShownPriority();
        manageVideos();
      }

      // THE PICTURES THE SHOPPER SEES FIRST ARE ASKED FOR FIRST (2026-10-06).
      // The page is revealed when the card is built and its first SHOWN
      // picture has arrived (snippets/agmf-boot-hold.liquid). Lazy slides are
      // only requested after layout, and marking the first slides eager while
      // building put the priority on the ARRANGED order - the colour deal then
      // parked them whenever the page opened on another colour, so the picture
      // waited for was the last one asked for (measured on K-Zero SF, Ranger
      // Green). So it is marked after the deal, on what is shown: the first
      // shown picture high priority, the first four (two rows of two on a
      // desktop, one on a phone) eager; parked slides lose the hint.
      function markShownPriority() {
        var n = 0, lim = columns() >= 2 ? 4 : 1, kids = track.children;
        for (var q = 0; q < kids.length; q++) {
          var pic = kids[q].querySelector && kids[q].querySelector('img');
          if (!pic) continue;
          if (kids[q].style.display === 'none' || kids[q].hasAttribute('data-agmf-parked')) {
            pic.removeAttribute('fetchpriority');
            continue;
          }
          n++;
          if (n === 1) pic.setAttribute('fetchpriority', 'high');   // before eager, so it starts high
          else pic.removeAttribute('fetchpriority');                // one picture at a time
          if (n <= lim) pic.setAttribute('loading', 'eager');
        }
      }

      // ---- THE ENDLESS TAIL (phone only) ---------------------------------
      // After the arranged slides the card keeps offering content of the
      // types chosen in Agilite Media, drawn from the feed and re-dealt
      // from the session's seed, for as long as the shopper swipes. Each
      // slide that comes into view is recorded (the seen ledger, surface
      // "product-card") and remembered for THIS page, so the feed below
      // does not show it again - see itemAtFlat.
      var heroTail = (function buildHeroTail() {
        if (!cardEndless || !cardEndless.types.length) return null;
        if (columns() >= 2) return null;      // the desktop card ends where it ends
        var want = {};
        cardEndless.types.forEach(function (ty) { want[ty] = 1; });
        var fixed = {};
        images.forEach(function (im) { if (im.ck) fixed[im.ck] = 1; if (im.itemId) fixed['id:' + im.itemId] = 1; });
        var pools = tailPools(feedItems, want, fixed, heroFeedTypesOff);
        var pool = pools.pool, extra = pools.extra;
        // Fewer than three is not "endless", it is the same picture again:
        // the feed's own rails refuse to carouselize under three siblings.
        if (pool.length + extra.length < 3) return null;
        var rnd = seededRandom((orderSeed ^ 0x5bd1e995) >>> 0);
        // each lap: the chosen kinds first, then the top-up
        var dealLap = function (r) { return shuffledBy(pool, r).concat(shuffledBy(extra, r)); };
        var deal = dealLap(rnd), cursor = 0, lap = 0, lastDealt = null;
        // Bounded by the pool as well as by memory: six photos need no
        // hundred and twenty slides to be seen.
        var CAP = Math.min(120, (pool.length + extra.length) * 4), BATCH = 3, tailEls = [];
        function next() {
          if (cursor >= deal.length) {
            lap++;
            deal = dealLap(seededRandom((orderSeed ^ 0x5bd1e995 ^ (lap * 0x9E3779B9)) >>> 0));
            // never the same slide twice in a row across the seam
            if (deal.length > 1 && deal[0] === lastDealt) deal.push(deal.shift());
            cursor = 0;
          }
          lastDealt = deal[cursor++];
          return lastDealt;
        }
        function slideFor(it) {
          var div = document.createElement('div');
          div.className = 'agmf-hero-slide agmf-hero-slide--tail';
          if (it.ck) { div.setAttribute('data-agmf-ck', it.ck); div.setAttribute('data-agmf-surface', 'card'); }
          if (it.contentType) div.setAttribute('data-agmf-type', it.contentType);
          // THE RECORD, NOT ONLY THE KEY (Dorian 2026-09-07: "the review tool
          // doesnt work on product card"). The phone bar picks what to judge
          // from `[data-agmf-id]`, and these slides carried the ledger key but
          // never the record id - so the whole endless tail, which is most of
          // what a shopper swipes up there, was invisible to it. The arranged
          // motion slides already carry it through heroKey(); this is the same
          // stamp, on the slides that are built in script rather than markup.
          if (it.id != null) div.setAttribute('data-agmf-id', String(it.id));
          if (isLooping(it)) div.setAttribute('data-agmf-loop', '1');
          if (it.video && it.video.provider === 'youtube') {
            var vert = it.video.vertical != null ? !!it.video.vertical : it.format === 'Short';
            div.innerHTML = ytShellHtml(it.video.id, it.thumb, vert ? 'agmf-media--yt-short' : '', capOverlayHtml(it));
          } else if (it.video) {
            div.innerHTML = videoShell(it, '', capOverlayHtml(it));
          } else {
            // A PHOTOGRAPH FILLS THE CARD: these are pictures of the gear in
            // use, never cut-out renders, so cover is always right here.
            div.innerHTML = '<img draggable="false" loading="lazy" alt="' + esc(it.title || '') +
              '" style="object-fit:cover" src="' + esc(it.thumb) + '">';
          }
          div.__agmfItem = it;
          return div;
        }
        function extend() {
          if (tailEls.length >= CAP) return false;
          for (var b = 0; b < BATCH && tailEls.length < CAP; b++) {
            var el2 = slideFor(next());
            track.appendChild(el2);
            tailEls.push(el2);
            watch(el2);
          }
          slideCount = railCount(track);
          lastDotsKey = '';
          paintDots(railIndex(track));
          manageVideos();
          return true;
        }
        // WHAT WAS SEEN. Half the slide on screen inside the track counts,
        // the same bar the feed's own ledger uses for a glance.
        var io = null;
        try {
          // Against the SCREEN (no root): a root of `track` measured slides
          // against the track's own box, which on a desktop grid held every
          // slide at once. The seen ledger records these slides itself -
          // they carry data-agmf-ck and surface "card" like every hero
          // slide - so nothing is marked twice here.
          io = new IntersectionObserver(function (entries) {
            entries.forEach(function (en) {
              if (!en.isIntersecting || en.intersectionRatio < 0.5) return;
              var it = en.target.__agmfItem;
              if (!it || !it.ck) return;
              heroShowedItem(it.ck);
            });
          }, { threshold: [0.5] });
        } catch (e) { io = null; }
        function watch(el2) { if (io) io.observe(el2); }
        // Keep two slides of runway ahead of the shopper.
        var ticking = false;
        track.addEventListener('scroll', function () {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(function () {
            ticking = false;
            var i = railIndex(track), n = railCount(track);
            if (n - i <= 2) extend();
          });
        }, { passive: true });
        extend();
        // A TABLET TURNED SIDEWAYS becomes the desktop grid, where the tail
        // slides would sit as product-gallery cells. Fold them away while
        // the page is wide; bring them back when it is a phone again.
        var folded = false;
        function syncColumns() {
          var wide = columns() >= 2;
          if (wide === folded) return;
          folded = wide;
          tailEls.forEach(function (el2) {
            el2.style.display = wide ? 'none' : '';
            if (wide) el2.setAttribute('data-agmf-parked', ''); else el2.removeAttribute('data-agmf-parked');
            if (io) { if (wide) io.unobserve(el2); else io.observe(el2); }
          });
          slideCount = railCount(track);
          lastDotsKey = '';
          paintDots(railIndex(track));
        }
        window.addEventListener('resize', function () { setTimeout(syncColumns, 250); });
        return {
          reattach: function () { tailEls.forEach(function (el2) { if (!heroSlideGone(el2)) track.appendChild(el2); }); slideCount = railCount(track); lastDotsKey = ''; paintDots(railIndex(track)); },
          size: function () { return tailEls.length; },
          pool: function () { return pool.length; }
        };
      }());

      // A long video in the gallery shouldn't dead-end the browse: when it
      // finishes (or after 30s of a very long one), slide to the next frame
      // (Dorian 2026-08-12). Any manual swipe cancels the auto-advance for
      // that slide - the shopper is driving.
      //
      // This is the PRODUCT CARD's answer to "when a video finishes", set in
      // Agilite Media and shipped in the feed. Off means off: no listeners
      // are attached, `loop` is left as the shell shipped it, and the polling
      // timer below is never started.
      (function heroAutoAdvance() {
        if (!AUTO.productCard) return;
        var advanced = {};
        function nextSlide() {
          var i = railIndex(track);
          if (i >= railCount(track) - 1) return;           // last frame: stay
          track.scrollTo({ left: railOffset(track, i + 1), behavior: 'smooth' });
        }
        el.querySelectorAll('.agmf-hero-slide video').forEach(function (v, i) {
          // A LOOP IS NEVER ADVANCED. It also keeps its looping: breaking that
          // to force an 'ended' is exactly what made a clip meant to repeat
          // stop after one pass.
          if (v.closest('[data-agmf-loop], .agmf-hero-slide--tail')) return;
          v.addEventListener('ended', function () { if (!advanced.manual && !advanced[i]) { advanced[i] = 1; nextSlide(); } });
          // `loop` on a gallery video would never fire 'ended'
          v.loop = false;
          v.addEventListener('timeupdate', function () {
            if (advanced.manual || advanced[i] || !v.duration) return;
            if (v.duration > 30 && v.currentTime > 30) { advanced[i] = 1; nextSlide(); }
          });
        });
        // YouTube frames report through the jsapi bridge: when a hero embed
        // is inside 1.5s of its end, move on
        setInterval(function () {
          el.querySelectorAll('[data-agmf-yt]').forEach(function (sh, i) {
            // a created loop runs a range of a longer video: `at > 30` would
            // have walked off one of ours after thirty seconds
            if (sh.closest('[data-agmf-loop]')) return;
            var dur = parseFloat(sh.dataset.agmfDur), at = parseFloat(sh.dataset.agmfAt);
            if (!(dur > 0) || !(at >= 0) || advanced.manual || advanced['y' + i]) return;
            if (dur - at < 1.5 || at > 30) { advanced['y' + i] = 1; nextSlide(); }
          });
        }, 1000);
        // THE SHOPPER IS DRIVING. This flag was written here and read
        // nowhere, so the gallery kept advancing under a swipe; the three
        // triggers above now all check it.
        track.addEventListener('pointerdown', function () { advanced.manual = 1; }, { passive: true });
      }());

      // PRODUCT MEDIA HAS ONE ALLOWED CARD FORMAT: a single 3:4 grid slot.
      // Colour filtering can leave an odd count, but layout parity must never
      // resize a shopper-visible photo or clip. Clear the legacy class only.
      //
      // ...AND AN ODD COUNT ENDS ON A LARGE PICTURE (Dorian 2026-10-22: "if
      // there is an odd number of images for the product card then in desktop
      // the last one should be a large one, and we prevent to have one of the
      // next content to go up like its part of the product card"). The
      // 2026-10-07 filler - one feed piece pulled into the empty cell - read
      // as part of the product card and is gone. The last picture now takes
      // the whole row (pdp-desk-3col.css); nothing is added to the card.
      function balanceHeroRows() {
        var wide = 'agmf-hero-slide--wide';
        var all = [].slice.call(track.children);
        all.forEach(function (el) { el.classList.remove(wide); });
        track.__agmfBalW = track.clientWidth;   // balanceTick re-runs this on a new width
        // The odd last picture is made large by pdp-desk-3col.css, which counts
        // the slides the colour deal shows (data-agmf-parked). This only clears
        // a class left by older markup.
      }
      // A LOOP IN THE CARD COUNTS AS SEEN ONLY WHERE IT IS SEEN: half of it on
      // screen, the bar every other surface uses. A rail slide parked off to
      // the side is clipped by its track, so a phone reports it only once the
      // shopper swipes to it; the desktop grid reports it when it scrolls into
      // view. The report is what takes it back out of the feed (see the
      // card-only note in buildOrder).
      (function watchCardLoops() {
        var loops = track.querySelectorAll('.agmf-hero-slide[data-agmf-loop][data-agmf-ck]');
        if (!loops.length || typeof IntersectionObserver !== 'function') return;
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (!en.isIntersecting || en.intersectionRatio < 0.5) return;
            if (en.target.hasAttribute('data-agmf-parked')) return;
            heroShowedItem(en.target.getAttribute('data-agmf-ck'));
          });
        }, { threshold: [0.5] });
        [].forEach.call(loops, function (s) { io.observe(s); });
      }());
      track.__agmfBalance = balanceHeroRows;
      balanceHeroRows();
      window.addEventListener('resize', function () { setTimeout(balanceHeroRows, 200); });
      (function () {
        var was = document.documentElement.classList.contains('agdesk-on');
        new MutationObserver(function () {
          var is = document.documentElement.classList.contains('agdesk-on');
          if (is === was) return;
          was = is;
          balanceHeroRows();
          requestAnimationFrame(balanceHeroRows);   // once the grid has settled
        }).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      }());

      // the gallery is a rail like any other: one image per swipe
      ownHorizontalGesture(track);

      // THE MARKER FOLLOWS THE RAIL, not the scroll event. `scroll` is
      // dispatched during the rendering step, so when frames are throttled
      // (background tab, battery saver) the rail moves and the dots go stale
      // - measured 2026-08-25: scrollLeft advanced a full slide with ZERO
      // scroll events. The engine drives this rail itself, so it reports its
      // own landing instead of waiting to be told.
      (function reportLandings() {
        var previous = onRailSettled;
        onRailSettled = function () {
          previous();
          paintDots(railIndex(track));
        };
      }());

      // ---- VARIANT -> PICTURE (Dorian 2026-08-19): choosing a colour jumps
      // the card to that colour's picture. The theme's picker dispatches a
      // bubbling variant:change on the product form; the slide to land on is
      // found by the feed's own colour stamp first (the library renders name
      // their colour), then by the variant's featured image (studio shots).
      (function heroVariantJump() {
        var pdata = null;
        var colourIdx = -1;
        try {
          pdata = JSON.parse(document.querySelector('[data-agmf-product-data]').textContent);
          var opts = (pdata && pdata.options) || [];
          for (var oi = 0; oi < opts.length; oi++) {
            if (/colou?r/i.test(opts[oi].name || '')) { colourIdx = oi; break; }
          }
        } catch (err) { /* no options payload: the featured-image route still works */ }
        function base(u) { return String(u || '').split('/').pop().split('?')[0]; }
        function jumpFor(v, behavior) {
          if (!v || !document.body.contains(track)) return;
          // a bundle's lead takes the colour's own picture first
          var leadMoved = setLead(leadPicFor(v));
          var target = -1;
          if (colourIdx !== -1 && v.options && v.options[colourIdx]) {
            var want = String(v.options[colourIdx]).toLowerCase();
            for (var i = 0; i < images.length; i++) {
              var ic = images[i].colours || (images[i].colour ? [images[i].colour] : []);
              for (var ci = 0; ci < ic.length; ci++) {
                if (String(ic[ci]).toLowerCase() === want) { target = i; break; }
              }
              if (target !== -1) break;
            }
            if (target !== -1) {
              // the deck re-deals: this colour's pictures, then the general
              // ones - a rival colour's render never follows (Dorian's rule)
              reDeal(v.options[colourIdx]);
              return;
            }
          }
          var fi = v.featured_image;
          if (target === -1 && fi) {
            for (var j = 0; j < images.length; j++) {
              if (leadFiles[fileOf(images[j].src)]) continue;   // a bundle picture: the lead shows it
              if ((fi.id != null && images[j].id != null && String(images[j].id) === String(fi.id)) ||
                  (fi.src && base(images[j].src) === base(fi.src))) { target = j; break; }
            }
          }
          if (target === -1) {
            // the colour's picture IS the lead: bring it into view
            if (leadMoved) track.scrollTo({ left: 0, behavior: behavior || 'smooth' });
            return;
          }
          // the lead slide sits in front of the deck's pictures
          track.scrollTo({ left: (target + (leadSlide ? 1 : 0)) * track.clientWidth, behavior: behavior || 'smooth' });
        }
        document.addEventListener('variant:change', function (e) {
          jumpFor(e.detail && e.detail.variant);
        });
        // CATCH-UP (Dorian 2026-08-20: 'it leaves the default picture until
        // we reclick'): colour-pref - or a ?variant= link - changes the
        // variant BEFORE this card exists, and that event is gone. The form's
        // own variant input remembers; the missed jump replays here, once.
        var idInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
        if (pdata && pdata.variants && idInput && idInput.value) {
          // UNCONDITIONAL (Dorian 2026-08-23: 'no matter what color it is,
          // the identified colour first, then general'): the ACTIVE colour -
          // preferred, clicked, linked, or simply the default - always gets
          // its partitioned path. The arranged order still rules WITHIN the
          // colour group and within the generals.
          for (var vi = 0; vi < pdata.variants.length; vi++) {
            if (String(pdata.variants[vi].id) === String(idInput.value)) {
              jumpFor(pdata.variants[vi], 'auto');
              break;
            }
          }
        }
        // A LOADOUT SOLD IN ONE COLOUR has no colour option to deal by; its
        // title names the colour ("Magnetix Battle Belt Loadout Black"). On a
        // bundle page, whose card is the individual's with every colour in
        // it, that colour is dealt as if it had been chosen - the longest
        // colour name the card knows wins ("multicam black" over "black").
        if (colourIdx === -1 && leadSlide) {
          var titled = String(hero.title || '').toLowerCase(), named = '';
          images.forEach(function (im) {
            (im.colours || (im.colour ? [im.colour] : [])).forEach(function (cc) {
              var c = String(cc).toLowerCase();
              if (c.length > named.length && titled.indexOf(c) !== -1) named = c;
            });
          });
          if (named) reDeal(named);
        }
        // a card fallen back to the bundle's own pictures parks the lead's
        // twin from the start (a colour deal above already did)
        if (deckHoldsLead) reDeal(lastDeal);
      }());
      markShownPriority();   // after the deal, on the slides actually shown

      track.addEventListener('scroll', function () {
        if (track.__agmfHeroRaf) return;
        track.__agmfHeroRaf = requestAnimationFrame(function () {
          track.__agmfHeroRaf = 0;
          var i = railIndex(track);
          paintDots(i);
          manageVideos();
        });
        // rAF is not guaranteed - frozen in a background tab, throttled under
        // battery saver - and the position marker going stale is worse than a
        // frame of lag. This settles it either way (measured 2026-08-25: the
        // strip read the same while the rail moved five slides).
        clearTimeout(track.__agmfDotTimer);
        track.__agmfDotTimer = setTimeout(function () {
          paintDots(railIndex(track));
          manageVideos();
        }, 70);
      }, { passive: true });

      // the title-row rating badge joins the hero head ONLY when the feed
      // carries no library rating (retries: Judge.me is async)
      if (productReviews && productReviews.count) return;
      (function captureBadge(attempt) {
        var b = document.querySelector('.jdgm-preview-badge');
        var slot = qs(el, '[data-agmf-hero-badge]');
        if (b && slot && !slot.contains(b) && !b.contains(el)) { slot.appendChild(b); return; }
        if (attempt < 8) setTimeout(function () { captureBadge(attempt + 1); }, attempt * 700 + 300);
      }(0));
    }

    document.addEventListener('agb:gs-ready', buildSuggestCard);

    // ---- mini suggestions card INSIDE the cart drawer (Dorian 2026-08-10):
    // after an add-to-cart, if this product has gear-builder/easy-suggestion
    // accessories, offer a compact 3-row version right in the drawer. Injected
    // by observing the drawer, fully guarded — drawer markup we don't own.
    // Dorian 2026-09-07: the drawer's "Complete your setup" moves INTO the
    // Ask card, where the bot can say why each accessory belongs with the
    // product (from the accessory's own page and the panel's notes) instead
    // of three bare rows in the drawer. Same pattern as SUGGEST_CARD_IN_FEED:
    // the code stays, the switch is off.
    var CART_CARD_IN_DRAWER = false;
    function buildCartSuggestCard() {
      if (!CART_CARD_IN_DRAWER) return;
      try {
        var gs = window.__AGB_GEAR_SUGG;
        var entry = gs && gs.byProduct && gs.byProduct[handle];
        var accs = (entry && entry.accessories) || [];
        if (!accs.length) return;
        var drawer = document.querySelector('#cart-drawer, cart-drawer, .cart-drawer');
        if (!drawer || drawer.querySelector('.agmf-cart-sugg')) return;
        var host = drawer.querySelector('form, .cart-drawer__items, [class*="items"]') || drawer;
        var box = document.createElement('div');
        box.className = 'agmf-cart-sugg';
        box.innerHTML = '<strong class="agmf-cart-sugg-title">Complete your setup</strong>' +
          '<div class="agmf-cart-sugg-rows"></div>' +
          '<button type="button" class="agmf-sugg-add" data-agmf-cart-add disabled>ADD SELECTED</button>';
        var rowsEl = qs(box, '.agmf-cart-sugg-rows');
        var addBtn = qs(box, '[data-agmf-cart-add]');
        box.addEventListener('change', function () {
          var n = box.querySelectorAll('input[data-agmf-variant]:checked').length;
          addBtn.disabled = n === 0;
          addBtn.textContent = n ? 'ADD SELECTED (' + n + ')' : 'ADD SELECTED';
        });
        addBtn.addEventListener('click', function () {
          var checked = box.querySelectorAll('input[data-agmf-variant]:checked');
          if (!checked.length) return;
          var props = {};
          try {
            if (window.AGB_Provenance) props = window.AGB_Provenance.props({ role: 'accessory', source: 'media-feed-cart' });
          } catch (err) { /* never load-bearing */ }
          addBtn.disabled = true; addBtn.textContent = 'ADDING…';
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [].map.call(checked, function (cb) {
              return { id: Number(cb.dataset.agmfVariant), quantity: 1, properties: props };
            }) }),
          }).then(function (r) {
            addBtn.textContent = r.ok ? 'ADDED ✓' : 'ADD SELECTED';
            addBtn.disabled = !r.ok;
            try { document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (err) { /* best-effort */ }
          }).catch(function () { addBtn.textContent = 'ADD SELECTED'; addBtn.disabled = false; });
        });
        accs.slice(0, 3).forEach(function (a) {
          var h = a && (a.handle || a.productHandle);
          if (!h) return;
          var row = document.createElement('label');
          row.className = 'agmf-sugg-row';
          row.innerHTML = '<input type="checkbox" class="agmf-sugg-check">' +
            '<span class="agmf-sugg-img"></span>' +
            '<span class="agmf-sugg-meta"><strong class="agmf-sugg-title"></strong>' +
            '<span class="agmf-sugg-price"></span></span>';
          rowsEl.appendChild(row);
          fetchProductJs(h)
            .catch(function () { return null; })
            .then(function (p) {
              var v = null;
              if (p) {
                for (var i = 0; i < (p.variants || []).length; i++) {
                  if (p.variants[i].available) { v = p.variants[i]; break; }
                }
                v = v || (p.variants && p.variants[0]);
              }
              if (!p || !v) { row.remove(); return; }
              if (p.featured_image) qs(row, '.agmf-sugg-img').style.backgroundImage = 'url("' + p.featured_image + '")';
              qs(row, '.agmf-sugg-title').textContent = p.title || h;
              qs(row, '.agmf-sugg-price').textContent = '$' + (v.price / 100).toFixed(2);
              var cb = qs(row, 'input');
              cb.dataset.agmfVariant = String(v.id);
              cb.disabled = !v.available;
            }).catch(function () { row.remove(); });
        });
        // UNDER the line items (Dorian 2026-08-11): between the items list
        // and the footer — never above the product. No items rendered (empty
        // cart) => no suggestions card at all.
        var itemsEl = drawer.querySelector('.cart-drawer__items');
        if (!itemsEl || !itemsEl.parentNode) { box.remove(); return; }
        itemsEl.parentNode.insertBefore(box, itemsEl.nextSibling);
      } catch (e) { /* the drawer is not ours — never break it */ }
    }

    if ('MutationObserver' in window) {
      new MutationObserver(function () { buildCartSuggestCard(); })
        .observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'open'] });
    }


    // ---- sticky ATC handoff: appears the instant the main button leaves view.
    // Geometric check inside the shared pass() rather than IntersectionObserver:
    // one code path that the harness can drive and real scrolls exercise anyway
    // (IO callbacks are render-driven and untestable in a hidden pane).
    var stickyBar = document.querySelector('[data-agmf-sticky]');
    var productBridge = window.AGMF_PRODUCT_BRIDGE || null;
    if (stickyBar && productBridge) {
      var stickyBtn = qs(stickyBar, '[data-agmf-sticky-atc]');
      if (stickyBtn) {
        stickyBtn.addEventListener('click', function () { productBridge.addToCart(); });
        productBridge.subscribe(function () {
          var state = productBridge.getAddToCartState();
          if (state.label && stickyBtn.textContent !== state.label) stickyBtn.textContent = state.label;
          stickyBtn.disabled = state.disabled;
          stickyBtn.setAttribute('aria-disabled', String(state.disabled));
        });
      }
    }

    // ---- variant drawer (prototype v9): the sticky bar's ghost button
    // opens a bottom sheet of REAL option rows; APPLY sets the real product
    // form's variant input, so the ATC (and every system on it) adds the
    // chosen variant ----
    (function initDrawer() {
      var drawer = document.querySelector('[data-agmf-drawer]');
      var scrim = document.querySelector('[data-agmf-drawer-scrim]');
      var sizeBtn = document.querySelector('[data-agmf-sticky-size]');
      var pEl = document.querySelector('[data-agmf-product-data]');
      if (!drawer || !sizeBtn || !pEl) return;
      var pdata = null;
      try { pdata = JSON.parse(pEl.textContent); } catch (err) { return; }
      if (!pdata || !pdata.options || !pdata.options.length || !pdata.variants) return;

      var sel = {};
      var cur = null;
      for (var vi = 0; vi < pdata.variants.length; vi++) {
        if (pdata.variants[vi].id === pdata.current) { cur = pdata.variants[vi]; break; }
      }
      pdata.options.forEach(function (o, oi) {
        sel[o.name] = cur ? cur.options[oi] : o.values[0];
      });

      /* ── THE SHEET OPENS ON WHAT THE PAGE HOLDS ───────────────────────
         Review 2026-10-11: `current` above is the variant LIQUID rendered -
         the product's default. Colour-pref, a builder's own default
         (K19 opens on Multicam Black), a swatch tapped in the buy column:
         each moves the page's variant after that, and the sheet still
         opened on the default. Measured: K-Zero SF with Black preferred ->
         page on Black, sheet on "Color=Multicam $309.60", and an untouched
         APPLY added Multicam; Magnetix with Black preferred -> form on
         Black / Small, chip "Multicam · Small".
         So the selection is read from the page each time the sheet opens -
         the id in the picker's own product form first: that is the variant
         Add to cart adds (a gear builder's add-all reads that same input
         before anything else; its window.baseItem can lag a swatch behind,
         main-product-agb-system 2026-07-13). Then the product bridge's
         answer, then any add-to-cart form - and only an id that is one of
         THIS product's variants counts. Between opens, the page's
         variant:change keeps it (and the chip) in step. */
      var variantById = {};
      pdata.variants.forEach(function (v) { variantById[String(v.id)] = v; });

      function pageVariant() {
        var ids = [];
        var picker = document.querySelector('variant-picker, .variant-picker');
        var formId = picker && picker.getAttribute('form');
        var form = (formId && document.getElementById(formId)) || (picker && picker.closest && picker.closest('form'));
        var own = form && form.querySelector('[name="id"]');
        if (own) ids.push(own.value);
        var bridge = window.AGMF_PRODUCT_BRIDGE;
        if (bridge && typeof bridge.getSelectedVariantId === 'function') {
          try { ids.push(bridge.getSelectedVariantId()); } catch (err) {}
        }
        var any = document.querySelectorAll('form[action*="/cart/add"] [name="id"]');
        for (var a = 0; a < any.length; a++) ids.push(any[a].value);
        for (var i = 0; i < ids.length; i++) {
          if (ids[i] != null && variantById[String(ids[i])]) return variantById[String(ids[i])];
        }
        return null;
      }

      function selectFrom(v) {
        pdata.options.forEach(function (o, oi) { sel[o.name] = v.options[oi]; });
      }

      // the chip names what the page holds, in the words APPLY writes
      function chipFor(v) {
        var label = document.querySelector('[data-agmf-sel-label]');
        var text = v && v.options ? v.options.join(' · ') : '';
        if (label && text && label.textContent !== text) label.textContent = text;
      }

      (function fromPage() {
        var v = pageVariant();
        if (v) { selectFrom(v); chipFor(v); }
      }());

      function matchVariant() {
        for (var i = 0; i < pdata.variants.length; i++) {
          var v = pdata.variants[i], hit = true;
          for (var oi = 0; oi < pdata.options.length; oi++) {
            if (v.options[oi] !== sel[pdata.options[oi].name]) { hit = false; break; }
          }
          if (hit) return v;
        }
        return null;
      }

      // A COLOUR IS A COLOUR HERE - not a word, and not the product photo.
      // The section resolves each value against the theme's own
      // color_swatch_config, so a camo gets its fabric tile and a solid gets
      // its hex, exactly as the variant picker higher up the page does.
      // Same config, same files, same fallback: the two can never disagree.
      function swatchFor(value) {
        return (pdata.swatches && pdata.swatches[value]) || '';
      }
      function isColourOption(name) { return /colou?r/i.test(name || ''); }
      function isSizeOption(name) { return /\bsize\b/i.test(name || ''); }

      // THE SIZE CHART, WHERE THE SIZES ARE. It already exists on the page -
      // in the Sizing tab of the product accordion - and a shopper choosing a
      // size in this sheet should not have to close it, scroll, and find it.
      // Read from the page rather than fetched: same content, no round trip,
      // and nothing to keep in sync.
      var _chart;
      function sizeChart() {
        if (_chart !== undefined) return _chart;
        _chart = null;
        try {
          // the tab whose BUTTON says sizing - never a fixed tab id, those are
          // positional and differ per product
          var btns = document.querySelectorAll('.tablinks-cus');
          for (var i = 0; i < btns.length; i++) {
            if (!/siz/i.test(btns[i].textContent || '')) continue;
            var m = (btns[i].getAttribute('onclick') || '').match(/['"]([^'"]+)['"]\s*\)/);
            var panel = m && document.getElementById(m[1]);
            // A BUTTON THAT NO LONGER NAMES ITS PANEL. Magnetix's description
            // lost its buttons' onclick (2026-10: `<button class="tablinks-cus">
            // Sizing</button>`, the chart still in its panel), so this found
            // nothing and the sheet's "Size chart" quietly disappeared. The
            // buttons and their panels are written in the same order, so the
            // n-th button of a tab bar opens the n-th panel beside that bar.
            if (!panel) {
              var bar = btns[i].parentElement;
              var own = bar ? Array.prototype.slice.call(bar.querySelectorAll('.tablinks-cus')) : [];
              var panels = bar && bar.parentElement
                ? bar.parentElement.querySelectorAll('.tabcontent-cus') : [];
              var at = own.indexOf(btns[i]);
              panel = at >= 0 && at < panels.length ? panels[at] : null;
            }
            var img = panel && panel.querySelector('img');
            if (img && img.getAttribute('src')) { _chart = img.getAttribute('src'); break; }
          }
        } catch (e) {}
        return _chart;
      }

      /* ── THE DRAWER IS A SECOND FACE OF THE SAME PICKER ───────────────
         Dorian 2026-08-31: choosing a colour or a size down here should move
         the product card's own selection, and the strip's thumbnail, with it.
         Until now a choice only re-drew the sheet; nothing else on the page
         knew until APPLY, and the strip never knew at all.

         It DRIVES THE THEME'S OWN CONTROL rather than repeating what that
         control does. `variant-picker` is radios named option1..optionN, so
         ticking the right one and letting the browser fire `change` makes the
         theme move its own swatches, the price, the availability and the
         gallery, and emit the `variant:change` that the hero deck already
         listens to for its colour re-deal. Every one of those repeated here
         would be a second implementation to keep in step with the first.

         `.click()` rather than setting `checked` and dispatching by hand: it
         is the real event sequence, and a radio already on its value does
         nothing, so this is safe to call after every choice. The inventory
         cloaking parks artificial `__x-N` swatches in the same radio group -
         matching on the option VALUE is what steps over them. */
      function syncPicker() {
        var picker = document.querySelector('variant-picker, .variant-picker');
        if (!picker) return;
        pdata.options.forEach(function (o, oi) {
          var want = sel[o.name];
          if (!want) return;
          var inputs = picker.querySelectorAll('input[name="option' + (oi + 1) + '"]');
          for (var i = 0; i < inputs.length; i++) {
            if (inputs[i].value !== want) continue;
            if (!inputs[i].checked) inputs[i].click();
            return;
          }
        });
      }

      /* The strip's thumbnail is painted once by Liquid from the product's
         featured image, so it kept showing the first colour forever. The
         exact variant is used when the sheet has a whole answer; with only a
         colour picked so far, the first variant of that colour carries the
         same picture. A variant with no image of its own leaves the strip
         alone rather than blanking it. */
      function variantImage() {
        var v = matchVariant();
        if (v && v.image) return v.image;
        var want = [];
        pdata.options.forEach(function (o, oi) { want[oi] = sel[o.name]; });
        for (var i = 0; i < pdata.variants.length; i++) {
          var vv = pdata.variants[i], ok = true;
          for (var k = 0; k < want.length; k++) {
            if (want[k] && vv.options[k] !== want[k]) { ok = false; break; }
          }
          if (ok && vv.image) return vv.image;
        }
        return null;
      }

      function syncReturnThumb() {
        var thumb = document.querySelector('.agmf-return-thumb');
        var src = variantImage();
        if (thumb && src) thumb.style.backgroundImage = 'url("' + src + '")';
      }

      function render() {
        var rows = qs(drawer, '[data-agmf-drawer-rows]');
        var html = '';
        pdata.options.forEach(function (o, oi) {
          var colour = isColourOption(o.name);
          var chart = isSizeOption(o.name) ? sizeChart() : null;
          html += '<div class="agmf-drawer-row' + (colour ? ' agmf-drawer-row--colour' : '') + '">' +
            '<span class="agmf-drawer-label">' + esc(o.name) + '</span>' +
            '<div class="agmf-drawer-choices' + (colour ? ' agmf-drawer-choices--swatch' : '') + '">';
          o.values.forEach(function (val) {
            // The swatch and the size button are different controls with
            // different selected styles. One shared class meant a chosen
            // COLOUR was marked with the size button's rule, which the
            // swatch's own transparent border - declared later in the
            // stylesheet - then overwrote. Nothing looked selected.
            var on = sel[o.name] === val
              ? (colour ? ' agmf-drawer-swatch--on' : ' agmf-drawer-choice--on')
              : '';
            if (colour) {
              var bg = swatchFor(val);
              html += '<button type="button" class="agmf-drawer-swatch' + on +
                '" data-agmf-choice data-opt="' + esc(o.name) + '" data-val="' + esc(val) +
                '" title="' + esc(val) + '" aria-label="' + esc(val) + '">' +
                '<span class="agmf-drawer-swatch-chip" aria-hidden="true"' +
                (bg ? ' style="background:' + esc(bg) + '"' : '') + '></span>' +
                '</button>';
            } else {
              html += '<button type="button" class="agmf-drawer-choice' + on +
                '" data-agmf-choice data-opt="' + esc(o.name) + '" data-val="' + esc(val) + '">' +
                esc(sizeAbbrev(val)) + '</button>';
            }
          });
          // NEXT TO THE LAST SIZE, as a word. It was a thumbnail parked at
          // the far right of the row, which read as a picture somebody had
          // dropped there rather than as the thing you press after reading
          // "XL" and not knowing which one you are (Dorian 2026-08-30). The
          // picture itself is one tap away and belongs full size, not as a
          // 56px tile nobody can read.
          if (chart) {
            html += '<button type="button" class="agmf-drawer-chartlink" data-agmf-chart ' +
              'data-chart="' + esc(chart) + '">Size chart</button>';
          }
          html += '</div></div>';
        });
        rows.innerHTML = html;
        var v = matchVariant();
        var priceEl = qs(drawer, '[data-agmf-drawer-price]');
        var applyBtn = qs(drawer, '[data-agmf-drawer-apply]');
        if (priceEl && v) priceEl.textContent = v.price;
        if (applyBtn) {
          applyBtn.disabled = !v || !v.available;
          applyBtn.textContent = !v ? 'UNAVAILABLE' : (v.available ? 'APPLY SELECTIONS' : 'OUT OF STOCK');
        }
      }

      // WHAT HAPPENED IN THE SHEET, said out loud (Dorian 2026-09-06: the
      // variant selector in the bottom button has to collect data). The
      // tracker listens and puts it on the wire; this file never talks to
      // analytics itself, exactly as with the heart.
      function tell(step, extra) {
        try {
          var v = matchVariant();
          document.dispatchEvent(new CustomEvent('agmf:variant', {
            bubbles: true,
            detail: Object.assign({
              step: step,
              options: Object.assign({}, sel),
              variant_id: v ? v.id : null,
              available: v ? !!v.available : null
            }, extra || {})
          }));
        } catch (err) { /* a report is never a reason to stop the sheet */ }
      }

      /* ── A CLOSE WITHOUT APPLY PUTS THE PAGE BACK ───────────────────────
         A choice previews on the page as it is tapped (syncPicker, above) -
         Dorian 2026-08-31. Review 2026-10-11: so a SOLD-OUT colour tapped
         and refused at APPLY ("OUT OF STOCK") stayed on the page after the
         sheet was closed - a greyed Add to cart and the earlier choice lost
         (K19 Coyote Brown; K-Zero Black / Medium; Magnetix Black / Small).
         Closing with the X or the scrim now returns the page to the variant
         it held when the sheet opened, through the same picker; the close
         says so (`restored`) so a gear builder can follow. APPLY keeps the
         choice. */
      var openedWith = null;
      function sameSelection(a, b) {
        for (var i = 0; i < pdata.options.length; i++) {
          var name = pdata.options[i].name;
          if (a[name] !== b[name]) return false;
        }
        return true;
      }

      function open(openIt, applied) {
        var was = drawer.classList.contains('agmf-drawer--open');
        var extra = null;
        if (openIt && !was) {
          var held = pageVariant();
          if (held) selectFrom(held);
          openedWith = Object.assign({}, sel);
        } else if (!openIt && was) {
          if (!applied && openedWith && !sameSelection(sel, openedWith)) {
            pdata.options.forEach(function (o) { sel[o.name] = openedWith[o.name]; });
            syncPicker();
            syncReturnThumb();
            extra = { restored: true };
          }
          openedWith = null;
        }
        drawer.classList.toggle('agmf-drawer--open', openIt);
        drawer.setAttribute('aria-hidden', String(!openIt));
        if (scrim) scrim.hidden = !openIt;
        if (openIt) render();
        if (was !== !!openIt) tell(openIt ? 'open' : 'close', extra);
      }

      // the page moved on its own (a swatch in the buy column, colour-pref
      // arriving late, a builder settling): the chip names it, and a closed
      // sheet follows. Another product's variant (a quick add) is not ours.
      document.addEventListener('variant:change', function (e) {
        var v = e && e.detail && e.detail.variant;
        var mine = v && v.id != null ? variantById[String(v.id)] : null;
        if (!mine) return;
        chipFor(mine);
        if (!drawer.classList.contains('agmf-drawer--open')) selectFrom(mine);
      });

      sizeBtn.addEventListener('click', function () { open(true); });
      if (scrim) scrim.addEventListener('click', function () { open(false); });
      drawer.addEventListener('click', function (e) {
        if (e.target.closest('[data-agmf-drawer-close]')) { open(false); return; }
        var chartBtn = e.target.closest('[data-agmf-chart]');
        if (chartBtn) {
          tell('size_chart');
          // full size, over the sheet, dismissed by a tap anywhere
          var src = chartBtn.getAttribute('data-chart');
          if (src) {
            var ov = document.createElement('div');
            ov.className = 'agmf-chart-overlay';
            ov.innerHTML = '<img src="' + esc(src) + '" alt="Size chart">';
            ov.addEventListener('click', function () { ov.remove(); });
            document.body.appendChild(ov);
          }
          return;
        }
        var c = e.target.closest('[data-agmf-choice]');
        if (c) {
          sel[c.getAttribute('data-opt')] = c.getAttribute('data-val');
          render();
          syncPicker();
          syncReturnThumb();
          tell('choose', { option_name: c.getAttribute('data-opt'), option_value: c.getAttribute('data-val') });
          return;
        }
        if (e.target.closest('[data-agmf-drawer-apply]')) {
          var v = matchVariant();
          if (!v || !v.available) {
            // the button is disabled on these, but a tap on it still says
            // what the shopper built and could not buy
            tell('apply_refused', { reason: !v ? 'no_such_variant' : 'sold_out' });
            return;
          }
          // ONLY THIS PRODUCT'S FORMS (review 2026-10-11): every add-to-cart
          // form on the page was given the chosen id, the complementary
          // product card's too - on AMAP III its Add to cart then added the
          // $143.91 pack instead of the $44.91 accessory. A form holding
          // another product's variant is left alone.
          document.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach(function (inp) {
            if (variantById[String(inp.value)]) inp.value = String(v.id);
          });
          var label = document.querySelector('[data-agmf-sel-label]');
          if (label) label.textContent = v.options.join(' \u00B7 ');
          tell('apply', { ok: true });
          open(false, true);
        }
      });
    })();

    // The theme's own header is position:sticky — the return strip must sit
    // BELOW it, not fight it at top:0. Measured, not hardcoded: header height
    // differs between desktop/mobile and with the announcement bar.
    function headerOffset() {
      // The bar must ALWAYS hang below the site header - at the very top of
      // the page the header is still in normal flow, so the old sticky-only
      // test returned 0 and our fixed bar covered the logo
      // (Dorian 2026-08-12). Take the header's real bottom edge, whatever
      // its position mode, clamped to the viewport.
      var cands = document.querySelectorAll(
        '#shopify-section-header, store-header, header.site-header, .site-header, header');
      var best = 0;
      for (var i = 0; i < cands.length; i++) {
        var el = cands[i];
        var r = el.getBoundingClientRect();
        // MEASURED on the live PDP: at the top of the page the promo bar
        // pushes the sticky header to top:35/bottom:120, so a "must be at
        // top<=4" test returned 0 and our bar covered the logo. Anything
        // occupying the top strip counts.
        if (r.height < 20 || r.bottom <= 0) continue;
        if (r.top > 60 || r.bottom > 220) continue;
        best = Math.max(best, Math.round(r.bottom));
      }
      return best;
    }

    // The header keeps RESIZING after first paint (the promo bar collapses,
    // fonts land), and every fixed timer I tried measured a stale box - the
    // tab bar kept sitting 8px over the logo. Watch the header itself: any
    // box change re-runs the offset. (Dorian 2026-08-12, measured in-page.)
    (function watchHeader() {
      var hdr = document.querySelector(
        '#shopify-section-header, store-header, header.site-header, .site-header, header');
      if (!hdr) { setTimeout(watchHeader, 500); return; }
      headerTick(); accBarTick();
      if (typeof ResizeObserver === 'function') {
        new ResizeObserver(function () { headerTick(); accBarTick(); }).observe(hdr);
      }
      // a promo bar ABOVE the header changes its top without resizing it
      var last = -1;
      setInterval(function () {
        var b = Math.round(hdr.getBoundingClientRect().bottom);
        if (b !== last) { last = b; headerTick(); accBarTick(); }
      }, 250);
    }());
    window.addEventListener('resize', function () { headerTick(); accBarTick(); }, { passive: true });

    function headerTick() {
      var off = headerOffset() + 'px';
      section.style.setProperty('--agmf-header-offset', off);
      // the accordion tab bar lives on <body>, OUTSIDE the section - without
      // this it read 0px and crammed under the site header (his screenshot)
      document.documentElement.style.setProperty('--agmf-header-offset', off);
    }

    function bottomTick() {
      var lift = 0;
      var pb = document.getElementById('preview-bar-iframe') ||
               document.querySelector('iframe[id*="preview-bar"], #preview-bar');
      if (pb) {
        var r = pb.getBoundingClientRect();
        var vh = window.innerHeight || 1;
        if (r.height > 0 && r.bottom > vh - 4 && r.top > vh * 0.5) lift = Math.round(r.height);
      }
      document.documentElement.style.setProperty('--agmf-bottom-lift', lift + 'px');
      // THE FREE-PATCH BANNER OWNS bottom:0 (review 2026-10-11). The
      // countdown banner (#ttb-floating-banner, ticking-time-bomb.js: fixed,
      // bottom 0, z-index 998) comes up on a visitor's first add to cart for
      // three hours, and from the feed down the phone's only buy bar - ours,
      // z-index 995 - sat under it: the tap landed on the timer, and K19
      // showed nothing but "Free Patch Will Disappear in: 02:59:42". The bar
      // and its sheet sit on top of the banner, as the resume bar already
      // does (updateResumeBar).
      var ttb = document.getElementById('ttb-floating-banner'), ttbLift = 0;
      if (ttb) {
        var tb = ttb.getBoundingClientRect();
        if (tb.height > 0 && getComputedStyle(ttb).display !== 'none') ttbLift = Math.round(tb.height);
      }
      document.documentElement.style.setProperty('--agmf-ttb-lift', ttbLift + 'px');
    }
    // the banner arrives, hides and leaves without a scroll: follow it
    (function watchTtbBanner() {
      if (typeof MutationObserver !== 'function' || !document.body) return;
      var watched = null;
      var attrs = new MutationObserver(function () { bottomTick(); });
      function hook() {
        var b = document.getElementById('ttb-floating-banner');
        if (b !== watched) {
          attrs.disconnect();
          watched = b;
          if (b) attrs.observe(b, { attributes: true, attributeFilter: ['style', 'hidden', 'class'] });
        }
        bottomTick();
      }
      new MutationObserver(hook).observe(document.body, { childList: true });
      hook();
    }());

    // ---- swipe tease (prototype inlineSwipeTease): after 2s dwelling on a
    // slidable card whose track is still untouched, nudge the content so the
    // next slide peeks, then spring back. Once per card. ----
    var teaseCard = null;
    var teaseSince = 0;
    var lastScrollAt = 0;
    var lastScrollY = -1;

    // The clock starts when the SHOPPER STOPS, not when a card happens to
    // become the most visible one (Dorian 2026-08-16). Counting from the
    // latter meant the two seconds were mostly spent mid-scroll: the nudge
    // either fired at a card flying past, or fired the instant they landed,
    // and either way it never read as "there is more here, swipe".
    // ── THE NUDGE ONLY EXISTS WHERE A SWIPE DOES ─────────────────────
    // Dorian, sharply, 2026-09-07: "the lookout effect is intended to let you
    // know you can swipe left right", so on a layout where you cannot, it is
    // noise advertising a gesture that does nothing.
    //
    // It is refused HERE, at the source, and no longer by a stylesheet. A CSS
    // refusal only stops the paint: the class still landed, the once-per-card
    // flag was still burned, and it took one orphan comment marker to let the
    // animation back through unnoticed (measured 2026-09-07). A guard in the
    // tick cannot be out-specified or commented out by accident.
    //
    // Phone and tablet only, under 1000px. NOT `agdesk-on` - that is 1200, so
    // gating on it left the whole 1000-1199 band teasing, which is the band he
    // was looking at. And the rail must genuinely have somewhere to go: a card
    // whose track does not overflow has no next slide to reveal.
    function swipeIsTheGesture() {
      if (document.documentElement.classList.contains('agdesk-on')) return false;
      return (window.innerWidth || 0) < 1000;
    }
    function railCanMove(track) {
      return !!track && track.scrollWidth > track.clientWidth + 4;
    }

    function teaseTick() {
      if (prefersReduced() || !swipeIsTheGesture()) return;
      var now = Date.now();
      if (window.scrollY !== lastScrollY) { lastScrollY = window.scrollY; lastScrollAt = now; }
      var c = mostVisibleCard();
      if (!c || !c.classList.contains('agmf-card--slidable')) { teaseCard = null; teaseSince = 0; return; }
      if (c !== teaseCard) { teaseCard = c; teaseSince = 0; }
      if (now - lastScrollAt < 350) { teaseSince = 0; return; }   // still moving
      // the dwell is counted from the LAST MOVEMENT, not from the moment we
      // noticed the stillness - otherwise the 350ms settling gate is added
      // on top and the nudge lands at 2.35s (measured)
      if (!teaseSince) teaseSince = lastScrollAt;
      if (now - teaseSince < 2000) return;                        // two seconds of rest
      var track = c.querySelector('.agmf-track');
      if (!track || track.dataset.agmfTeased || !railCanMove(track)) return;
      // the shopper already swiped -> the affordance taught itself
      if (Math.abs(track.scrollLeft - railOffset(track, 1)) > 2) return;
      track.dataset.agmfTeased = '1';
      track.classList.add('agmf-tease');
      setTimeout(function () { track.classList.remove('agmf-tease'); }, 1200);
    }

    // A clip ending is the natural moment to hint "there's another one"
    // (Dorian 2026-08-12) - same nudge as the dwell tease, fired once per
    // card when the video is inside its last ~2.5s.
    function endTeaseTick() {
      if (prefersReduced() || !swipeIsTheGesture()) return;
      var c = mostVisibleCard();
      if (!c || !c.classList.contains('agmf-card--slidable')) return;
      var track = c.querySelector('.agmf-track');
      if (!track || track.dataset.agmfEndTeased || !railCanMove(track)) return;
      if (Math.abs(track.scrollLeft - railOffset(track, 1)) > 2) return;   // already swiped
      var left = null;
      var v = c.querySelector('video[src]');
      if (v && v.duration && !v.paused) left = v.duration - v.currentTime;
      if (left === null) {
        var shell = c.querySelector('[data-agmf-yt]');
        var dur = shell && parseFloat(shell.dataset.agmfDur);
        var at = shell && parseFloat(shell.dataset.agmfAt);
        if (dur > 0 && at >= 0) left = dur - at;
      }
      if (left === null || left > 2.5 || left < 0.2) return;
      track.dataset.agmfEndTeased = '1';
      track.classList.add('agmf-tease');
      setTimeout(function () { track.classList.remove('agmf-tease'); }, 1200);
    }

    function stickyTick() {
      // ONE arrival state drives the whole chrome choreography (Dorian
      // 2026-08-11): reaching the feed's first media compacts the black
      // header (wordmark only), fades the fixed return strip in place, and
      // shows the bottom size+ATC bar.
      var vh2 = window.innerHeight || 1;
      var near = section.getBoundingClientRect().top < vh2 * 0.6;
      document.documentElement.classList.toggle('agmf-hdr-compact', near);
      if (returnBtn) {
        document.documentElement.style.setProperty('--agmf-strip-h', returnBtn.offsetHeight + 'px');
      }
      if (stickyBar && stickyBar.hidden !== !near) stickyBar.hidden = !near;
    }

    // ---- ONE pass shared by the live rAF loop and the harness tick ----
    // ── SOMETHING HAS TO ASK WHILE NOTHING IS HAPPENING (Dorian 2026-09-07:
    // the tease "is not working anymore").
    //
    // Both teases wait for the page to be STILL - teaseTick() wants two
    // seconds with no scroll movement - and the only thing that called them
    // was the scroll handler. So the question "has it been still for two
    // seconds?" was only ever asked while the page was moving, and the answer
    // was always no. Dated by the mapping pass to commit 58207c8
    // (2026-08-20), which rewrote the dwell from "this card became the most
    // visible one" (a fact true during a scroll) to "the last scroll
    // movement" (a fact true only at rest) without giving anything the job of
    // asking at rest. This timer is what that rewrite needed.
    //
    // Not on desktop: pdp-desk-3col.css refuses the animation there anyway
    // (the nudge advertises a gesture the mouse cannot make), and ticking
    // would burn the once-per-card `agmfTeased` flag on cards that never
    // animated - so a window resized down to a tablet would find every card
    // already used up.
    setInterval(function () {
      if (document.hidden || !swipeIsTheGesture()) return;
      teaseTick();
      endTeaseTick();
    }, 400);

    function pass() {
      ensureRunway();
      recycle();
      balanceTick();
      manageVideos();
      if (insideFeed() && section.getBoundingClientRect().top < 0) saveSession();
      activateNearby();
      teaseTick();
      endTeaseTick();
      headerTick();
      bottomTick();
      stickyTick();
      accBarTick();
      updateResumeBar();
    }

    function onScroll() {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = 0; pass(); });
    }

    // ---- boot ----
    // ── language serving ───────────────────────────────────────────────
    // Country -> language, only where a country really has ONE. Belgium and
    // Switzerland are deliberately absent: guessing French for a Belgian is a
    // coin toss, so they fall back to English.
    var COUNTRY_LANGUAGE = {
      IL: 'he', DE: 'de', AT: 'de', FR: 'fr', ES: 'es', MX: 'es', IT: 'it',
      PT: 'pt', BR: 'pt', NL: 'nl', PL: 'pl', CZ: 'cs', UA: 'uk', RU: 'ru',
      JP: 'ja', KR: 'ko', TH: 'th', TR: 'tr', GR: 'el', SE: 'sv', DK: 'da',
      RO: 'ro'
    };

    function shopperCountry() {
      // ASK THE COUNTRY THE STOREFRONT ACTUALLY KNOWS (Dorian 2026-09-03:
      // "start wiring the Country"). The geo app on this store writes the
      // visitor's country into localStorage as a NAME ("France"), and that is
      // the value he was looking at; Shopify's own is a code and is only
      // right where Markets is configured for this storefront. agmf-pref.js
      // resolves either into a code, so it answers first.
      try {
        if (window.AGMFPref && window.AGMFPref.country()) return window.AGMFPref.country();
      } catch (e) {}
      try {
        if (window.Shopify && window.Shopify.country) return String(window.Shopify.country).toUpperCase();
      } catch (e) {}
      var loc = (document.documentElement.lang || '').split('-');
      return (loc[1] || '').toUpperCase();
    }

    function applyLanguagePolicy(items, policy) {
      if (!policy || !policy.mode || policy.mode === 'off') return items;
      var cc = shopperCountry();
      // MORE THAN ONE LANGUAGE, AND MORE THAN A HANDFUL OF COUNTRIES. The
      // table below names 22 countries and one language each, and leaves out
      // the ones that speak two. agmf-pref.js carries the full list - every
      // Spanish-speaking country, Switzerland's three, Belgium's two - which
      // is what Dorian asked for ("all spanish talking countries should show
      // the content tagged as spanish"). This table stays as the fallback.
      var locals = [];
      try { if (window.AGMFPref) locals = window.AGMFPref.langs(); } catch (e) {}
      if (!locals.length) {
        var one = COUNTRY_LANGUAGE[cc];
        if (one) locals = [one];
      }
      // A country the library calls English-speaking prefers nothing in
      // particular. The field was already being shipped and never read.
      //
      // ...BUT NOT A LANGUAGE THE SHOPPER STATED. `englishCountries` is a
      // claim about a PLACE, so it may clear what the country guessed and
      // nothing else. Two people it was silencing, both measured 2026-09-07:
      // the shopper reading /de/products/magnetix-battle-belt (country US is
      // in this list, so `locals` was emptied, the branch below fell to
      // English-only, and the five German pieces the feed carries were cut -
      // on the German page); and the French speaker in the United States
      // whose device asks for French, who is the whole reason
      // navigator.languages is read at all. `stated()` is the locale plus the
      // device, without the country's inference.
      var stated = [];
      try { if (window.AGMFPref && window.AGMFPref.stated) stated = window.AGMFPref.stated(); } catch (e) {}
      if (policy.englishCountries && policy.englishCountries.indexOf(cc) !== -1) {
        locals = stated;
      }
      var local = locals.length ? locals[0] : null;
      // Content no language could be read from is NEVER cut. It is a quarter
      // of the library - photos with three-word captions - and dropping it
      // would empty the feed to prove a point.
      function unreadable(it) { return !it.language || it.language === 'unknown'; }
      function isEnglish(it) { return it.language === 'en'; }
      function isLocal(it) { return !!locals.length && locals.indexOf(it.language) !== -1; }

      if (policy.mode === 'englishOnly' || !locals.length) {
        return items.filter(function (it) { return isEnglish(it) || unreadable(it); });
      }
      if (policy.mode === 'localOnly') {
        var strict = items.filter(isLocal);
        // Most countries hold only a handful of cards in their language, so a
        // thin result falls back rather than showing an empty feed.
        if (strict.length >= 6) return strict;
        return items.filter(function (it) {
          return isLocal(it) || isEnglish(it) || unreadable(it);
        });
      }
      // localPlusEnglish
      var kept = items.filter(function (it) {
        return isLocal(it) || isEnglish(it) || unreadable(it);
      });
      if (!policy.prioritiseLocal) return kept;
      // HARMONISED, NOT A BLOCK. This used to be `mine.concat(rest)`: every
      // piece in the shopper's language first, then everything else - which
      // is the one thing Dorian ruled out ("dont just put all straight but it
      // should along with the international content"). blend() deals the two
      // piles alternately instead, so the local content leads without
      // arriving as a wall.
      try {
        if (window.AGMFPref && window.AGMFPref.blend) return window.AGMFPref.blend(kept);
      } catch (e) {}
      var mine = [], rest = [];
      kept.forEach(function (it) { (isLocal(it) ? mine : rest).push(it); });
      return mine.concat(rest);
    }

    // THE LIVE FEED FIRST, the theme asset second.
    //
    // Baking the feed into the theme meant every editorial change - a
    // thumbs-down, a card type switched off - waited for a theme push. That
    // is the wrong shape for something edited daily, and it was the single
    // cause of "why has nothing changed?" all through 2026-08-16.
    //
    // The bundled asset stays as the fallback, so the page still works if the
    // bucket is unreachable or the shopper is behind something that blocks it.
    // THIS PAGE'S OWN FEED, named by the section (snippets/agmf-feed-name.liquid).
    // It was the belt's address, typed here - so the moment a second product
    // carried the section it would have been shown the belt's media, the bucket
    // being read before the bundled copy. The belt's address stays only for an
    // older section markup that does not carry the attribute.
    var LIVE_FEED = section.dataset.liveFeedUrl ||
      'https://storage.googleapis.com/agilite-media-storefront/feeds/agilite-media-feed-v2.json';

    /** A feed worth rendering, or null. An EMPTY one is not worth rendering. */
    function usableFeed(feed) {
      return feed && Array.isArray(feed.items) ? feed : null;
    }

    /** The copy bundled into the theme - always there, sometimes older. */
    function bundledFeed() {
      return fetch(section.dataset.feedUrl || '', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }

    function loadFeed() {
      // A minute-granularity buster. Google's edge decides its own caching
      // and was still answering with an hour-old header after the object's
      // own cache-control was shortened - this makes the delay a minute
      // whatever the edge feels like doing, without hammering it per view.
      var minute = Math.floor(Date.now() / 60000);
      var url = LIVE_FEED + '?v=' + minute;
      // ALREADY IN FLIGHT, started in the head while the document was still
      // parsing. Reused only when it is the same URL, so the minute can never
      // roll over into two requests for one page.
      var warm = window.__agmfFeedWarm;
      var live = (warm && warm.url === url && warm.promise)
        ? warm.promise
        : fetch(url, { cache: 'no-cache' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; });
      // Both sources start together; neither can hold the product page
      // indefinitely. Compare timestamps so a newer reviewed preview wins.
      function bounded(promise) {
        return new Promise(function (resolve) {
          var timer = setTimeout(function () { resolve(null); }, 3500);
          promise.then(function (value) { clearTimeout(timer); resolve(value); },
            function () { clearTimeout(timer); resolve(null); });
        });
      }
      return Promise.all([bounded(live), bounded(bundledFeed())]).then(function (feeds) {
        return window.AGMF_PRODUCT_SOURCES
          ? window.AGMF_PRODUCT_SOURCES.selectFeed(feeds[0], feeds[1])
          : usableFeed(feeds[0]) || usableFeed(feeds[1]);
      });
    }

    function finishEmptyFeed() {
      buildProductHero([]);
      buildSpecAccordions();
      sentinel.classList.add('agmf-sentinel--end');
      sentinel.textContent = "That's everything for now.";
      document.documentElement.classList.add('agmf-feed-dead');
      if (heroBuilt) document.documentElement.classList.add('agmf-feed-live');
    }

    loadFeed()
      .then(function (feed) { return feed; })
      .then(function (feed) {
        if (!usableFeed(feed)) {
          // NOTHING TO SHOW. Say so, so the boot hold releases the page now
          // rather than leaving it hidden for the rest of the backstop.
          finishEmptyFeed();
          return;
        }
        // AUTOSWAP, chosen in Agilite Media. Read before anything renders, so
        // the first card built already knows. An older feed carries no answer
        // and keeps the fallbacks above.
        if (feed.autoAdvance) {
          if (typeof feed.autoAdvance.productCard === 'boolean') AUTO.productCard = feed.autoAdvance.productCard;
          if (typeof feed.autoAdvance.feed === 'boolean') AUTO.feed = feed.autoAdvance.feed;
        }
        // WHICH LANGUAGES THIS SHOPPER SEES, chosen in Agilite Media.
        // The feed is one file for every country this store sells to, so the
        // rule ships INSIDE it and is applied here, per visitor. Without this
        // the setting changed the file and nothing on the page.
        // The country -> languages list travels in the feed now; apply it
        // before anything asks what this shopper's languages are.
        try {
          if (window.AGMFPref && window.AGMFPref.useTable) {
            window.AGMFPref.useTable(feed.countryLanguages);
          }
        } catch (e) {}
        feed.items = applyLanguagePolicy(feed.items, feed.languagePolicy);
        // PER-PRODUCT SHARES of a mixed bundle feed (Dorian 2026-10-08), read
        // once the language filter has had its say (a product it empties drops
        // out of the mix) and before the card is built - the card and its
        // endless tail stay the card product's - and before the order is dealt.
        // No shares: null, and nothing below changes.
        try { sharePlan = makeSharePlan(feed); } catch (e) { sharePlan = null; }
        shareCardOwns = sharePlan ? sharePlan.owns : null;
        // satellites (the Ask card) read the recipe from here
        window.AGMF_FEED = feed;
        // Product surfaces do not depend on the endless feed having cards.
        // Establish their optional overrides before any empty-state return.
        specPanel = feed.specPanel || null;
        productReviews = feed.productReviews || null;
        heroImages = (feed.heroImages && feed.heroImages.length) ? feed.heroImages : null;
        heroPinned = !!feed.heroPinned;
        heroFeedTypesOff = (feed.invocation && feed.invocation.cardTypes && feed.invocation.cardTypes.typesOff) || [];
        cardEndless = (feed.cardEndless && Array.isArray(feed.cardEndless.types))
          ? { types: feed.cardEndless.types.map(String) } : null;
        heroShown = readHeroShown();
        buildProductHero(feed.items);
        buildSpecAccordions();
        // ── THE PLAYER'S HOSTS ARE WARMED BEFORE THE FIRST PLAYER (2026-09-08)
        // A YouTube embed opens three origins the page had never touched -
        // the embed document, the player script, the thumbnail - and on a
        // phone each new origin is a DNS lookup and a TLS handshake before
        // a byte of player arrives. Nothing in the theme preconnected them.
        // Only when the feed actually carries YouTube; a page without it
        // opens nothing. The product card counts too (2026-10-05): a hero
        // entry can bring its own YouTube `video` with no matching feed item,
        // and that player - the first one on the page - opened its hosts cold.
        try {
          var playsYouTube = function (it) { return it && it.video && it.video.provider === 'youtube'; };
          if (feed.items.some(playsYouTube) || (heroImages || []).some(playsYouTube)) {
            // not www.youtube.com: the player script is fetched from inside
            // the embed, and a browser that partitions connections by frame
            // cannot hand it a socket the top frame opened
            ['https://www.youtube-nocookie.com', 'https://i.ytimg.com'].forEach(function (href) {
              if (document.querySelector('link[rel="preconnect"][href="' + href + '"]')) return;
              var l = document.createElement('link');
              l.rel = 'preconnect';
              l.href = href;
              document.head.appendChild(l);
            });
          }
        } catch (e) {}
        if (!feed.items.length) {
          // A REAL FEED CAN BECOME EMPTY FOR THIS SHOPPER. The bundled file
          // may contain media, then the country/language policy can remove its
          // final item (K19's first local fallback contained one German video
          // on the US page). Returning here used to leave the sentinel saying
          // "Loading more media" forever and the shared card runtime waiting
          // for a cycle that could never exist.
          //
          // Close the feed honestly. The shared runtime observes this class
          // and can place Delivery / Ask / other utility cards at the end,
          // while the boot hold is released immediately.
          finishEmptyFeed();
          return;
        }
        // What the card showed earlier in this session, known BEFORE the
        // first cycles are planned - so nothing has to be rewound.
        // WHAT TO DO WITH CONTENT THIS BROWSER HAS ALREADY BEEN SHOWN,
        // chosen per content type in Agilite Media. Applied to the SCROLL
        // only - feed.items stays whole, so the product card above still gets
        // every tagged item. Absent file or no rules chosen = unchanged order.
        buildOrder(window.AGMF_Policy
          ? window.AGMF_Policy.plan(feed.items, feed.seenPolicy)
          : feed.items);
        if (!order.length) { finishEmptyFeed(); return; }
        // A COLOUR CHOSEN LATER STILL COUNTS. Laps are planned once and kept;
        // forgetting the ones not yet on screen is what lets the next lap
        // answer a swatch the shopper picked after the feed was built. The
        // lap they are reading is already in the DOM and is never touched.
        window.addEventListener('agmf:prefchanged', function () {
          for (var key in lapOrders) {
            if (Object.prototype.hasOwnProperty.call(lapOrders, key)) delete lapOrders[key];
          }
        });
        appendCycle();
        appendCycle();
        // feed committed — ONLY now may the CSS hide the originals the feed
        // replaces (gallery video, description video, old suggestion widget)
        document.documentElement.classList.add('agmf-feed-live');
        buildSuggestCard();
        buildProductHero(feed.items);
        // the spec CARD is OFF for now (Dorian 2026-08-11): the TOP accordions
        // serve Details|Sizing|Specs|Returns|Gov Sales. Re-enable if wanted:
        // buildSpecCard();
        buildSpecAccordions();
        // A YouTube embed reports its position; nothing tells us it ended.
        // One timer, started only when the feed is actually set to move on.
        if (AUTO.feed) setInterval(checkFeedYtEnds, 1000);
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        if ('IntersectionObserver' in window) {
          new IntersectionObserver(function (entries) {
            if (entries.some(function (en) { return en.isIntersecting; })) {
              appendCycle(); appendCycle();
              manageVideos();
            }
          }, { rootMargin: '2400px 0px' }).observe(sentinel);
        }
        pass();
      })
      .catch(function () {
        // A media outage must not roll an onboarded product back to the old
        // gallery/table. Both native sources were rendered by Liquid already.
        finishEmptyFeed();
      });

    // harness/debug surface — intentionally tiny and read-only (except tick)
    window.AGMF = window.AGMF || {};
    // A SUPPORTED entry point, not a debug one: everything in here - attaching
    // video sources, playing them, extending the runway - hangs off the WINDOW
    // scroll. Any layout that scrolls something else instead has to say so, or
    // the feed stops living (the desktop three-column page, 2026-08-30: videos
    // never attached, so nothing autoplayed and no new cycles arrived).
    window.AGMF.tick = pass;
    // THE LINK AS THE ENGINE SEES IT, for the tracker (agmf-track.js stamps
    // it on every event) - 'constrained' | 'open' - and the last measured
    // attach-to-first-message of a YouTube player, ms. Supported, not debug.
    window.AGMF.link = function () { return constrainedLink() ? 'constrained' : 'open'; };
    window.AGMF.ytBootMs = function () { return lastYtBootMs; };
    // THE RECORD BEHIND A CARD, for anything outside this closure that opens
    // content (the desktop lightbox, 2026-09-02). Reviews and articles carry
    // their record on the element; every other feed card carries only its
    // index AND its lap, and the lap matters - each lap re-interleaves the
    // library, so order[idx] is the wrong item on any lap past the first.
    // THE SAME CAPTION, WHEREVER THE CLIP IS SHOWN. A created loop's words are
    // painted on the card by capOverlayHtml; a dialog that shows the clip
    // without them loses the whole point of the clip (Dorian 2026-09-02).
    window.AGMF.captionHtml = function (item) { return item ? capOverlayHtml(item) : ''; };
    window.AGMF.itemFor = function (el) {
      var card = el && el.closest ? el.closest('.agmf-card') : null;
      if (!card) return null;
      if (card.__agmfItem) return card.__agmfItem;
      var oi = parseInt(card.dataset.agmfIdx, 10);
      if (isNaN(oi)) return null;
      var lap = parseInt(card.dataset.agmfLap || '0', 10) || 0;
      var lo = orderForLap(lap);
      return (lo && lo[oi]) || order[oi] || null;
    };
    window.AGMF._debug = {
      // the feed's decided order, so a language/colour preference can be
      // checked without walking the whole feed (the pane throttles hard)
      order: function () {
        return order.map(function (it) {
          return { ck: it && it.ck, lang: (it && it.language) || 'unknown', platform: it && it.platform };
        });
      },
      cycles: function () { return cycles.length; },
      materialized: function () {
        return cycles.filter(function (c) { return c.materialized; }).length;
      },
      orderLen: function () { return order.length; },
      // a mixed bundle feed's shares as dealt: lap length, each product's
      // share, pieces, pieces per lap and how far through them it has got;
      // null when the feed has no shares (or they could not be applied)
      shares: function () { return sharePlan ? sharePlan.debug() : null; },
      // the block boundaries, so they can be checked without fighting the
      // recycler - a dematerialised block has no cards to inspect
      blocks: function () { return cycleSpan.slice(); },
      // the rail a card WOULD get, without scrolling to it
      railPool: function (oi, lap) {
        var lo = orderForLap(lap || 0);
        var it = lo[oi];
        return it ? railPoolFor(it, oi, lap || 0, lo).map(function (x) { return x.id; }) : null;
      },
      laneOf: function (oi, lap) {
        var it = orderForLap(lap || 0)[oi];
        return it ? laneOf(it) : null;
      },
      videosWithSrc: function () { return section.querySelectorAll('video[src]').length; },
      playing: function () {
        return [].filter.call(section.querySelectorAll('video'), function (v) { return !v.paused; }).length;
      },
      tick: pass,
      activateVisible: function () { activateCarousel(mostVisibleCard()); },
      slidableCards: function () { return section.querySelectorAll('.agmf-card--slidable').length; },
      suggestCard: function () { return !!section.querySelector('.agmf-suggest'); },
      suggRows: function () { return section.querySelectorAll('.agmf-sugg-row').length; },
      suggTiles: function () { return section.querySelectorAll('.agmf-sugg-tile').length; },
      suggDots: function () { return section.querySelectorAll('.agmf-sugg-dot').length; },
      accWrap: function () { return !!document.querySelector('.agmf-acc-wrap'); },
      accCount: function () { return document.querySelectorAll('.agmf-acc').length; },
      specCard: function () { return !!section.querySelector('.agmf-spec'); },
      specTabs: function () { return section.querySelectorAll('.agmf-spec-tab').length; },
      specTabOpen: function (i) {
        var b = section.querySelector('[data-agmf-spec-tab="' + i + '"]');
        if (b) b.click();
        var body = section.querySelector('[data-agmf-spec-body]');
        return body ? body.innerHTML.length : 0;
      },
      ytFrames: function () { return section.querySelectorAll('.agmf-ytframe').length; },
      ytBooting: function () { return document.querySelectorAll('[data-agmf-yt-booting="1"]').length; },
      ytReady: function () { return document.querySelectorAll('[data-agmf-yt][data-agmf-ready="1"]').length; },
      link: function () { return window.AGMF.link(); },
      ytBootMs: function () { return window.AGMF.ytBootMs(); },
      stickyVisible: function () {
        var b = document.querySelector('[data-agmf-sticky]');
        return !!(b && !b.hidden);
      },
    };
  }

  function init() {
    try {
      var section = document.querySelector('[data-agmf]');
      // The <head> hold (snippets/agmf-boot-hold.liquid) reads this: once the
      // engine has booted it is working, so a slow page is waited for rather
      // than released to the old block a moment before the new one.
      if (section) { window.__agmfEngine = true; boot(section); }
    } catch (e) { /* never let the media layer break a product page */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}());
